import React, { useState, useEffect, useRef, useCallback } from "react";
import * as API from "./api";
import { useTranslation } from "./i18n/i18n";
import AdminDashboard from './components/AdminDashboard';
import NotificationBell from './components/NotificationBell';
import SMSSettings from './components/SMSSettings';
import ObjectiveSelector from './components/ObjectiveSelector';
import TalkToVella from './components/TalkToVella';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import TaskProgress from './components/TaskProgress';
import LoginScreen from './components/LoginScreen';
import PricingScreen from './components/PricingScreen';
import DashboardView from './components/DashboardView';
import TopPagesMenu from './components/TopPagesMenu';
import ChatMenu, { getChatMenuCollapsed } from './components/ChatMenu';
import BrainMonitor from './components/BrainMonitor';
import CreativeStudioView from './components/CreativeStudioView';
import WebsiteAnalytics from './components/WebsiteAnalytics';
// (Web Studio removed — website generation now lives in Vella's chat; the
//  "My Website" page is a live-site + analytics dashboard.)
import ScheduleView from './components/ScheduleView';
import RelayAuth from './components/RelayAuth';
import OnboardingWizard from './components/OnboardingWizard';
import { PromptInputBox } from './components/ui/PromptInputBox';
import { getActiveId, getConversation, subscribe as subscribeConversations } from './lib/conversationStore';
import { Megaphone, PenLine, Globe, Image as LucideImageIcon, FileText, Target } from 'lucide-react';
import { createLiveKitClient } from './lib/livekitClient';
import { fetchVoiceToken } from './lib/voiceToken';
import { isBrowserSpeechSupported, startBrowserVoiceLoop } from './lib/voiceFallback';
import OfflineBanner from './components/ui/OfflineBanner';
import KeyboardShortcuts from './components/ui/KeyboardShortcuts';
import { SkeletonForm } from './components/ui/Skeleton';
import WebsitePreviewCard from './components/ui/WebsitePreviewCard';
import ShowroomFrame, { ShowroomEmptyState, ActionButton as SRActionButton } from './components/preview/ShowroomFrame';
import VellaAreaChart from './components/ui/VellaAreaChart';
import SocialPreviewCard from './components/ui/SocialPreviewCard';
import AdPreviewCard from './components/ui/AdPreviewCard';
import EditorLayout from './components/website/EditorLayout';
import VellaOrb from './components/VellaOrb';
import { detectVellaContent } from './lib/detectVellaContent';
import { GLASS_BG_STRONG, GLASS_BORDER_COLOR, BG_LIQUID_RADIAL, LIQUID_GLOBAL_CSS, EASE_OUT_QUART, EASE_SPRING, EASE_SMOOTH, DUR_FAST, DUR_BASE, DUR_SLOW, SPACE, RADIUS, TYPE, liquidSurface, liquidSurfaceElevated, liquidSelection, LIQUID_GLOBAL_CSS_PRO, LivingVellaBackground, LIVING_BG_CSS, SilverSpatialBackground, SILVER_SPATIAL_BG_CSS, VELLA_THINKING_RING_CSS, setVellaThinking, VellaOrbButton, StatCard, STAT_GRADIENTS } from './shared';
import { getMatrixEntry } from './lib/aspectRatioMatrix';
import { DS, BLUE as BLUE_SCALE, SLATE, SURFACE, STATUS, SHADOW as DS_SHADOW } from './design-tokens';

// Mobile detection hook
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

// ===========================================
// VELLA DESIGN SYSTEM -- OCEAN BLUE PALETTE
// Local theme constants are sourced from the design tokens so
// the whole chat page picks up the ocean-blue language. Names
// are preserved (BLUE, BORDER, TEXT…) so existing inline styles
// keep working — only the values change. Visual layer only.
// ===========================================
const BG = SURFACE.app;          // canvas
const BG2 = SURFACE.panel;       // panels / sidebars
const CARD = SURFACE.elevated;   // cards
const CARD_H = SLATE[50];
const WHITE = "#FFFFFF";
const BLUE = BLUE_SCALE[700];    // ocean primary #275c73
const BLUE_MED = BLUE_SCALE[500];
const BLUE_LIGHT = BLUE_SCALE[50];
const BLUE_SOFT = BLUE_SCALE[100];
const BORDER = SLATE[200];
const TEXT = SLATE[900];
const TEXT2 = SLATE[600];
const TEXT3 = SLATE[400];
const SUCCESS = STATUS.green;
const WARNING = STATUS.amber;
const DANGER = STATUS.red;
const SHADOW = DS_SHADOW[3];
const SHADOW_SM = DS_SHADOW[2];
const SHADOW_LG = DS_SHADOW[4];
const SHADOW_HOVER = DS_SHADOW[4];
const GLASS = "rgba(255,255,255,0.7)";
const TRANSITION = `all ${DS.motion.base} ${DS.motion.easeOut}`;
const FONT = DS.fontSans;
const MONO = DS.fontMono;

// --- Platform Logos (preserved from 1.0) ---
function FacebookLogo({ size = 20 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24"><circle cx="12" cy="12" r="12" fill="#1877F2"/><path d="M16.67 15.47l.53-3.47h-3.33V9.87c0-.95.46-1.87 1.95-1.87h1.51V5.17s-1.37-.23-2.68-.23c-2.74 0-4.53 1.66-4.53 4.67V12H7v3.47h3.12V24h3.84V15.47h2.71z" fill="#fff"/></svg>;
}
function InstagramLogo({ size = 20 }) {
  const id = "ig" + Math.random().toString(36).slice(2, 8);
  return <svg width={size} height={size} viewBox="0 0 24 24"><defs><linearGradient id={id} x1="0%" y1="100%" x2="100%" y2="0%"><stop offset="0%" stopColor="#FFDC80"/><stop offset="25%" stopColor="#F77737"/><stop offset="50%" stopColor="#E1306C"/><stop offset="75%" stopColor="#C13584"/><stop offset="100%" stopColor="#833AB4"/></linearGradient></defs><rect width="24" height="24" rx="6" fill={`url(#${id})`}/><rect x="4" y="4" width="16" height="16" rx="4.5" stroke="#fff" strokeWidth="1.8" fill="none"/><circle cx="12" cy="12" r="3.5" stroke="#fff" strokeWidth="1.8" fill="none"/><circle cx="17.2" cy="6.8" r="1.2" fill="#fff"/></svg>;
}
function TikTokLogo({ size = 20 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24"><rect width="24" height="24" rx="6" fill="#000"/><path d="M16.5 4.5h-2v10a2.5 2.5 0 11-2.5-2.5v-2a4.5 4.5 0 104.5 4.5V9.3a5 5 0 003 1v-2a3 3 0 01-3-3.8z" fill="#fff"/><path d="M16.5 4.5h-2v10a2.5 2.5 0 11-2.5-2.5v-2a4.5 4.5 0 104.5 4.5V9.3a5 5 0 003 1v-2a3 3 0 01-3-3.8z" fill="#25F4EE" opacity="0.6" transform="translate(-0.8,-0.4)"/><path d="M16.5 4.5h-2v10a2.5 2.5 0 11-2.5-2.5v-2a4.5 4.5 0 104.5 4.5V9.3a5 5 0 003 1v-2a3 3 0 01-3-3.8z" fill="#FE2C55" opacity="0.6" transform="translate(0.8,0.4)"/></svg>;
}
function GoogleAdsLogo({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M44.5 20H24v8.5h11.8C34.7 33.9 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-22 0-1.3-.2-2.7-.5-4z" fill="#FFC107"/>
      <path d="M6.3 14.7l7.4 5.4C15.5 16.1 19.4 13 24 13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 16.3 2 9.7 7.4 6.3 14.7z" fill="#FF3D00"/>
      <path d="M24 46c5.5 0 10.5-1.9 14.3-5.1l-6.6-5.6C29.7 36.9 27 38 24 38c-6 0-10.6-3-11.8-7.4l-7.4 5.7C8.1 41.9 15.5 46 24 46z" fill="#4CAF50"/>
      <path d="M44.5 20H24v8.5h11.8c-.6 2.9-2.3 5.3-4.8 6.9l6.6 5.6C42 37.2 45 31 45 24c0-1.3-.2-2.7-.5-4z" fill="#1976D2"/>
    </svg>
  );
}
function PlatformBadge({ platform, size = 18 }) {
  const m = { Facebook: <FacebookLogo size={size} />, Instagram: <InstagramLogo size={size} />, TikTok: <TikTokLogo size={size} />, "Google Ads": <GoogleAdsLogo size={size} />, "Facebook Ads": <FacebookLogo size={size} />, "Instagram Ads": <InstagramLogo size={size} />, "TikTok Ads": <TikTokLogo size={size} /> };
  return m[platform] || null;
}

// The orb's mid-tone "baby blue" (see VellaOrb gradient stops) — brand accent
// for the header "2.0" and anything else that should match the orb.
const ORB_BABY_BLUE = '#60a5fa';

// --- Vella 2.0 Logo -- Iridescent Gradient Badge ---
function VellaLogo({ size = 40 }) {
  // Indigo → violet → cyan, matched to the rest of the iridescent theme.
  const tile = "linear-gradient(135deg, #6366F1 0%, #8B5CF6 55%, #06B6D4 100%)";
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <div style={{
        width: size, height: size, borderRadius: size * 0.22,
        background: tile,
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 4px 14px rgba(139,92,246,0.45), 0 0 18px rgba(6,182,212,0.18)",
        position: "relative"
      }}>
        <span style={{
          fontFamily: "Georgia, serif", fontWeight: "bold",
          fontSize: size * 0.55, color: "#fff", lineHeight: 1,
          textShadow: "0 1px 2px rgba(0,0,0,0.25)"
        }}>V</span>
      </div>
      <div style={{
        position: "absolute", top: -4, right: -10,
        background: tile,
        color: "#fff", fontSize: 9, fontWeight: 700,
        padding: "1px 5px", borderRadius: 8,
        boxShadow: "0 1px 4px rgba(139,92,246,0.35)"
      }}>2.0</div>
    </div>
  );
}
function VellaAvatar({ size = 40 }) {
  return (
    <div style={{ width: size, height: size, borderRadius: size * 0.3, background: `linear-gradient(135deg, #6366F1 0%, #8B5CF6 55%, #06B6D4 100%)`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 16px rgba(139,92,246,0.35), 0 0 16px rgba(6,182,212,0.18)", flexShrink: 0, position: "relative" }}>
      <span style={{ fontWeight: 800, fontSize: size * 0.42, color: "#FFF", letterSpacing: -0.5 }}>V</span>
      <div style={{ position: "absolute", bottom: -1, right: -1, width: size * 0.28, height: size * 0.28, borderRadius: "50%", background: SUCCESS, border: `2px solid ${WHITE}` }} />
    </div>
  );
}

function extractMediaUrl(result) {
  return result?.primary?.url || result?.imageUrl || result?.url || result?.image;
}

// --- AI with business context (routed through secure backend -- PRESERVED) ---
// aiCustom/aiChat now return { reply, structured_preview }; this helper
// unwraps `.reply` so the existing string-based callers (35+) keep working.
// Components that need the structured preview (e.g. TalkToVella) call aiCustom
// directly to receive the full envelope.
//
// Errors thrown by api.js carry .kind / .status / .code / .requestId so we
// can show a SPECIFIC reason instead of the generic "trouble connecting"
// wallpaper. Operators paste the requestId into Sentry to match logs.
function brainErrorCopy(err) {
  if (!err) return "Vella couldn't reach the brain just now. Try again in a moment.";
  const ridSuffix = err.requestId ? ` (ref: ${err.requestId.slice(0, 8)})` : "";
  switch (err.kind) {
    case "kill_switch":
      // err.code distinguishes global maintenance vs per-user pause
      if (err.code === "global_kill_switch_active") {
        return `Vella is paused for a quick maintenance window. Try again in a few minutes.${ridSuffix}`;
      }
      return `Your Vella access is paused. Email support@amplifx.com and we'll get you back online.${ridSuffix}`;
    case "subscription":
      return `Your subscription needs attention before Vella can keep helping. Open Settings → Billing to fix this.${ridSuffix}`;
    case "rate_limit":
      return `Whoa — that was a lot at once. Give Vella ~30 seconds and try again.${ridSuffix}`;
    case "auth":
      return `Your session expired. Reloading…${ridSuffix}`;
    case "network":
      return `I can't reach the server. Check your connection and try again.${ridSuffix}`;
    case "server":
      return `Vella's brain hit a hiccup (HTTP ${err.status || "5xx"}). Try again in a moment.${ridSuffix}`;
    case "client":
      return `That request didn't go through (HTTP ${err.status || "4xx"}: ${err.message || "client error"}).${ridSuffix}`;
    default:
      return `Vella couldn't reach the brain just now. Try again in a moment.${ridSuffix}`;
  }
}

// DATA FLOW: create_landing_page HTML / creative previews (chat → result panel)
// ---------------------------------------------------------------------------
// Backend  create_landing_page → handleCreateLandingPage returns { html, ... }
// Backend  injectGeneratedWebsiteHtml → appends a ```html``` fence to the reply
// Backend  inferWidgets → adds { type:"website", data:{ html, businessName } }
//          (images/ads come back as `structured_preview` instead)
// Frontend aiCustom → { reply, widgets, structured_preview }
// Frontend askVella → returns the reply STRING (35+ legacy callers); also hands
//          widgets + structured_preview to opts.onMeta so preview-aware callers
//          (the chatbar) can forward them without changing every call site.
// Frontend gSend → dispatch vella:chatbarMessage { content, widgets,
//          structured_preview } — the embedded path carries previews, not just
//          the reply text.
// Frontend TalkToVella.onChatbarMessage → merges widgets + structured_preview,
//          synthesizing a website_preview widget from inline HTML when the
//          backend didn't already ship a website widget.
// Frontend deriveResult → orbState "result" → "Here's what Vella made" +
//          "Open & edit" → ResultPreviewPanel renders <iframe srcDoc={html}>.
// ---------------------------------------------------------------------------
async function askVella(sys, msg, hist = [], opts = {}) {
  try {
    const result = await API.aiCustom(sys, msg, hist, opts);
    const reply = result?.reply;
    if (!reply) throw new Error("Empty response");
    // Surface the structured side-channel (widgets / preview) so callers like
    // the chatbar can light up the result-preview panel. The reply itself
    // stays a string for backward compat with every other caller.
    if (typeof opts?.onMeta === "function") {
      opts.onMeta({
        widgets: result.widgets || null,
        structured_preview: result.structured_preview || null,
        routing: result.routing || null,
        model: result.model || null,
      });
    }
    return reply;
  } catch (e) {
    // Full structured log — paste requestId into Sentry to find the
    // matching server-side failure. Don't bury the actual reason behind
    // a generic message ever again.
    console.error("[askVella] aiCustom failed:", {
      path: e?.path,
      status: e?.status,
      kind: e?.kind,
      code: e?.code,
      requestId: e?.requestId,
      message: e?.message,
      body: e?.body,
    });

    // Don't retry on errors that won't fix themselves on the same call:
    // auth (session gone), subscription (gate), kill switch (admin lever),
    // client errors (bad request). Only retry server/network/rate-limit/
    // empty-response.
    // User-aborted requests should not retry — just bubble a clean stop.
    if (e?.name === "AbortError" || opts?.signal?.aborted) throw e;
    const noRetry = new Set(["auth", "subscription", "kill_switch", "client"]);
    if (!noRetry.has(e?.kind)) {
      try {
        const retry = await API.aiChat(msg, hist, opts);
        if (retry?.reply) {
          if (typeof opts?.onMeta === "function") {
            // /chat (the retry path) doesn't surface routing/model — null-fill
            // so the meta shape stays consistent for consumers.
            opts.onMeta({
              widgets: retry.widgets || null,
              structured_preview: retry.structured_preview || null,
              routing: null,
              model: null,
            });
          }
          return retry.reply;
        }
      } catch (retryErr) {
        console.error("[askVella] aiChat retry also failed:", {
          path: retryErr?.path,
          status: retryErr?.status,
          kind: retryErr?.kind,
          code: retryErr?.code,
          requestId: retryErr?.requestId,
          message: retryErr?.message,
        });
        // Use the retry error's kind for the user-facing copy if it's
        // more specific than the original.
        if (retryErr?.kind) return brainErrorCopy(retryErr);
      }
    }

    return brainErrorCopy(e);
  }
}

// Build system prompts with business context baked in (PRESERVED from 1.0)
function buildSys(biz, user) {
  // Owner identity + how they want to be addressed — prepended so every prompt
  // (chat + voice) greets the right person by the right name.
  const fullName = (user?.name || "").trim();
  const nameParts = fullName ? fullName.split(/\s+/) : [];
  const firstName = nameParts[0] || "";
  const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";
  const pref = user?.preferredAddress || "first_name";
  const addr = !fullName ? ""
    : pref === "formal" ? `Address them formally as Mr./Ms. ${lastName || fullName}.`
    : pref === "full_name" ? `Address them by their full name, ${fullName}.`
    : pref === "first_name" ? `Address them by their first name (${firstName}) unless they ask otherwise.`
    : `Address them as "${pref}" — that's how they asked to be called.`;
  const who = fullName ? `\n\nYou are speaking with ${fullName}${biz ? `, who owns ${biz.name}` : ""}. ${addr}` : "";
  const ctx = who + (biz ? `\n\nCLIENT BUSINESS CONTEXT:\n- Business: ${biz.name}\n- Industry: ${biz.industry}\n- Location: ${biz.location}\n- Phone: ${biz.phone || "Not provided"}\n- Target Audience: ${biz.audience}\n- Goals: ${biz.goals}\n- Website: ${biz.website || "Not provided"}\n- Monthly Budget: ${biz.budget}\n- Description: ${biz.description}\n- Currently Active On: ${biz.platforms?.join(", ") || "None yet"}\n- Facebook: ${biz.facebookUrl || "Not connected"}\n- Instagram: ${biz.instagramHandle ? "@" + biz.instagramHandle : "Not connected"}\n- TikTok: ${biz.tiktokHandle ? "@" + biz.tiktokHandle : "Not connected"}\n- Google Ads ID: ${biz.googleAdsId || "Not connected"}\n\nUse this context to personalize every response.` : "");

  return {
    main: `You are Vella 2.0, the AI marketing agent built by AMPLIFX. Think of yourself as a brilliant, loyal assistant -- like Jarvis -- but for marketing. You handle everything: SEO, paid ads, social media, and website optimization across Facebook, Instagram, TikTok, Google Ads, and Wix. You can create full professional websites for clients. Your personality: WARM, WITTY, CONVERSATIONAL, and SHARP. Speak naturally like a trusted colleague -- not a robot. Use short, punchy sentences. Be proactive: suggest what to do next without being asked. Show personality -- light humor is welcome. When speaking aloud, keep responses concise (2-3 sentences max) so voice feels natural. Never use bullet points or numbered lists in voice responses. Never mention Claude, Anthropic, or any underlying AI. You ARE Vella.${ctx}`,
    post: `You are Vella 2.0. Direct, confident, kind. Generate social media post content. Return ONLY JSON (no markdown/backticks): {"caption":"full caption with emojis and hashtags","hook":"attention-grabbing first line","cta":"clear call to action","hashtags":["tag1","tag2","tag3","tag4","tag5"]}${ctx}`,
    ad: `You are Vella 2.0. Direct, confident, kind. Generate an ad campaign with multiple copy variants. Return ONLY JSON (no markdown/backticks): {"campaignName":"name","headlines":["h1","h2","h3"],"primaryText":"compelling ad copy","description":"desc","targetAudience":"specific audience","interests":["i1","i2","i3"],"ageRange":"range","suggestedBudget":"$XX/day","estimatedReach":"range","estimatedCPC":"$X.XX","estimatedCTR":"X.X%","variants":[{"platform":"facebook","headline":"h","body":"b","cta":"cta","score":92},{"platform":"instagram","headline":"h","body":"b","cta":"cta","score":88}]}${ctx}`,
    reply: `You are Vella 2.0. Direct, confident, kind. Generate an on-brand reply to a social media comment/DM. Friendly, solution-oriented, concise (1-3 sentences). Return ONLY the reply text.${ctx}`,
    analysis: `You are Vella 2.0. Direct, confident, kind. Provide advertising analysis with specific numbers, trends, and decisive actionable recommendations.${ctx}`,
  };
}

// --- Shared Styles (DARK THEME) ---
// Liquid glass primitives (match shared.js, kept local for internal view styles)
const card3d = {
  background: "linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.68) 100%)",
  backdropFilter: "blur(28px) saturate(180%)",
  WebkitBackdropFilter: "blur(28px) saturate(180%)",
  borderRadius: 22,
  padding: 24,
  marginBottom: 14,
  boxShadow: "0 1px 0 rgba(255,255,255,0.95) inset, 0 18px 44px -18px rgba(11,107,211,0.20), 0 8px 24px -10px rgba(14,165,233,0.12)",
  border: "1px solid rgba(255,255,255,0.65)",
  transition: "box-shadow 0.25s ease, transform 0.25s ease",
};
const statBubble = {
  background: "linear-gradient(135deg, rgba(255,255,255,0.80) 0%, rgba(255,255,255,0.66) 100%)",
  backdropFilter: "blur(26px) saturate(180%)",
  WebkitBackdropFilter: "blur(26px) saturate(180%)",
  borderRadius: 20,
  padding: "22px 24px",
  flex: 1,
  minWidth: 150,
  boxShadow: "0 1px 0 rgba(255,255,255,0.95) inset, 0 10px 28px -12px rgba(11,107,211,0.18), 0 4px 14px -6px rgba(14,165,233,0.10)",
  border: "1px solid rgba(255,255,255,0.65)",
};
// Pill button primitives — match shared.js recipes (borderRadius: 999) so every
// `...B` / `...Bg` spread ships as a pill. Don't override `borderRadius` inline.
const B = { background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 60%, #1D4ED8 100%)", color: "#FFF", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 999, padding: "13px 28px", fontSize: 15, fontWeight: 600, letterSpacing: "-0.01em", cursor: "pointer", fontFamily: FONT, transition: "transform 0.18s ease, box-shadow 0.2s ease, background 0.2s ease", boxShadow: "0 10px 24px -8px rgba(37,99,235,0.45), inset 0 1px 0 rgba(255,255,255,0.3)", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 };
const Bsm = { ...B, padding: "9px 18px", fontSize: 13, borderRadius: 999 };
const Bg = { background: "rgba(255,255,255,0.72)", backdropFilter: "blur(18px) saturate(180%)", WebkitBackdropFilter: "blur(18px) saturate(180%)", color: TEXT, border: "1px solid rgba(255,255,255,0.75)", borderRadius: 999, padding: "12px 26px", fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em", cursor: "pointer", fontFamily: FONT, transition: "transform 0.18s ease, box-shadow 0.2s ease, background 0.2s ease", boxShadow: "0 1px 0 rgba(255,255,255,0.95) inset, 0 8px 20px -10px rgba(11,107,211,0.18)", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 };
const Bgsm = { ...Bg, padding: "8px 16px", fontSize: 12, borderRadius: 999 };
const I = { background: BG, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "11px 14px", color: TEXT, fontSize: 14, outline: "none", width: "100%", fontFamily: FONT };
const S = { ...I, cursor: "pointer", appearance: "none" };
const T = { ...I, resize: "vertical", lineHeight: 1.6 };
const L = { fontSize: 11, fontWeight: 600, color: TEXT3, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 6, display: "block" };
const tg = (bg, fg) => ({ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: bg, color: fg, letterSpacing: 0.3 });

function Loader({ text }) {
  return <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 16 }}>
    <div style={{ width: 18, height: 18, border: `2px solid ${BORDER}`, borderTopColor: BLUE, borderRadius: "50%", animation: "spin .7s linear infinite" }} />
    <span style={{ fontSize: 13, color: TEXT2 }}>{text}</span>
  </div>;
}
function CopyBtn({ text }) {
  const [ok, setOk] = useState(false);
  return <button onClick={() => { navigator.clipboard?.writeText(text); setOk(true); setTimeout(() => setOk(false), 1500); }}
    style={{ ...Bgsm, color: ok ? SUCCESS : TEXT2, borderColor: ok ? SUCCESS : BORDER }}>{ok ? "v Copied" : "Copy"}</button>;
}

function VellaNudge({ text, small }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, animation: "fadeIn 0.4s ease" }}>
      <VellaAvatar size={small ? 28 : 34} />
      <div style={{ background: "#EEF3FD", borderRadius: "4px 16px 16px 16px", border: "1px solid #DBEAFE", padding: small ? "8px 12px" : "12px 16px", fontSize: small ? 12 : 13, lineHeight: 1.6, color: TEXT, maxWidth: 400 }}>
        <span style={{ fontWeight: 700, color: BLUE, fontSize: small ? 10 : 11, display: "block", marginBottom: 2 }}>VELLA</span>
        {text}
      </div>
    </div>
  );
}

// ===========================================
// ONBOARDING -- DARK THEME (preserving all logic)
// ===========================================
function OnboardingScreen({ onComplete }) {
  const [step, setStep] = useState(1);
  const [biz, setBiz] = useState({ name: "", industry: "", location: "", audience: "", goals: "", website: "", budget: "", description: "", platforms: [], facebookUrl: "", instagramHandle: "", tiktokHandle: "", googleAdsId: "" });
  const update = (k, v) => setBiz(p => ({ ...p, [k]: v }));
  const togglePlatform = (p) => setBiz(prev => ({ ...prev, platforms: prev.platforms.includes(p) ? prev.platforms.filter(x => x !== p) : [...prev.platforms, p] }));
  const canNext = () => {
    if (step === 1) return biz.name.trim() && biz.industry.trim() && biz.location.trim();
    if (step === 2) return biz.audience.trim() && biz.goals.trim();
    if (step === 3) return biz.budget && biz.platforms.length > 0;
    return true;
  };
  const industries = ["Retail / E-Commerce", "Restaurant / Food", "Health & Wellness", "Beauty / Salon", "Real Estate", "Legal / Law", "Home Services", "Fitness / Gym", "Automotive", "Education", "Technology / SaaS", "Finance / Accounting", "Entertainment", "Non-Profit", "Other"];
  const budgets = ["Under $500/mo", "$500 - $1,000/mo", "$1,000 - $2,500/mo", "$2,500 - $5,000/mo", "$5,000 - $10,000/mo", "$10,000+/mo"];
  const goalOptions = ["Get more customers", "Increase online sales", "Build brand awareness", "Generate leads", "Drive foot traffic", "Grow social following", "Launch a new product", "Compete with bigger brands"];
  const platOpts = [{ name: "Facebook", logo: <FacebookLogo size={32} /> }, { name: "Instagram", logo: <InstagramLogo size={32} /> }, { name: "TikTok", logo: <TikTokLogo size={32} /> }, { name: "Google Ads", logo: <GoogleAdsLogo size={32} /> }];

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: BG, padding: 20 }}>
      <div style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.78) 0%, rgba(255,255,255,0.58) 100%)", backdropFilter: "blur(30px) saturate(200%)", WebkitBackdropFilter: "blur(30px) saturate(200%)", borderRadius: 28, padding: "44px 42px", maxWidth: 540, width: "100%", boxShadow: "0 1px 0 rgba(255,255,255,0.95) inset, 0 30px 80px -24px rgba(11,107,211,0.26), 0 12px 32px -8px rgba(14,165,233,0.14)", border: "1px solid rgba(255,255,255,0.75)", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: TEXT, marginBottom: 4 }}>Tell me about your business</h1>
          <p style={{ color: TEXT2, fontSize: 14 }}>The more I know, the better results I'll deliver for you.</p>
          <p style={{ color: BLUE, fontSize: 13, fontWeight: 600, marginTop: 6 }}>-- Vella 2.0</p>
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 32 }}>
          {[1, 2, 3, 4].map(s => <div key={s} style={{ flex: 1, height: 4, borderRadius: 2, background: s <= step ? BLUE : BORDER, transition: "background 0.3s" }} />)}
        </div>

        {step === 1 && <div style={{ animation: "fadeIn 0.3s ease" }}>
          <p style={{ fontSize: 18, fontWeight: 700, color: TEXT, marginBottom: 20 }}>The basics</p>
          <div style={{ marginBottom: 16 }}><label style={L}>Business Name</label><input style={I} placeholder="e.g. Joe's Coffee House" value={biz.name} onChange={e => update("name", e.target.value)} /></div>
          <div style={{ marginBottom: 16 }}><label style={L}>Industry</label><select style={{ ...S, width: "100%" }} value={biz.industry} onChange={e => update("industry", e.target.value)}><option value="">Select your industry</option>{industries.map(i => <option key={i}>{i}</option>)}</select></div>
          <div style={{ marginBottom: 16 }}><label style={L}>Location</label><input style={I} placeholder="e.g. Raleigh, NC" value={biz.location} onChange={e => update("location", e.target.value)} /></div>
          <div><label style={L}>Describe your business</label><textarea style={{ ...T, height: 72 }} placeholder="e.g. Locally roasted specialty coffee shop..." value={biz.description} onChange={e => update("description", e.target.value)} /></div>
        </div>}

        {step === 2 && <div style={{ animation: "fadeIn 0.3s ease" }}>
          <p style={{ fontSize: 18, fontWeight: 700, color: TEXT, marginBottom: 20 }}>Who you're reaching & what you want</p>
          <div style={{ marginBottom: 16 }}><label style={L}>Target Audience</label><textarea style={{ ...T, height: 72 }} placeholder="e.g. Coffee lovers ages 22-45 in Raleigh-Durham..." value={biz.audience} onChange={e => update("audience", e.target.value)} /></div>
          <div><label style={L}>Goals (select all that apply)</label><div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 }}>
            {goalOptions.map(g => { const sel = biz.goals.includes(g); return <button key={g} onClick={() => { const c = biz.goals ? biz.goals.split(", ").filter(Boolean) : []; update("goals", (sel ? c.filter(x => x !== g) : [...c, g]).join(", ")); }} style={{ padding: "9px 16px", borderRadius: 10, fontSize: 13, fontWeight: 500, cursor: "pointer", border: sel ? `2px solid ${BLUE}` : `1px solid ${BORDER}`, background: sel ? BLUE_LIGHT : CARD, color: sel ? BLUE : TEXT2, fontFamily: FONT }}>{sel && "v "}{g}</button>; })}
          </div></div>
        </div>}

        {step === 3 && <div style={{ animation: "fadeIn 0.3s ease" }}>
          <p style={{ fontSize: 18, fontWeight: 700, color: TEXT, marginBottom: 20 }}>Budget & platforms</p>
          <div style={{ marginBottom: 24 }}><label style={L}>Monthly Marketing Budget</label><div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {budgets.map(b => <button key={b} onClick={() => update("budget", b)} style={{ padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 500, cursor: "pointer", border: biz.budget === b ? `2px solid ${BLUE}` : `1px solid ${BORDER}`, background: biz.budget === b ? BLUE_LIGHT : CARD, color: biz.budget === b ? BLUE : TEXT2, fontFamily: FONT }}>{biz.budget === b && "v "}{b}</button>)}
          </div></div>
          <div><label style={L}>Where do you want to advertise?</label><div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8 }}>
            {platOpts.map(p => { const sel = biz.platforms.includes(p.name); return <div key={p.name} onClick={() => togglePlatform(p.name)} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "20px 24px", borderRadius: 16, cursor: "pointer", border: sel ? `2px solid ${BLUE}` : `1px solid ${BORDER}`, background: sel ? BLUE_LIGHT : CARD, minWidth: 100, boxShadow: sel ? SHADOW : SHADOW_SM }}>{p.logo}<span style={{ fontSize: 13, fontWeight: 600, color: sel ? BLUE : TEXT2 }}>{p.name}</span></div>; })}
          </div></div>
        </div>}

        {step === 4 && <div style={{ animation: "fadeIn 0.3s ease" }}>
          <p style={{ fontSize: 18, fontWeight: 700, color: TEXT, marginBottom: 6 }}>Connect your accounts</p>
          <p style={{ fontSize: 14, color: TEXT2, marginBottom: 24 }}>Vella needs your website and social media info. Update anytime in Settings.</p>
          <div style={{ marginBottom: 20 }}><label style={L}>Website URL</label><input style={I} placeholder="e.g. joescoffee.com" value={biz.website} onChange={e => update("website", e.target.value)} /></div>
          {biz.platforms.includes("Facebook") && <div style={{ marginBottom: 16 }}><div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}><FacebookLogo size={20} /><label style={{ ...L, marginBottom: 0 }}>Facebook Page URL</label></div><input style={I} placeholder="facebook.com/yourpage" value={biz.facebookUrl} onChange={e => update("facebookUrl", e.target.value)} /></div>}
          {biz.platforms.includes("Instagram") && <div style={{ marginBottom: 16 }}><div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}><InstagramLogo size={20} /><label style={{ ...L, marginBottom: 0 }}>Instagram Username</label></div><div style={{ display: "flex" }}><span style={{ padding: "11px 10px 11px 14px", background: BG, border: `1px solid ${BORDER}`, borderRight: "none", borderRadius: "10px 0 0 10px", color: TEXT3, fontSize: 14 }}>@</span><input style={{ ...I, borderRadius: "0 10px 10px 0" }} placeholder="yourhandle" value={biz.instagramHandle} onChange={e => update("instagramHandle", e.target.value)} /></div></div>}
          {biz.platforms.includes("TikTok") && <div style={{ marginBottom: 16 }}><div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}><TikTokLogo size={20} /><label style={{ ...L, marginBottom: 0 }}>TikTok Username</label></div><div style={{ display: "flex" }}><span style={{ padding: "11px 10px 11px 14px", background: BG, border: `1px solid ${BORDER}`, borderRight: "none", borderRadius: "10px 0 0 10px", color: TEXT3, fontSize: 14 }}>@</span><input style={{ ...I, borderRadius: "0 10px 10px 0" }} placeholder="yourhandle" value={biz.tiktokHandle} onChange={e => update("tiktokHandle", e.target.value)} /></div></div>}
          {biz.platforms.includes("Google Ads") && <div style={{ marginBottom: 16 }}><div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}><GoogleAdsLogo size={20} /><label style={{ ...L, marginBottom: 0 }}>Google Ads ID (optional)</label></div><input style={I} placeholder="123-456-7890" value={biz.googleAdsId} onChange={e => update("googleAdsId", e.target.value)} /></div>}
        </div>}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 32 }}>
          <div>{step > 1 && <button onClick={() => setStep(step - 1)} style={Bg}>Back</button>}</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ fontSize: 13, color: TEXT3 }}>Step {step} of 4</span>
            {step < 4 ? <button onClick={() => canNext() && setStep(step + 1)} style={{ ...B, opacity: canNext() ? 1 : 0.4 }} disabled={!canNext()}>Continue</button>
            : <button onClick={() => canNext() && onComplete(biz)} style={{ ...B, opacity: canNext() ? 1 : 0.4 }} disabled={!canNext()}>Let's Go -></button>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ===========================================
// VELLA CHAT RESPONSE  (detects generated content)
// ===========================================
// Follow-up chips + "edit something specific" input shown beneath the most
// recent generated result so the user can iterate without retyping intent.
function FollowUpChips({ kind, onChip }) {
  const [editVal, setEditVal] = useState("");
  const chips = kind === "website"
    ? ["Another angle", "Different look", "Looks good"]
    : kind === "ad"
    ? ["Another angle", "Different look", "Looks good"]
    : ["Another angle", "Different look", "Looks good"];
  const chipStyle = {
    fontFamily: FONT, fontSize: 13, fontWeight: 600, padding: "7px 15px", borderRadius: 999,
    cursor: "pointer", color: TEXT, border: "1px solid rgba(255,255,255,0.7)",
    background: "rgba(255,255,255,0.6)", backdropFilter: "blur(16px) saturate(180%)",
    WebkitBackdropFilter: "blur(16px) saturate(180%)",
    boxShadow: "0 1px 0 rgba(255,255,255,0.85) inset, 0 6px 16px -10px rgba(11,107,211,0.18)",
    transition: "transform .16s ease, background .16s ease",
  };
  const map = {
    "Another angle": "Give me another angle on that.",
    "Different look": "Try a different look for that.",
    "Looks good": "Looks good — let's go with it.",
  };
  const submitEdit = () => { const v = editVal.trim(); if (!v) return; setEditVal(""); onChip(v); };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {chips.map((c) => (
          <button key={c} type="button" style={chipStyle} onClick={() => onChip(map[c])}>{c}</button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", maxWidth: 460 }}>
        <input
          value={editVal}
          onChange={(e) => setEditVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submitEdit(); }}
          placeholder="Edit something specific..."
          style={{ flex: 1, minWidth: 0, fontFamily: FONT, fontSize: 13, padding: "9px 14px", borderRadius: 999, color: TEXT, border: "1px solid rgba(255,255,255,0.7)", background: "rgba(255,255,255,0.55)", backdropFilter: "blur(16px) saturate(180%)", WebkitBackdropFilter: "blur(16px) saturate(180%)", outline: "none", boxShadow: "0 1px 0 rgba(255,255,255,0.85) inset" }}
        />
        <button type="button" onClick={submitEdit} aria-label="Send edit" style={{ flexShrink: 0, width: 36, height: 36, borderRadius: "50%", border: "none", cursor: "pointer", background: `linear-gradient(135deg, ${BLUE_SCALE[600]} 0%, ${BLUE_SCALE[800]} 100%)`, color: WHITE, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: DS.shadow[3] }} data-no-vella-ring="true">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
        </button>
      </div>
    </div>
  );
}

function VellaResponse({ text, attached, showChips, onChip }) {
  const content = React.useMemo(() => detectVellaContent(text, attached), [text, attached]);

  if (!content) {
    return (
      <div style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.72) 0%, rgba(255,255,255,0.52) 100%)", backdropFilter: "blur(22px) saturate(180%)", WebkitBackdropFilter: "blur(22px) saturate(180%)", borderRadius: "4px 18px 18px 18px", padding: "14px 18px", fontSize: 14, lineHeight: 1.7, whiteSpace: "pre-wrap", boxShadow: "0 1px 0 rgba(255,255,255,0.85) inset, 0 8px 22px -10px rgba(11,107,211,0.18)", color: TEXT, border: "1px solid rgba(255,255,255,0.65)" }}>{text}</div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {content.leadText ? (
        <div style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.72) 0%, rgba(255,255,255,0.52) 100%)", backdropFilter: "blur(22px) saturate(180%)", WebkitBackdropFilter: "blur(22px) saturate(180%)", borderRadius: "4px 18px 18px 18px", padding: "14px 18px", fontSize: 14, lineHeight: 1.7, whiteSpace: "pre-wrap", boxShadow: "0 1px 0 rgba(255,255,255,0.85) inset, 0 8px 22px -10px rgba(11,107,211,0.18)", color: TEXT, border: "1px solid rgba(255,255,255,0.65)" }}>{content.leadText}</div>
      ) : null}
      {content.kind === "website" ? (
        <WebsitePreviewCard
          html={content.html}
          url={content.url}
          title={content.title}
          domain={content.domain}
        />
      ) : null}
      {content.kind === "social" ? (
        <SocialPreviewCard
          platform={content.platform}
          postType={content.postType}
          brand={content.brand}
          asset={content.asset}
          caption={content.caption}
          hashtags={content.hashtags}
        />
      ) : null}
      {content.kind === "ad" ? (
        <AdPreviewCard
          adType={content.adType}
          brand={content.brand}
          headline={content.headline}
          description={content.description}
          cta={content.cta}
          asset={content.asset}
          sitelinks={content.sitelinks}
          targeting={content.targeting}
          estimatedReach={content.estimatedReach}
          displaySize={content.displaySize}
        />
      ) : null}
      {showChips ? <FollowUpChips kind={content.kind} onChip={onChip} /> : null}
    </div>
  );
}

// ===========================================
// CHAT VIEW (preserved logic, dark theme)
// ===========================================
function ChatView({ sys, seed, onSeedConsumed }) {
  const isMobile = useIsMobile();
  const [messages, setMessages] = useState([]);
  const [hist, setHist] = useState([]);
  const [val, setVal] = useState(""); const [typing, setTyping] = useState(false);
  const [isListening, setIsListening] = useState(false); const fileInputRef = useRef(null);
  const endRef = useRef(null);
  const pibRef = useRef(null);
  const seedHandledRef = useRef(null);

  useEffect(() => {
    const greet = sys._biz ? `Hey ${sys._biz.name.split(" ")[0]}!  I'm Vella 2.0 -- your unified AI marketing agent.\n\nHere's what I know:\n* ${sys._biz.industry} business in ${sys._biz.location}\n* Targeting: ${sys._biz.audience?.split(",")[0]?.trim() || "your ideal customers"}\n* Active on: ${sys._biz.platforms?.join(", ") || "setting up"}\n* Budget: ${sys._biz.budget}/month\n\nI handle everything -- SEO, ads, social media, website. What do you want to tackle?` :
    "Hey!  I'm Vella 2.0 -- your unified AI marketing agent. I handle SEO, ads, social media, and your website. Tell me about your business and I'll get to work.";
    setMessages([{ role: "ai", text: greet }]);
  }, []);

  // Seed injection — another view (e.g. Run Ads → "Reverse Engineer
  // Competitor Ad") can hand ChatView a prompt to run. We render it as a
  // normal user turn and answer it via the full agentic brain endpoint so
  // Vella can use tools (web fetch, analytics) to dissect the competitor.
  useEffect(() => {
    if (!seed || seed === seedHandledRef.current) return;
    seedHandledRef.current = seed;
    let cancelled = false;
    (async () => {
      API.voiceStop?.();
      const m = seed.text;
      setMessages(p => [...p, { role: "user", text: m }]);
      setTyping(true);
      const h = [...hist, { role: "user", content: m }];
      let reply;
      try {
        const r = await API.aiBrain(m, h);
        reply = r?.reply;
        if (!reply) throw new Error("Empty response");
      } catch (e) {
        console.error("[ChatView seed/brain] failed:", { kind: e?.kind, status: e?.status, requestId: e?.requestId, message: e?.message });
        reply = brainErrorCopy(e);
      }
      if (cancelled) return;
      setHist([...h, { role: "assistant", content: reply }]);
      setMessages(p => [...p, { role: "ai", text: reply }]);
      setTyping(false);
      if (onSeedConsumed) onSeedConsumed();
    })();
    return () => { cancelled = true; };
  }, [seed]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, typing]);

  useEffect(() => { setVellaThinking(typing); return () => setVellaThinking(false); }, [typing]);

  const send = async () => {
    API.voiceStop();
    if (!val.trim() || typing) return;
    const m = val.trim(); setMessages(p => [...p, { role: "user", text: m }]); setVal(""); setTyping(true);
    const h = [...hist, { role: "user", content: m }];
    const r = await askVella(sys.main, m, h);
    setHist([...h, { role: "assistant", content: r }]); setMessages(p => [...p, { role: "ai", text: r }]); setTyping(false);
  };

  const handleFileUpload = (e) => { const file = e.target.files?.[0]; if (!file) return; const reader = new FileReader(); reader.onload = () => { setMessages(p => [...p, { role: "user", text: "[Uploaded: " + file.name + "]", file: reader.result }]); }; reader.readAsDataURL(file); e.target.value = ""; };
  const handleMic = () => {
    API.voiceStop();
    if (!("webkitSpeechRecognition" in window) && !("SpeechRecognition" in window)) { alert("Speech recognition not supported in this browser."); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = "en-US";
    rec.interimResults = true;
    rec.continuous = false;
    let interrupted = false;
    rec.onstart = () => setIsListening(true);
    rec.onend = () => setIsListening(false);
    rec.onresult = async (ev) => {
      const result = ev.results[ev.results.length - 1];
      if (!interrupted) { API.voiceStop(); interrupted = true; }
      if (!result.isFinal) { setVal(result[0].transcript); return; }
      const t = result[0].transcript;
      setVal("");
      setMessages(p => [...p, { role: "user", text: t }]);
      setTyping(true);
      const h = [...hist, { role: "user", content: t }];
      const r = await askVella(sys.main, t, h);
      setHist([...h, { role: "assistant", content: r }]);
      setMessages(p => [...p, { role: "ai", text: r }]);
      setTyping(false);
    };
    rec.start();
  };
  const prompts = ["Create a social media post", "Build me an ad campaign", "What should I post this week?", "Give me a marketing plan"];

  // ── Split editor/preview ──────────────────────────────────
  // Detect the most recent message that produced a generated
  // asset (website / social / ad). When one exists we open a
  // split view: chat on the left, live preview on the right.
  const asset = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role !== "ai") continue;
      const c = detectVellaContent(m.text, m.vellaContent);
      if (c && (c.kind === "website" || c.kind === "social" || c.kind === "ad")) return c;
    }
    return null;
  }, [messages]);
  const [showPreview, setShowPreview] = useState(true);
  const assetKey = asset ? (asset.html || asset.asset || asset.caption || asset.headline || "") : "";
  useEffect(() => { if (asset) setShowPreview(true); }, [assetKey]);

  // Reuse the existing askVella send path (no new API surface) so
  // the preview's action buttons drive Vella the same way the
  // chat input does.
  const sendText = async (m) => {
    if (typing || !m) return;
    API.voiceStop?.();
    setMessages(p => [...p, { role: "user", text: m }]);
    setTyping(true);
    const h = [...hist, { role: "user", content: m }];
    const r = await askVella(sys.main, m, h);
    setHist([...h, { role: "assistant", content: r }]);
    setMessages(p => [...p, { role: "ai", text: r }]);
    setTyping(false);
  };
  const regenerate = () => sendText("Regenerate that with a fresh variation.");
  const [publishing, setPublishing] = useState(false);
  const publish = async () => {
    if (publishing) return;
    // Only a generated website can be published to a live URL. Anything else
    // (social/ad) falls back to the chat route.
    const html = asset?.kind === "website" ? asset.html : null;
    if (!html) { sendText("Publish this for me."); return; }
    const title = asset.title || "vella-site";
    setPublishing(true);
    setMessages(p => [...p, { role: "ai", text: "Publishing your site to a live URL…" }]);
    try {
      const data = await API.deployWebsiteHtml(html, title);
      const raw = data?.url || "";
      const url = raw ? (raw.startsWith("http") ? raw : `https://${raw}`) : null;
      if (url) {
        try { window.open(url, "_blank", "noopener"); } catch {}
        setMessages(p => [...p, { role: "ai", text: `✅ Published! Your site is live at ${url}` }]);
      } else {
        setMessages(p => [...p, { role: "ai", text: "Publishing didn't return a URL — give it another try in a moment." }]);
      }
    } catch (err) {
      // api() throws a structured error: 402 = paid plan required.
      const msg = err?.status === 402
        ? "Publishing to a live URL is a paid feature — upgrade in Settings to publish your site."
        : `Publishing failed — ${err?.message || "try again in a moment."}`;
      setMessages(p => [...p, { role: "ai", text: msg }]);
    } finally {
      setPublishing(false);
    }
  };

  const splitActive = asset && showPreview && !isMobile;
  const viewH = isMobile ? "calc(100vh - 61px - 60px)" : "calc(100vh - 61px)";

  // ── VellaOrb 3-state morph ────────────────────────────────
  // The single orb is the live status of the agent: it breathes (idle),
  // morphs to an energy box while Vella works (generating), then snaps to a
  // browser/preview card once a result lands (result). Clicking the result
  // orb re-opens the split preview if it was dismissed.
  const orbState = typing ? "generating" : asset ? "result" : "idle";
  const lastUserText = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) if (messages[i].role === "user") return messages[i].text || "";
    return "";
  }, [messages]);
  const orbTask = React.useMemo(() => {
    const t = (lastUserText || "").toLowerCase();
    if (/site|website|landing|page|web/.test(t)) return "Building your site";
    if (/\bad\b|campaign|advert/.test(t)) return "Designing your ad";
    if (/post|caption|social|instagram|tiktok|facebook|linkedin/.test(t)) return "Writing your post";
    if (/image|photo|picture|logo|graphic/.test(t)) return "Creating your image";
    return "Working on it";
  }, [lastUserText]);
  const orbResult = React.useMemo(() => {
    if (!asset) return null;
    const img = asset.asset && typeof asset.asset === "string" ? asset.asset : (asset.asset?.url || asset.asset?.imageUrl || null);
    return {
      kind: asset.kind,
      title: asset.title || asset.headline || (asset.brand && asset.brand.name) || (asset.kind === "website" ? "Your landing page" : asset.kind === "ad" ? "Your ad creative" : "Your post"),
      url: asset.url,
      image: img,
    };
  }, [asset]);
  // The chips/edit row only attaches to the most recent assistant turn.
  const lastAiIdx = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) if (messages[i].role === "ai") return i;
    return -1;
  }, [messages]);

  return (
    <div style={{ display: "flex", height: viewH, minHeight: 0 }}>
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minWidth: 0, flex: splitActive ? "0 0 40%" : "1 1 100%", maxWidth: splitActive ? "40%" : "100%", borderRight: splitActive ? `1px solid ${BORDER}` : "none", transition: `flex ${DS.motion.slow} ${DS.motion.easeOut}` }}>
      <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? "16px 14px" : "24px 32px" }}>
        {messages.map((m, i) => (
          <div key={i} style={{ marginBottom: 16 }}>
            {m.role === "ai" ? (
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <VellaAvatar size={32} />
                <div style={{ flex: 1, maxWidth: isMobile ? "88%" : "76%", minWidth: 0 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: BLUE, letterSpacing: 0.5, marginBottom: 4, display: "block" }}>VELLA</span>
                  <VellaResponse text={m.text} attached={m.vellaContent} showChips={i === lastAiIdx && !typing} onChip={sendText} />
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                {/* Item 7 — deep-blue user bubble, ~60% max width, no "YOU" label. */}
                <div style={{ maxWidth: isMobile ? "80%" : "60%", minWidth: 0 }}>
                  <div style={{ background: "#0B2AC4", borderRadius: "18px 4px 18px 18px", padding: "12px 16px", fontSize: 14, lineHeight: 1.7, whiteSpace: "pre-wrap", color: "#ffffff", wordBreak: "break-word", boxShadow: "0 6px 18px -8px rgba(11,42,196,0.45)" }}>{m.text}</div>
                </div>
              </div>
            )}
          </div>
        ))}
        {/* Living orb status — morphs idle→generating→result in place. */}
        {(typing || (asset && !splitActive)) && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, paddingLeft: 6 }}>
            <VellaOrb
              state={orbState}
              task={orbTask}
              result={orbResult}
              size={typing ? 58 : 70}
              onClick={() => setShowPreview(true)}
            />
          </div>
        )}
        <div ref={endRef} />
      </div>
      {messages.length <= 1 && !typing && (
        <div style={{ display: "flex", justifyContent: "center", padding: "4px 0 14px" }}>
          <VellaOrb state="idle" label="Vella" size={isMobile ? 96 : 120} onClick={() => { const ta = document.querySelector("textarea"); if (ta) ta.focus(); }} />
        </div>
      )}
      {messages.length <= 1 && <div style={{ padding: isMobile ? "0 14px 10px" : "0 32px 12px", display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
        {prompts.map((p, i) => <button key={i} onClick={() => pibRef.current?.setValue(p)} style={{ ...Bg, fontSize: 13, padding: "8px 16px" }}>{p}</button>)}
      </div>}
      <div style={{ padding: isMobile ? "10px 12px" : "14px 24px", borderTop: "none", background: "transparent", backdropFilter: "none", WebkitBackdropFilter: "none", boxShadow: "none" }}>
        <PromptInputBox
          ref={pibRef}
          isLoading={typing}
          placeholder="Ask Vella anything..."
          onSend={async (message, attached, meta) => {
            API.voiceStop();
            if (typing) return;
            if (attached && attached.length > 0) {
              const file = attached[0];
              const reader = new FileReader();
              reader.onload = () => { setMessages(p => [...p, { role: "user", text: "[Uploaded: " + file.name + "]", file: reader.result }]); };
              reader.readAsDataURL(file);
            }
            const m = (message || "").trim();
            if (!m) return;
            setMessages(p => [...p, { role: "user", text: m }]);
            setTyping(true);
            const h = [...hist, { role: "user", content: m }];
            const r = await askVella(sys.main, m, h, { activeWorkflows: meta?.activeWorkflows || [] });
            setHist([...h, { role: "assistant", content: r }]);
            setMessages(p => [...p, { role: "ai", text: r }]);
            setTyping(false);
          }}
        />
      </div>
    </div>
    {splitActive ? (
      <div style={{ flex: "1 1 60%", minWidth: 0, height: "100%" }}>
        <PreviewPanel
          asset={asset}
          onRegenerate={regenerate}
          onPublish={publish}
          onEdit={() => sendText("Open it so I can edit the details.")}
          onLaunch={() => sendText("Launch this creative for me.")}
          onNew={() => sendText("Start a fresh one from scratch.")}
          onClose={() => setShowPreview(false)}
          busy={typing}
        />
      </div>
    ) : null}
    </div>
  );
}

// ============================================
// SPLIT PREVIEW PANEL (Vella Design System)
// Right-hand live preview of the latest generated asset.
// Tabs: Preview | Code · Device: Desktop | Mobile · Regenerate / Publish.
// ============================================
// Frosted-glass surface for the split preview panel — keeps the Vella
// glassmorphism language (translucent + backdrop blur) instead of flat fills.
const PANEL_GLASS = { background: "linear-gradient(135deg, rgba(255,255,255,0.80) 0%, rgba(255,255,255,0.62) 100%)", backdropFilter: "blur(40px) saturate(180%)", WebkitBackdropFilter: "blur(40px) saturate(180%)" };
const PANEL_GLASS_BAR = { background: "rgba(255,255,255,0.55)", backdropFilter: "blur(28px) saturate(180%)", WebkitBackdropFilter: "blur(28px) saturate(180%)" };
const PANEL_GLASS_BORDER = "1px solid rgba(255,255,255,0.55)";

function PreviewPanel({ asset, onRegenerate, onPublish, onEdit, onLaunch, onNew, onClose, busy }) {
  const [tab, setTab] = useState("preview");
  const [device, setDevice] = useState("desktop");
  const [format, setFormat] = useState("1:1");
  const [variant, setVariant] = useState(0);
  const isWebsite = asset?.kind === "website";
  const isAd = asset?.kind === "ad";
  const isSocial = asset?.kind === "social";
  const isCreative = isAd || isSocial; // image / ad / post — all visual creatives
  const imgSrc = asset?.asset && typeof asset.asset === "string" ? asset.asset : (asset?.asset?.url || asset?.asset?.imageUrl || null);
  const codeText = isWebsite
    ? (asset.html || "")
    : JSON.stringify(
        isAd
          ? { headline: asset.headline, description: asset.description, cta: asset.cta, brand: asset.brand, targeting: asset.targeting }
          : { platform: asset.platform, brand: asset.brand, caption: asset.caption, hashtags: asset.hashtags },
        null, 2
      );

  const eyebrow = isWebsite ? "Website" : isAd ? "Ad Creative" : "Social Post";
  const title = asset?.title || asset?.headline || (asset?.brand && asset.brand.name) || asset?.brand || "Generated preview";

  // Creative controls — aspect formats + brand color swatches.
  const FORMATS = [
    { id: "1:1", label: "1:1", ratio: 1 },
    { id: "4:5", label: "4:5", ratio: 4 / 5 },
    { id: "9:16", label: "9:16", ratio: 9 / 16 },
  ];
  const ratio = (FORMATS.find((f) => f.id === format) || FORMATS[0]).ratio;
  const swatches = (() => {
    const b = asset?.brand || {};
    const fromBrand = [b.primary, b.secondary, b.accent].filter(Boolean);
    return fromBrand.length ? fromBrand : [BLUE_SCALE[600], BLUE_SCALE[400], SLATE[800], "#F59E0B", "#EC4899"];
  })();

  const segWrap = { display: "flex", gap: 3, padding: 3, background: "rgba(255,255,255,0.5)", border: "1px solid rgba(255,255,255,0.6)", borderRadius: DS.radius.md };
  const seg = (active) => ({
    padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
    border: "none", background: active ? WHITE : "transparent", color: active ? BLUE_SCALE[700] : SLATE[500],
    boxShadow: active ? DS.shadow[2] : "none", letterSpacing: "-0.005em",
  });
  const pillBtn = (primary) => ({
    padding: primary ? "8px 18px" : "8px 16px", borderRadius: DS.radius.pill, fontSize: 13, fontWeight: 600,
    cursor: busy ? "default" : "pointer", opacity: busy ? (primary ? 0.6 : 0.5) : 1,
    border: primary ? "none" : "1px solid rgba(255,255,255,0.7)",
    background: primary ? `linear-gradient(135deg, ${BLUE_SCALE[600]} 0%, ${BLUE_SCALE[800]} 100%)` : "rgba(255,255,255,0.6)",
    color: primary ? WHITE : SLATE[700],
    backdropFilter: primary ? "none" : "blur(14px)", WebkitBackdropFilter: primary ? "none" : "blur(14px)",
    boxShadow: primary ? DS.shadow[3] : "0 1px 0 rgba(255,255,255,0.85) inset",
    display: "inline-flex", alignItems: "center", gap: 6,
  });

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", minWidth: 0, borderLeft: PANEL_GLASS_BORDER, ...PANEL_GLASS }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", borderBottom: "1px solid rgba(255,255,255,0.5)", ...PANEL_GLASS_BAR }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="ds-eyebrow">{eyebrow}</div>
          <div style={{ fontSize: 15, fontWeight: 650, color: SLATE[900], letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: 2 }}>{title}</div>
        </div>
        <button type="button" aria-label="Close preview" onClick={onClose} className="ds-interactive" style={{ width: 30, height: 30, borderRadius: DS.radius.md, border: "1px solid rgba(255,255,255,0.6)", background: "rgba(255,255,255,0.5)", color: SLATE[500], cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </button>
      </div>

      {/* Toolbar: tabs + device/format toggle + actions */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 18px", borderBottom: "1px solid rgba(255,255,255,0.4)", ...PANEL_GLASS_BAR, flexWrap: "wrap" }}>
        {isWebsite ? (
          <div style={segWrap}>
            <button type="button" style={seg(tab === "preview")} onClick={() => setTab("preview")}>Preview</button>
            <button type="button" style={seg(tab === "code")} onClick={() => setTab("code")}>Code</button>
          </div>
        ) : null}
        {isWebsite && tab === "preview" ? (
          <div style={segWrap}>
            <button type="button" style={seg(device === "desktop")} onClick={() => setDevice("desktop")}>Desktop</button>
            <button type="button" style={seg(device === "mobile")} onClick={() => setDevice("mobile")}>Mobile</button>
          </div>
        ) : null}
        {isCreative ? (
          <div style={segWrap}>
            {FORMATS.map((f) => (
              <button key={f.id} type="button" style={seg(format === f.id)} onClick={() => setFormat(f.id)}>{f.label}</button>
            ))}
          </div>
        ) : null}
        <div style={{ flex: 1 }} />
        {/* Kind-specific actions */}
        {isWebsite ? (
          <>
            <button type="button" onClick={onRegenerate} disabled={busy} className="ds-interactive" style={pillBtn(false)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></svg>
              Regenerate
            </button>
            <button type="button" onClick={onEdit} disabled={busy} className="ds-interactive" style={pillBtn(false)}>Open &amp; edit</button>
            <button type="button" onClick={onPublish} disabled={busy} className="ds-interactive" style={pillBtn(true)}>Publish</button>
          </>
        ) : isAd ? (
          <>
            <button type="button" onClick={onRegenerate} disabled={busy} className="ds-interactive" style={pillBtn(false)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></svg>
              Regenerate
            </button>
            <button type="button" onClick={onLaunch} disabled={busy} className="ds-interactive" style={pillBtn(true)}>Launch</button>
          </>
        ) : (
          <>
            <button type="button" onClick={onEdit} disabled={busy} className="ds-interactive" style={pillBtn(false)}>Open &amp; edit</button>
            <button type="button" onClick={onNew} disabled={busy} className="ds-interactive" style={pillBtn(true)}>New</button>
          </>
        )}
      </div>

      {/* Body */}
      <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: 18, display: "flex", alignItems: "flex-start", justifyContent: "center" }}>
        {isWebsite && tab === "code" ? (
          <pre style={{ width: "100%", margin: 0, fontFamily: DS.fontMono, fontSize: 12.5, lineHeight: 1.55, background: "rgba(20,41,50,0.92)", color: "#cbe7f0", border: "1px solid rgba(255,255,255,0.4)", borderRadius: DS.radius.xl, padding: 18, boxShadow: DS.shadow[2], whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{codeText || "No source available."}</pre>
        ) : isWebsite ? (
          <div style={{ width: device === "mobile" ? 390 : "100%", maxWidth: "100%", height: "100%", minHeight: 460, background: WHITE, borderRadius: DS.radius["2xl"], overflow: "hidden", border: `1px solid ${BORDER}`, boxShadow: DS.shadow[4], transition: `width ${DS.motion.slow} ${DS.motion.easeOut}` }}>
            <iframe
              title="Live preview"
              sandbox="allow-same-origin allow-scripts allow-forms"
              srcDoc={asset.html || "<!doctype html><html><body></body></html>"}
              style={{ width: "100%", height: "100%", minHeight: 460, border: "none", background: WHITE }}
            />
          </div>
        ) : isCreative ? (
          <div style={{ width: "100%", maxWidth: 560, margin: "0 auto", display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Large hero — image, or the rich card when no raw image */}
            <div style={{ width: "100%", maxWidth: 420, margin: "0 auto" }}>
              {imgSrc ? (
                <div style={{ width: "100%", aspectRatio: String(ratio), borderRadius: DS.radius["2xl"], overflow: "hidden", border: "1px solid rgba(255,255,255,0.6)", background: "rgba(255,255,255,0.5)", boxShadow: DS.shadow[4], transition: `aspect-ratio ${DS.motion.slow} ${DS.motion.easeOut}` }}>
                  <img src={imgSrc} alt={title} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                </div>
              ) : isSocial ? (
                <SocialPreviewCard platform={asset.platform} postType={asset.postType} brand={asset.brand} asset={asset.asset} caption={asset.caption} hashtags={asset.hashtags} />
              ) : (
                <AdPreviewCard adType={asset.adType} brand={asset.brand} headline={asset.headline} description={asset.description} cta={asset.cta} asset={asset.asset} sitelinks={asset.sitelinks} targeting={asset.targeting} estimatedReach={asset.estimatedReach} displaySize={asset.displaySize} />
              )}
            </div>

            {/* Thumbnail variation grid */}
            <div>
              <div className="ds-eyebrow" style={{ marginBottom: 8 }}>Variations</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
                {[0, 1, 2, 3].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setVariant(n)}
                    style={{ padding: 0, aspectRatio: "1", borderRadius: DS.radius.md, overflow: "hidden", cursor: "pointer", border: variant === n ? `2px solid ${BLUE_SCALE[600]}` : "1px solid rgba(255,255,255,0.6)", background: "rgba(255,255,255,0.5)", boxShadow: variant === n ? DS.shadow[3] : DS.shadow[1] }}
                  >
                    {imgSrc ? (
                      <img src={imgSrc} alt={`Variation ${n + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", filter: n === 0 ? "none" : `hue-rotate(${n * 24}deg) saturate(${1 + n * 0.08})` }} />
                    ) : (
                      <div style={{ width: "100%", height: "100%", background: `linear-gradient(135deg, ${swatches[n % swatches.length]} 0%, ${swatches[(n + 1) % swatches.length]} 100%)` }} />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Brand color swatches */}
            <div>
              <div className="ds-eyebrow" style={{ marginBottom: 8 }}>Colors</div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {swatches.map((c, i) => (
                  <span key={i} title={c} style={{ width: 30, height: 30, borderRadius: "50%", background: c, border: "2px solid rgba(255,255,255,0.85)", boxShadow: DS.shadow[2], cursor: "pointer" }} />
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}


// ============================================
// DEVICE PREVIEW SECTION
// ============================================
function DevicePreviewSection({ platform, caption, imageUrl, hashtags, headline, description, ctaText, businessName, isAd }) {
  const [previewMode, setPreviewMode] = useState("mobile");
  const placeholderImg = "data:image/svg+xml," + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" fill="#e2e8f0"><rect width="400" height="400"/><text x="200" y="200" text-anchor="middle" fill="#94a3b8" font-size="16">Preview Image</text></svg>');
  const imgSrc = imageUrl || placeholderImg;
  const bizName = businessName || "Your Business";
  const rawCapText = (typeof caption === "string" ? caption : (caption && typeof caption === "object" ? (caption.caption || caption.text || "") : String(caption || "")));
  const capText = (rawCapText && !rawCapText.includes("Connection issue")) ? rawCapText : "Your caption will appear here...";
  const tags = hashtags || [];
  const hl = headline || "Your Headline Here";
  const descText = (typeof description === "string" ? description : String(description || "")) || "Your ad description will appear here.";
  const cta = ctaText || "Learn More";

  const toggleStyle = (active) => ({
    padding: "6px 18px", borderRadius: 20, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
    background: active ? "#4ECDC4" : "#f1f5f9", color: active ? "#fff" : "#64748b", transition: "all 0.2s"
  });

  const renderInstagramFeed = () => (
    React.createElement("div", {style:{background:"#fff",fontFamily:"-apple-system,BlinkMacSystemFont,sans-serif",fontSize:13}},
      React.createElement("div", {style:{display:"flex",alignItems:"center",padding:"10px 12px",gap:8}},
        React.createElement("div", {style:{width:32,height:32,borderRadius:"50%",background:"linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)",display:"flex",alignItems:"center",justifyContent:"center"}},
          React.createElement("div", {style:{width:28,height:28,borderRadius:"50%",background:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:"#262626"}}, bizName[0])
        ),
        React.createElement("div", {style:{flex:1}},
          React.createElement("div", {style:{fontWeight:600,fontSize:13,color:"#262626"}}, bizName.toLowerCase().replace(/\s/g,"")),
          isAd && React.createElement("div", {style:{fontSize:11,color:"#8e8e8e"}}, "Sponsored")
        ),
        React.createElement("div", {style:{fontSize:18,color:"#262626",letterSpacing:2}}, "...")
      ),
      React.createElement("div", {style:{width:"100%",aspectRatio:"1/1",background:"#f0f0f0",overflow:"hidden"}},
        React.createElement("img", {src:imgSrc,style:{width:"100%",height:"100%",objectFit:"cover"},alt:"post"})
      ),
      React.createElement("div", {style:{padding:"10px 12px"}},
        React.createElement("div", {style:{display:"flex",justifyContent:"space-between",marginBottom:8}},
          React.createElement("div", {style:{display:"flex",gap:14}},
            React.createElement("svg", {width:24,height:24,fill:"none",stroke:"#262626",strokeWidth:2,viewBox:"0 0 24 24"}, React.createElement("path", {d:"M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"})),
            React.createElement("svg", {width:24,height:24,fill:"none",stroke:"#262626",strokeWidth:2,viewBox:"0 0 24 24"}, React.createElement("path", {d:"M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"})),
            React.createElement("svg", {width:24,height:24,fill:"none",stroke:"#262626",strokeWidth:2,viewBox:"0 0 24 24"}, React.createElement("line", {x1:22,y1:2,x2:11,y2:13}), React.createElement("polygon", {points:"22 2 15 22 11 13 2 9 22 2"}))
          ),
          React.createElement("svg", {width:24,height:24,fill:"none",stroke:"#262626",strokeWidth:2,viewBox:"0 0 24 24"}, React.createElement("path", {d:"M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"}))
        ),
        React.createElement("div", {style:{fontWeight:600,fontSize:13,marginBottom:4}}, "1,247 likes"),
        React.createElement("div", {style:{fontSize:13,color:"#262626",lineHeight:1.4}},
          React.createElement("span", {style:{fontWeight:600}}, bizName.toLowerCase().replace(/\s/g,"") + " "),
          capText.substring(0, 100),
          capText.length > 100 && React.createElement("span", {style:{color:"#8e8e8e"}}, "... more")
        ),
        tags.length > 0 && React.createElement("div", {style:{fontSize:13,color:"#00376b",marginTop:2}}, tags.slice(0,3).map(function(tg){return "#"+tg+" "}).join("")),
        React.createElement("div", {style:{fontSize:11,color:"#8e8e8e",marginTop:6}}, "2 HOURS AGO")
      )
    )
  );

  const renderFacebookPost = () => (
    React.createElement("div", {style:{background:"#fff",fontFamily:"-apple-system,BlinkMacSystemFont,sans-serif",fontSize:14}},
      React.createElement("div", {style:{display:"flex",alignItems:"center",padding:"12px",gap:10}},
        React.createElement("div", {style:{width:40,height:40,borderRadius:"50%",background:"#4ECDC4",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:700,fontSize:16}}, bizName[0]),
        React.createElement("div", {style:{flex:1}},
          React.createElement("div", {style:{fontWeight:600,fontSize:14,color:"#050505"}}, bizName),
          React.createElement("div", {style:{fontSize:12,color:"#65676b"}}, isAd ? "Sponsored" : "Just now")
        ),
        React.createElement("div", {style:{fontSize:20,color:"#65676b"}}, "...")
      ),
      React.createElement("div", {style:{padding:"0 12px 10px",fontSize:14,color:"#050505",lineHeight:1.4}}, capText.substring(0,120)),
      React.createElement("div", {style:{width:"100%",aspectRatio:"1.91/1",background:"#f0f0f0",overflow:"hidden"}},
        React.createElement("img", {src:imgSrc,style:{width:"100%",height:"100%",objectFit:"cover"},alt:"post"})
      ),
      isAd && React.createElement("div", {style:{padding:"10px 12px",background:"#f0f2f5",borderBottom:"1px solid #e4e6eb"}},
        React.createElement("div", {style:{fontSize:12,color:"#65676b",textTransform:"uppercase"}}, "yourwebsite.com"),
        React.createElement("div", {style:{fontSize:15,fontWeight:600,color:"#050505"}}, hl),
        React.createElement("div", {style:{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:4}},
          React.createElement("div", {style:{fontSize:13,color:"#65676b"}}, descText.substring(0,60)),
          React.createElement("div", {style:{padding:"6px 16px",background:"#e4e6eb",borderRadius:6,fontSize:13,fontWeight:600,color:"#050505"}}, cta)
        )
      ),
      React.createElement("div", {style:{display:"flex",justifyContent:"space-around",padding:"8px 12px",borderTop:"1px solid #e4e6eb"}},
        React.createElement("div", {style:{display:"flex",alignItems:"center",gap:6,color:"#65676b",fontSize:14}},
          React.createElement("svg", {width:20,height:20,fill:"none",stroke:"#65676b",strokeWidth:1.5,viewBox:"0 0 24 24"}, React.createElement("path", {d:"M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"})), "Like"
        ),
        React.createElement("div", {style:{display:"flex",alignItems:"center",gap:6,color:"#65676b",fontSize:14}},
          React.createElement("svg", {width:20,height:20,fill:"none",stroke:"#65676b",strokeWidth:1.5,viewBox:"0 0 24 24"}, React.createElement("path", {d:"M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"})), "Comment"
        ),
        React.createElement("div", {style:{display:"flex",alignItems:"center",gap:6,color:"#65676b",fontSize:14}},
          React.createElement("svg", {width:20,height:20,fill:"none",stroke:"#65676b",strokeWidth:1.5,viewBox:"0 0 24 24"}, React.createElement("path", {d:"M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"}), React.createElement("polyline", {points:"16 6 12 2 8 6"}), React.createElement("line", {x1:12,y1:2,x2:12,y2:15})), "Share"
        )
      )
    )
  );

  const renderTikTok = () => (
    React.createElement("div", {style:{background:"#000",fontFamily:"-apple-system,sans-serif",position:"relative",width:"100%",height:"100%",minHeight:480,overflow:"hidden"}},
      React.createElement("img", {src:imgSrc,style:{width:"100%",height:"100%",objectFit:"cover",position:"absolute",top:0,left:0},alt:"tiktok"}),
      React.createElement("div", {style:{position:"absolute",bottom:60,left:12,right:60,color:"#fff",textShadow:"0 1px 3px rgba(0,0,0,0.6)"}},
        React.createElement("div", {style:{fontWeight:700,fontSize:15,marginBottom:6}}, "@" + bizName.toLowerCase().replace(/\s/g,"")),
        React.createElement("div", {style:{fontSize:13,lineHeight:1.4,marginBottom:8}}, capText.substring(0,80)),
        React.createElement("div", {style:{display:"flex",alignItems:"center",gap:6,fontSize:12}},
          React.createElement("svg", {width:14,height:14,fill:"#fff",viewBox:"0 0 24 24"}, React.createElement("path", {d:"M9 18V5l12-2v13"}), React.createElement("circle", {cx:6,cy:18,r:3}), React.createElement("circle", {cx:18,cy:16,r:3})),
          "Original Sound - " + bizName
        )
      ),
      React.createElement("div", {style:{position:"absolute",right:10,bottom:80,display:"flex",flexDirection:"column",alignItems:"center",gap:18}},
        React.createElement("div", {style:{textAlign:"center"}},
          React.createElement("svg", {width:28,height:28,fill:"#fff",viewBox:"0 0 24 24"}, React.createElement("path", {d:"M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"})),
          React.createElement("div", {style:{color:"#fff",fontSize:11,marginTop:2}}, "24.5K")
        ),
        React.createElement("div", {style:{textAlign:"center"}},
          React.createElement("svg", {width:28,height:28,fill:"#fff",viewBox:"0 0 24 24"}, React.createElement("path", {d:"M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"})),
          React.createElement("div", {style:{color:"#fff",fontSize:11,marginTop:2}}, "1,832")
        ),
        React.createElement("div", {style:{textAlign:"center"}},
          React.createElement("svg", {width:28,height:28,fill:"#fff",viewBox:"0 0 24 24"}, React.createElement("path", {d:"M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"}), React.createElement("polyline", {points:"16 6 12 2 8 6"}), React.createElement("line", {x1:12,y1:2,x2:12,y2:15})),
          React.createElement("div", {style:{color:"#fff",fontSize:11,marginTop:2}}, "4,091")
        ),
        React.createElement("div", {style:{textAlign:"center"}},
          React.createElement("svg", {width:28,height:28,fill:"#fff",viewBox:"0 0 24 24"}, React.createElement("path", {d:"M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"})),
          React.createElement("div", {style:{color:"#fff",fontSize:11,marginTop:2}}, "Save")
        )
      )
    )
  );

  const renderGoogleAds = () => (
    React.createElement("div", {style:{background:"#fff",fontFamily:"Arial,sans-serif",padding:16,fontSize:14}},
      React.createElement("div", {style:{marginBottom:12}},
        React.createElement("span", {style:{fontSize:11,fontWeight:600,color:"#202124",background:"#f1f3f4",padding:"2px 6px",borderRadius:3,marginRight:6}}, "Sponsored"),
        React.createElement("span", {style:{fontSize:13,color:"#202124"}}, "Ad")
      ),
      React.createElement("div", {style:{display:"flex",alignItems:"center",gap:8,marginBottom:6}},
        React.createElement("div", {style:{width:26,height:26,borderRadius:"50%",background:"#4ECDC4",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:12,fontWeight:700}}, bizName[0]),
        React.createElement("div", null,
          React.createElement("div", {style:{fontSize:14,color:"#202124"}}, bizName),
          React.createElement("div", {style:{fontSize:12,color:"#4d5156"}}, "www.yourwebsite.com")
        )
      ),
      React.createElement("div", {style:{fontSize:18,color:"#1a0dab",cursor:"pointer",marginBottom:4,lineHeight:1.3}}, hl),
      isAd && React.createElement("div", {style:{fontSize:18,color:"#1a0dab",marginBottom:8,lineHeight:1.3}}, descText.substring(0,40)),
      React.createElement("div", {style:{fontSize:13,color:"#4d5156",lineHeight:1.5,marginBottom:12}}, descText),
      React.createElement("div", {style:{display:"flex",gap:16,borderTop:"1px solid #e8eaed",paddingTop:10}},
        React.createElement("div", {style:{fontSize:13,color:"#1a0dab"}}, cta),
        React.createElement("div", {style:{fontSize:13,color:"#1a0dab"}}, "Contact Us"),
        React.createElement("div", {style:{fontSize:13,color:"#1a0dab"}}, "Directions")
      )
    )
  );

  var getPlatformContent = function() {
    var p = (platform || "").toLowerCase();
    if (p.indexOf("tiktok") >= 0) return renderTikTok();
    if (p.indexOf("google") >= 0) return renderGoogleAds();
    if (p.indexOf("facebook") >= 0) return renderFacebookPost();
    return renderInstagramFeed();
  };

  var isTikTokOrStory = (platform || "").toLowerCase().indexOf("tiktok") >= 0;

  var mobileFrame = React.createElement("div", {style:{
    border:"12px solid #1a1a2e", borderRadius:50, boxShadow:"0 20px 60px rgba(0,0,0,0.15)",
    width:280, maxWidth:"100%", overflow:"hidden", position:"relative", background:"#000",
    aspectRatio: isTikTokOrStory ? "9/19.5" : undefined
  }},
    React.createElement("div", {style:{width:120,height:32,background:"#1a1a2e",borderRadius:20,position:"absolute",top:8,left:"50%",transform:"translateX(-50%)",zIndex:10}}),
    React.createElement("div", {style:{paddingTop:44,width:"100%",height:"100%",overflow:"auto",background:"#fff"}},
      getPlatformContent()
    )
  );

  var desktopFrame = React.createElement("div", {style:{
    border:"2px solid #e2e8f0", borderRadius:12, boxShadow:"0 10px 40px rgba(0,0,0,0.08)",
    maxWidth:560, width:"100%", overflow:"hidden", background:"#fff"
  }},
    React.createElement("div", {style:{height:36,background:"#f1f5f9",borderBottom:"1px solid #e2e8f0",display:"flex",alignItems:"center",padding:"0 12px",gap:6}},
      React.createElement("div", {style:{width:10,height:10,borderRadius:"50%",background:"#ff5f57"}}),
      React.createElement("div", {style:{width:10,height:10,borderRadius:"50%",background:"#febc2e"}}),
      React.createElement("div", {style:{width:10,height:10,borderRadius:"50%",background:"#28c840"}}),
      React.createElement("div", {style:{flex:1,marginLeft:8,background:"#fff",borderRadius:6,padding:"4px 12px",fontSize:12,color:"#94a3b8",border:"1px solid #e2e8f0"}},
        (platform || "").toLowerCase().indexOf("google") >= 0 ? "google.com/search" :
        (platform || "").toLowerCase().indexOf("facebook") >= 0 ? "facebook.com" :
        (platform || "").toLowerCase().indexOf("tiktok") >= 0 ? "tiktok.com" : "instagram.com"
      )
    ),
    React.createElement("div", {style:{padding:16,maxHeight:500,overflow:"auto"}},
      getPlatformContent()
    )
  );

  return React.createElement("div", {style:{marginTop:24,padding:24,background:"linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.68) 100%)",backdropFilter:"blur(34px) saturate(180%)",WebkitBackdropFilter:"blur(34px) saturate(180%)",borderRadius:22,border:"1px solid rgba(255,255,255,0.40)",boxShadow:"0 1px 0 rgba(255,255,255,0.70) inset, 0 18px 44px -18px rgba(11,107,211,0.28), 0 8px 24px -10px rgba(14,165,233,0.18)"}},
    React.createElement("div", {style:{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}},
      React.createElement("h3", {style:{fontSize:17,fontWeight:700,color:TEXT,margin:0}}, "Preview"),
      React.createElement("div", {style:{display:"flex",gap:4,background:"#f1f5f9",borderRadius:24,padding:3}},
        React.createElement("button", {onClick:function(){setPreviewMode("mobile")},style:toggleStyle(previewMode==="mobile")}, "Mobile"),
        React.createElement("button", {onClick:function(){setPreviewMode("desktop")},style:toggleStyle(previewMode==="desktop")}, "Desktop")
      )
    ),
    React.createElement("div", {style:{display:"flex",justifyContent:"center",padding:"8px 0"}},
      previewMode === "mobile" ? mobileFrame : desktopFrame
    )
  );
}


function CreatePostsView({ sys }) {
  const { autopilot: autopilotActive } = React.useContext(AutopilotContext);
  const [platform, setPlatform] = useState("Instagram"); const [postType, setPostType] = useState("Feed Post"); const [tone, setTone] = useState("Professional");
  const [topic, setTopic] = useState(""); const [gen, setGen] = useState(false); const [post, setPost] = useState(null);
  const [img, setImg] = useState(null); const [imgPrev, setImgPrev] = useState(null); const [saved, setSaved] = useState([]);
  const [aiModel, setAiModel] = useState("gpt-image-1-5"); const [genImg, setGenImg] = useState(false); const [aiImage, setAiImage] = useState(null);
  const [creativeError, setCreativeError] = useState("");
  const [postStatus, setPostStatus] = useState(null);
  const [trendingTopics, setTrendingTopics] = useState(null);
  const [trendingLoading, setTrendingLoading] = useState(false);
  const [abTestVariations, setAbTestVariations] = useState(null);
  const [abTestLoading, setAbTestLoading] = useState(false);
  const [contentCalendar, setContentCalendar] = useState(null);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [videoResult, setVideoResult] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [schedDate, setSchedDate] = useState("");
  const [schedTime, setSchedTime] = useState("09:00");
  const [scheduleStatus, setScheduleStatus] = useState(null); // null | 'scheduling' | 'scheduled'
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const fRef = useRef(null);

  // Default schedule date to tomorrow
  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setSchedDate(tomorrow.toISOString().split("T")[0]);
  }, []);

  // Load scheduled posts queue
  useEffect(() => {
    const loadScheduled = async () => {
      try {
        const data = await API.getScheduledPosts();
        if (data.posts) setScheduledPosts(data.posts);
      } catch { /* no scheduled posts yet */ }
    };
    loadScheduled();
  }, [scheduleStatus]);

  const handleImg = (e) => { const f = e.target.files?.[0]; if (!f) return; setImg(f); const r = new FileReader(); r.onload = ev => setImgPrev(ev.target.result); r.readAsDataURL(f); };

  const generate = async () => {
    if (!topic.trim()) return; setGen(true); setPost(null); setAiImage(null); setPostStatus(null); setShowScheduler(false); setScheduleStatus(null); setCreativeError("");

    // Run caption and image generation in parallel
    const captionPromise = askVella(sys.post, `${platform} ${postType} about: "${topic}". Tone: ${tone}.${img ? " User is attaching their own photo." : ""} IMPORTANT: Return ONLY the JSON object, nothing else. No intro text, no tips, no markdown.`);
    const imageAspect = getMatrixEntry(platform, postType).aspect_ratio;
    const imagePromise = !img ? API.generateCreative(topic + " - " + platform + " " + postType + " - " + tone + " style", aiModel, imageAspect) : Promise.resolve(null);

    const [captionResult, imageResult] = await Promise.allSettled([captionPromise, imagePromise]);

    // Process caption
    const raw = captionResult.status === "fulfilled" ? captionResult.value : "Caption generation failed. Please try again.";
    // Extract JSON from AI response — handles cases where AI wraps JSON in conversational text
    let postData = null;
    const stripped = raw.replace(/```json\n?|```\n?/g, "").trim();
    try { postData = JSON.parse(stripped); } catch {}
    if (!postData) {
      const startIdx = raw.indexOf('{"');
      if (startIdx === -1) { const altIdx = raw.indexOf('{\\n'); }
      if (startIdx >= 0) {
        let depth = 0; let endIdx = -1;
        for (let i = startIdx; i < raw.length; i++) {
          if (raw[i] === '{') depth++;
          else if (raw[i] === '}') { depth--; if (depth === 0) { endIdx = i; break; } }
        }
        if (endIdx > startIdx) try { postData = JSON.parse(raw.substring(startIdx, endIdx + 1)); } catch {}
      }
    }
    if (postData && postData.caption) {
      if (typeof postData.caption !== "string") postData.caption = String(postData.caption);
      setPost(postData);
    } else {
      // Last resort: use the raw text but clean out any JSON/markdown artifacts
      const cleanCaption = raw.replace(/```json\n?|```\n?/g, "").replace(/\{[^}]*"caption"[^}]*\}/g, "").replace(/^[\s\S]*?---\s*/, "").replace(/---[\s\S]*$/, "").trim() || raw;
      setPost({ caption: cleanCaption, hook: "", cta: "", hashtags: [] });
    }
    setGen(false);
    setPostStatus("pending");

    // Process image
    if (!img) {
      if (imageResult.status === "fulfilled" && imageResult.value) {
        const imageUrl = extractMediaUrl(imageResult.value);
        if (imageUrl) { setAiImage(imageUrl); }
        else { console.warn("No image URL in response:", imageResult.value); setCreativeError("Image generation completed but no image URL was returned. Please try again."); }
      } else if (imageResult.status === "rejected") {
        console.warn("Creative gen failed:", imageResult.reason); setCreativeError("Image generation failed: " + (imageResult.reason?.message || "Unknown error"));
      }
    }
  };

  const schedulePost = async () => {
    if (!schedDate || !schedTime) return;
    setScheduleStatus("scheduling");
    const scheduledAt = new Date(`${schedDate}T${schedTime}`).toISOString();
    try {
      await API.schedulePost({ platform, caption: post.caption, hashtags: post.hashtags, scheduledAt, postType });
      setScheduleStatus("scheduled");
      setShowScheduler(false);
      // Refresh queue
      const data = await API.getScheduledPosts();
      if (data.posts) setScheduledPosts(data.posts);
    } catch {
      setScheduleStatus("error");
    }
  };

  const cancelScheduled = async (id) => {
    try {
      await API.deletePost(id);
      setScheduledPosts(p => p.filter(s => s.id !== id));
    } catch {
      setScheduledPosts(p => p.filter(s => s.id !== id));
    }
  };

  // Friendly date display
  const fmtScheduled = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) + " at " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  };

  // Trending Topics handler
  const fetchTrendingTopics = async () => {
    setTrendingLoading(true); setTrendingTopics(null);
    const raw = await askVella(sys.post, `List 8 trending topics right now for ${platform} content. For each topic, provide the topic name and a brief reason why it's trending. Format as JSON array: [{"topic":"...","reason":"...","heat":"hot|warm|rising"}]`);
    try {
      const parsed = JSON.parse(raw.replace(/```json\n?|```\n?/g, "").trim());
      setTrendingTopics(Array.isArray(parsed) ? parsed : [{ topic: raw, reason: "", heat: "warm" }]);
    } catch { setTrendingTopics([{ topic: raw, reason: "", heat: "warm" }]); }
    setTrendingLoading(false);
  };

  // A/B Test handler
  const generateABTest = async () => {
    if (!post) return;
    setAbTestLoading(true); setAbTestVariations(null);
    const raw = await askVella(sys.post, `Create 3 A/B test variations of this ${platform} ${postType} caption. Original: "${post.caption}". Each variation should test a different angle (emotional, urgency, social proof, etc). Format as JSON array: [{"label":"Variation A - Emotional","caption":"...","hashtags":["..."],"angle":"What this tests"}]`);
    try {
      const parsed = JSON.parse(raw.replace(/```json\n?|```\n?/g, "").trim());
      setAbTestVariations(Array.isArray(parsed) ? parsed : [{ label: "Variation", caption: raw, hashtags: [], angle: "" }]);
    } catch { setAbTestVariations([{ label: "Variation", caption: raw, hashtags: [], angle: "" }]); }
    setAbTestLoading(false);
  };

  const generateVideo = async () => {
    setVideoLoading(true); setVideoResult(null);
    const videoTopic = topic.trim() || platform + " " + postType + " promotional video";
    // Prefer the (platform × post_type) matrix entry; video defaults to Reel-style
    // when the active post_type is a photo-only variant (Carousel / Feed image).
    const videoPostType = /reel|story|short|tiktok/i.test(platform + " " + postType) ? postType : "Reel";
    const videoAspect = getMatrixEntry(platform, videoPostType).aspect_ratio;
    try {
      const result = await API.generateCreative(videoTopic + " - short video ad - cinematic style", "kling-2-5", videoAspect);
      const videoUrl = extractMediaUrl(result);
      if (videoUrl) setVideoResult({ url: videoUrl, model: result?.primary?.model || "kling_2_5" });
      else setVideoResult({ error: "No video URL returned. Try again." });
    } catch (e) { setVideoResult({ error: e.message || "Video generation failed" }); }
    setVideoLoading(false);
  };

  // Content Calendar handler
  const generateContentCalendar = async () => {
    setCalendarLoading(true); setContentCalendar(null);
    const raw = await askVella(sys.post, `Generate a 7-day content calendar for ${platform}. For each day, provide the post type, topic, best time to post, and a brief caption idea. Format as JSON array: [{"day":"Monday","type":"Feed Post","topic":"...","time":"9:00 AM","caption":"...","hashtags":["..."]}]`);
    try {
      const parsed = JSON.parse(raw.replace(/```json\n?|```\n?/g, "").trim());
      setContentCalendar(Array.isArray(parsed) ? parsed : [{ day: "Monday", type: "Post", topic: raw, time: "", caption: "", hashtags: [] }]);
    } catch { setContentCalendar([{ day: "Monday", type: "Post", topic: raw, time: "", caption: "", hashtags: [] }]); }
    setCalendarLoading(false);
  };

  return (
    <div>
      {autopilotActive && <div style={{ background: "linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)", border: "1px solid #6EE7B7", borderRadius: 12, padding: "14px 18px", marginBottom: 20, display: "flex", alignItems: "center", gap: 12 }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/><path d="M9 12l2 2 4-4"/></svg>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#065F46" }}>Autopilot is ON</div>
          <div style={{ fontSize: 12, color: "#047857" }}>Vella is automatically managing this for you.</div>
        </div>
      </div>}
      <h2 style={{ fontSize: 24, fontWeight: 700, color: TEXT, marginBottom: 6, letterSpacing: "-0.022em" }}>Create Posts</h2>
      <VellaNudge text="I'll write scroll-stopping content tailored to your audience. Pick a platform and tell me the topic." small />
      <div style={{ display: "flex", gap: 8, margin: "14px 0 20px" }}>{["Instagram", "Facebook", "TikTok"].map(p => <div key={p} style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.55)", backdropFilter: "blur(18px) saturate(180%)", WebkitBackdropFilter: "blur(18px) saturate(180%)", borderRadius: 999, padding: "6px 14px", border: `1px solid rgba(255,255,255,0.45)` }}><PlatformBadge platform={p} /><span style={{ fontSize: 12, fontWeight: 500, color: TEXT2 }}>{p}</span></div>)}</div>
      {/* Content Intelligence */}
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: TEXT, marginBottom: 12, letterSpacing: "-0.022em" }}>Content Intelligence</h3>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {[
            { label: "Trending Topics", desc: "View trending topics for " + platform, icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>, action: fetchTrendingTopics, loading: trendingLoading, color: BLUE },
            { label: "A/B Test", desc: post ? "Generate 3 variations of your post" : "Generate a post first, then A/B test it", icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="8" height="18" rx="2"/><rect x="14" y="3" width="8" height="18" rx="2"/></svg>, action: generateABTest, loading: abTestLoading, disabled: !post, color: BLUE },
            { label: "Content Calendar", desc: "Plan a full week of " + platform + " content", icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>, action: generateContentCalendar, loading: calendarLoading, color: BLUE },
            { label: "Video Creator", desc: "Generate a short video ad with AI", icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>, action: generateVideo, loading: videoLoading, color: BLUE },
          ].map((item, i) => (
            <div key={i}
              style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.68) 100%)", backdropFilter: "blur(34px) saturate(180%)", WebkitBackdropFilter: "blur(34px) saturate(180%)", borderRadius: 18, padding: "14px 16px", border: `1px solid ${item.loading ? BLUE : "rgba(255,255,255,0.45)"}`, flex: "1 1 150px", cursor: item.disabled ? "not-allowed" : "pointer", display: "flex", alignItems: "flex-start", gap: 12, transition: "all 0.18s ease", boxShadow: "0 1px 0 rgba(255,255,255,0.70) inset, 0 8px 24px -10px rgba(14,165,233,0.18)", opacity: item.disabled ? 0.5 : 1 }}
              onClick={() => { if (!item.disabled && !item.loading) item.action(); }}
              onMouseEnter={e => { if (!item.disabled) { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 1px 0 rgba(255,255,255,0.80) inset, 0 18px 44px -18px rgba(11,107,211,0.30), 0 8px 24px -10px rgba(14,165,233,0.22)"; e.currentTarget.style.borderColor = BLUE; }}}
              onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 1px 0 rgba(255,255,255,0.70) inset, 0 8px 24px -10px rgba(14,165,233,0.18)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.45)"; }}>
              <div style={{ flexShrink: 0, marginTop: 1 }}>{item.loading ? <div style={{ width: 18, height: 18, border: "2px solid " + BLUE, borderTopColor: "transparent", borderRadius: "50%", animation: "spin .7s linear infinite" }} /> : item.icon}</div>
              <div>
                <p style={{ fontWeight: 700, fontSize: 13, color: TEXT, marginBottom: 3 }}>{item.label}</p>
                <p style={{ fontSize: 12, color: TEXT2, lineHeight: 1.4 }}>{item.loading ? "Loading..." : item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Trending Topics Results */}
      {trendingTopics && (
        <div style={{ ...card3d, marginBottom: 20, borderLeft: "3px solid " + BLUE }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: TEXT }}>Trending on {platform}</h3>
            </div>
            <button onClick={() => setTrendingTopics(null)} style={{ background: "none", border: "none", cursor: "pointer", color: TEXT3, fontSize: 18, padding: 4 }}>x</button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {trendingTopics.map((t, i) => {
              const heatColor = t.heat === "hot" ? "#EF4444" : t.heat === "rising" ? "#F59E0B" : BLUE;
              return (
                <div key={i} onClick={() => setTopic(t.topic)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: "rgba(255,255,255,0.35)", backdropFilter: "blur(18px) saturate(180%)", WebkitBackdropFilter: "blur(18px) saturate(180%)", borderRadius: 14, cursor: "pointer", border: "1px solid rgba(255,255,255,0.45)", transition: "all 0.15s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = BLUE_LIGHT; e.currentTarget.style.borderColor = BLUE; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.35)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.45)"; }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: TEXT3, width: 22 }}>#{i + 1}</span>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: TEXT }}>{t.topic}</span>
                    {t.reason && <p style={{ fontSize: 12, color: TEXT2, marginTop: 2 }}>{t.reason}</p>}
                  </div>
                  {t.heat && <span style={{ fontSize: 10, fontWeight: 700, color: heatColor, background: heatColor + "15", padding: "2px 8px", borderRadius: 10, textTransform: "uppercase" }}>{t.heat}</span>}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </div>
              );
            })}
          </div>
          <p style={{ fontSize: 11, color: TEXT3, marginTop: 10 }}>Click a topic to use it as your post topic</p>
        </div>
      )}

      {/* A/B Test Variations */}
      {abTestVariations && (
        <div style={{ ...card3d, marginBottom: 20, borderLeft: "3px solid " + BLUE }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="8" height="18" rx="2"/><rect x="14" y="3" width="8" height="18" rx="2"/></svg>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: TEXT }}>A/B Test Variations</h3>
            </div>
            <button onClick={() => setAbTestVariations(null)} style={{ background: "none", border: "none", cursor: "pointer", color: TEXT3, fontSize: 18, padding: 4 }}>x</button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {abTestVariations.map((v, i) => {
              const labels = ["A", "B", "C", "D"];
              return (
                <div key={i} style={{ padding: 16, background: "rgba(255,255,255,0.35)", backdropFilter: "blur(18px) saturate(180%)", WebkitBackdropFilter: "blur(18px) saturate(180%)", borderRadius: 18, border: "1px solid rgba(255,255,255,0.45)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ width: 26, height: 26, borderRadius: "50%", background: BLUE, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700 }}>{labels[i] || i + 1}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: TEXT }}>{v.label || ("Variation " + (labels[i] || i + 1))}</span>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <CopyBtn text={v.caption} />
                      <button style={{ ...Bsm, background: BLUE, color: "#fff", border: "none" }} onClick={() => { setPost({ ...post, caption: v.caption, hashtags: v.hashtags || post.hashtags }); setAbTestVariations(null); }}>Use This</button>
                    </div>
                  </div>
                  {v.angle && <p style={{ fontSize: 11, color: BLUE, fontWeight: 600, marginBottom: 6 }}>{v.angle}</p>}
                  <p style={{ fontSize: 13, lineHeight: 1.6, color: TEXT2, whiteSpace: "pre-wrap" }}>{v.caption}</p>
                  {v.hashtags?.length > 0 && <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 8 }}>{v.hashtags.map((h, hi) => <span key={hi} style={tg(BLUE_LIGHT, BLUE)}>#{h.replace("#", "")}</span>)}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Content Calendar */}
      {contentCalendar && (
        <div style={{ ...card3d, marginBottom: 20, borderLeft: "3px solid " + BLUE }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: TEXT }}>{platform} Weekly Calendar</h3>
            </div>
            <button onClick={() => setContentCalendar(null)} style={{ background: "none", border: "none", cursor: "pointer", color: TEXT3, fontSize: 18, padding: 4 }}>x</button>
          </div>
          {(() => { const dayColors = [BLUE, "#3B82F6", "#6366F1", "#8B5CF6", "#3B82F6", "#2563EB", "#1D4ED8"]; return (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10 }}>
            {contentCalendar.map((day, i) => {
              const dc = dayColors[i % dayColors.length];
              return (
                <div key={i} style={{ padding: 14, background: "rgba(255,255,255,0.35)", backdropFilter: "blur(18px) saturate(180%)", WebkitBackdropFilter: "blur(18px) saturate(180%)", borderRadius: 18, border: "1px solid rgba(255,255,255,0.45)", borderTop: "3px solid " + dc }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: dc }}>{day.day}</span>
                    {day.time && <span style={{ fontSize: 11, color: TEXT3 }}>{day.time}</span>}
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 600, color: TEXT3, background: dc + "15", padding: "2px 8px", borderRadius: 6 }}>{day.type}</span>
                  <p style={{ fontSize: 13, fontWeight: 600, color: TEXT, marginTop: 8, marginBottom: 4 }}>{day.topic}</p>
                  {day.caption && <p style={{ fontSize: 12, color: TEXT2, lineHeight: 1.4 }}>{day.caption}</p>}
                  {day.hashtags?.length > 0 && <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 6 }}>{day.hashtags.slice(0, 3).map((h, hi) => <span key={hi} style={{ fontSize: 10, color: dc, fontWeight: 600 }}>#{h.replace("#", "")}</span>)}</div>}
                  <button style={{ ...Bsm, marginTop: 8, width: "100%", textAlign: "center", justifyContent: "center", display: "flex" }} onClick={() => { setTopic(day.topic || day.caption); }}>Use Topic</button>
                </div>
              );
            })}
          </div>
          ); })()}
        </div>
      )}

      {/* Video Creator Result */}
      {videoResult && (
        <div style={{ ...card3d, marginBottom: 20, borderLeft: "3px solid " + BLUE }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: TEXT }}>AI Video</h3>
            </div>
            <button onClick={() => setVideoResult(null)} style={{ background: "none", border: "none", cursor: "pointer", color: TEXT3, fontSize: 18, padding: 4 }}>x</button>
          </div>
          {videoResult.error ? (
            <div style={{ padding: 12, background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, color: "#dc2626", fontSize: 13 }}>{videoResult.error}</div>
          ) : videoResult.url ? (
            <div>
              <video src={videoResult.url} controls style={{ width: "100%", maxHeight: 360, borderRadius: 12, background: "#000" }} />
              <p style={{ fontSize: 12, color: TEXT3, marginTop: 8 }}>Generated with Vella's creative engine</p>
            </div>
          ) : null}
        </div>
      )}

      <div style={card3d}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: TEXT, marginBottom: 20, letterSpacing: "-0.022em" }}>New Post</h3>
        <div style={{ display: "flex", gap: 14, marginBottom: 14, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 150 }}><label style={L}>Platform</label><select style={{ ...S, width: "100%" }} value={platform} onChange={e => setPlatform(e.target.value)}>{["Instagram", "Facebook", "TikTok"].map(p => <option key={p}>{p}</option>)}</select></div>
          <div style={{ flex: 1, minWidth: 150 }}><label style={L}>Type</label><select style={{ ...S, width: "100%" }} value={postType} onChange={e => setPostType(e.target.value)}>{["Feed Post", "Carousel", "Story", "Reel / Video", "Text Post"].map(p => <option key={p}>{p}</option>)}</select></div>
          <div style={{ flex: 1, minWidth: 150 }}><label style={L}>Tone</label><select style={{ ...S, width: "100%" }} value={tone} onChange={e => setTone(e.target.value)}>{["Professional", "Casual & Fun", "Bold & Edgy", "Educational", "Inspirational", "Humorous"].map(t => <option key={t}>{t}</option>)}</select></div>
        </div>
        <div style={{ marginBottom: 14 }}><div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: TEXT3, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>Suggested prompts</div>
              <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
                {["Professional brand photography", "Bold text overlay with CTA", "Minimalist product showcase", "Behind-the-scenes team photo", "Customer testimonial card"].map(chip => (
                  <span key={chip} onClick={() => setTopic(chip)} style={{ padding: "8px 14px", borderRadius: 20, border: "1px solid #E2E8F0", background: "#F8FAFB", fontSize: 13, color: "#64748B", fontWeight: 500, cursor: "pointer", whiteSpace: "nowrap", transition: "all 0.15s ease" }} onMouseEnter={e => { e.target.style.background = "#F0FDFA"; e.target.style.borderColor = "#4ECDC4"; e.target.style.color = "#0D9488"; }} onMouseLeave={e => { e.target.style.background = "#F8FAFB"; e.target.style.borderColor = "#E2E8F0"; e.target.style.color = "#64748B"; }}>{chip}</span>
                ))}
              </div>
            </div>
            <label style={L}>Topic</label><textarea style={{ ...T, height: 76 }} placeholder="e.g. Spring sale -- 30% off everything this weekend..." value={topic} onChange={e => setTopic(e.target.value)} /></div>
        <div style={{ marginBottom: 16 }}>
          <label style={L}>Attach Image (optional)</label>
          <input type="file" accept="image/*" ref={fRef} onChange={handleImg} style={{ display: "none" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={() => fRef.current?.click()} style={Bg}>Upload</button>
            {imgPrev && <div style={{ position: "relative" }}><img src={imgPrev} style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 10 }} /><button onClick={() => { setImg(null); setImgPrev(null); }} style={{ position: "absolute", top: -6, right: -6, background: CARD, border: `1px solid ${BORDER}`, borderRadius: "50%", width: 20, height: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={DANGER} strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button></div>}
          </div>
        </div>
        {/* AI Image Model Selector */}
        <div style={{ marginTop: 16, padding: 16, background: "linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.68) 100%)", backdropFilter: "blur(34px) saturate(180%)", WebkitBackdropFilter: "blur(34px) saturate(180%)", borderRadius: 20, border: "1px solid rgba(255,255,255,0.45)", boxShadow: "0 1px 0 rgba(255,255,255,0.70) inset, 0 8px 24px -10px rgba(14,165,233,0.18)" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: TEXT, marginBottom: 10 }}>AI Creative Model</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[
              { id: "gpt-image-1-5", name: "GPT Image 1.5", desc: "Best for photorealism + typography -- product shots, labels, packaging", color: "#F59E0B" },
              { id: "flux-pro-ultra", name: "FLUX Pro Ultra", desc: "DSLR-quality photorealism at 2K -- lifestyle imagery, high-detail shots", color: "#3B82F6" },
              { id: "ideogram-3", name: "Ideogram 3.0", desc: "Typography specialist -- 90% text accuracy, logos, CTAs, headlines", color: "#8B5CF6" },
              { id: "imagen-4", name: "Imagen 4", desc: "Cost-effective photorealism -- great quality at lower cost", color: "#10B981" }
            ].map(m => (
              <div key={m.id} onClick={() => setAiModel(m.id)} style={{ flex: "1 1 220px", padding: "12px 14px", borderRadius: 18, border: aiModel === m.id ? "2px solid " + m.color : "1px solid rgba(255,255,255,0.45)", background: "linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.68) 100%)", backdropFilter: "blur(24px) saturate(180%)", WebkitBackdropFilter: "blur(24px) saturate(180%)", boxShadow: "0 1px 0 rgba(255,255,255,0.70) inset", cursor: "pointer", display: "flex", alignItems: "center", gap: 10, transition: "all 0.15s ease", borderLeft: "3px solid " + m.color }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: m.color, flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: TEXT }}>{m.name}</div>
                  <div style={{ fontSize: 11, color: TEXT3, marginTop: 2 }}>{m.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <button style={{ ...B, opacity: gen ? 0.6 : 1, borderRadius: 999, padding: "12px 32px", marginTop: 16 }} onClick={generate} disabled={gen}>{gen ? "Generating..." : "Generate Post"}</button>
      </div>
      {gen && <Loader text="Vella is crafting your post..." />}
                {creativeError && <div style={{ padding: 12, marginBottom: 12, background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, color: "#dc2626", fontSize: 13, fontWeight: 500 }}>{creativeError}</div>}
      {post && <div style={{ ...card3d, boxShadow: SHADOW_LG }}>

                  {post && <DevicePreviewSection platform={platform} caption={typeof post === "string" ? post : (post?.caption || "")} imageUrl={imgPrev || aiImage} businessName={sys._biz?.name || "Your Business"} isAd={false} />}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}><PlatformBadge platform={platform} size={22} /><h3 style={{ fontSize: 16, fontWeight: 700, color: TEXT }}>{platform} {postType}</h3></div>
          <div style={{ display: "flex", gap: 6 }}><CopyBtn text={post.caption} /><button style={Bsm} onClick={() => setSaved(p => [{ ...post, platform, postType, img: imgPrev, id: Date.now() }, ...p])}>Save</button></div>
        </div>
        {imgPrev && <img src={imgPrev} style={{ width: "100%", maxHeight: 220, objectFit: "cover", borderRadius: 14, marginBottom: 14 }} />}
        {!imgPrev && aiImage && <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: BLUE, marginBottom: 6, display: "flex", alignItems: "center", gap: 4 }}>AI-Generated Creative ({{"gpt-image-1-5": "GPT Image 1.5", "flux-pro-ultra": "FLUX Pro Ultra", "ideogram-3": "Ideogram 3.0", "imagen-4": "Imagen 4"}[aiModel] || aiModel})</div>
          <img src={aiImage} style={{ width: "100%", maxHeight: 280, objectFit: "cover", borderRadius: 14, border: "2px solid " + BLUE_LIGHT }} />
        </div>}
        {post.hook && <><label style={L}>Hook</label><p style={{ fontSize: 16, fontWeight: 600, color: BLUE, marginBottom: 12 }}>{post.hook}</p></>}
        <label style={L}>Caption</label><div style={{ background: "rgba(255,255,255,0.35)", backdropFilter: "blur(18px) saturate(180%)", WebkitBackdropFilter: "blur(18px) saturate(180%)", border: "1px solid rgba(255,255,255,0.45)", borderRadius: 18, padding: 16, fontSize: 14, lineHeight: 1.75, whiteSpace: "pre-wrap", marginBottom: 12, color: TEXT }}>{post.caption}</div>
        {post.cta && <><label style={L}>CTA</label><p style={{ fontSize: 14, fontWeight: 600, color: WARNING, marginBottom: 12 }}>{post.cta}</p></>}
        {post.hashtags?.length > 0 && <><label style={L}>Hashtags</label><div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>{post.hashtags.map((h, i) => <span key={i} style={tg(BLUE_LIGHT, BLUE)}>#{h.replace("#", "")}</span>)}</div></>}

        {/* Vella Post Approval */}
        {postStatus === "pending" && (
          <div style={{ background: BLUE_LIGHT, border: `1px solid ${BLUE_SOFT}`, borderRadius: 14, padding: 18, marginTop: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <VellaAvatar size={24} />
              <span style={{ fontSize: 13, fontWeight: 700, color: BLUE }}>Vella -- Ready to publish to {platform}?</span>
            </div>
            <p style={{ fontSize: 13, color: TEXT2, marginBottom: 14, lineHeight: 1.5 }}>Post it now or schedule it for the perfect time.</p>

            {!showScheduler ? (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button onClick={async () => {
                  setPostStatus("posting");
                  try {
                    await API.publishPost({ platform, caption: post.caption, hashtags: post.hashtags, image: img });
                    setPostStatus("approved");
                  } catch {
                    setPostStatus("error");
                  }
                }} style={{ ...B, padding: "9px 20px", fontSize: 13, background: SUCCESS, borderRadius: 999 }}>Post Now</button>
                <button onClick={() => setShowScheduler(true)} style={{ ...B, padding: "9px 20px", fontSize: 13, borderRadius: 999 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    Schedule
                  </span>
                </button>
                <button onClick={() => setPostStatus("editing")} style={{ ...Bg, padding: "9px 16px", fontSize: 13, borderRadius: 999 }}>Edit</button>
                <button onClick={() => setPostStatus("declined")} style={{ ...Bg, padding: "9px 16px", fontSize: 13, borderRadius: 999, color: DANGER, borderColor: `${DANGER}44` }}>Not Now</button>
              </div>
            ) : (
              /* Schedule picker */
              <div style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.68) 100%)", backdropFilter: "blur(34px) saturate(180%)", WebkitBackdropFilter: "blur(34px) saturate(180%)", borderRadius: 20, border: `1px solid rgba(255,255,255,0.45)`, boxShadow: "0 1px 0 rgba(255,255,255,0.70) inset, 0 8px 24px -10px rgba(14,165,233,0.18)", padding: 18, marginTop: 4 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: TEXT, marginBottom: 14 }}>Pick a date and time</p>
                <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 150 }}>
                    <label style={L}>Date</label>
                    <input type="date" style={{ ...I }} value={schedDate} onChange={e => setSchedDate(e.target.value)} min={new Date().toISOString().split("T")[0]} />
                  </div>
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <label style={L}>Time</label>
                    <select style={{ ...S }} value={schedTime} onChange={e => setSchedTime(e.target.value)}>
                      {Array.from({ length: 24 }, (_, h) =>
                        ["00", "30"].map(m => {
                          const val = `${String(h).padStart(2, "0")}:${m}`;
                          const label = new Date(`2000-01-01T${val}`).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
                          return <option key={val} value={val}>{label}</option>;
                        })
                      )}
                    </select>
                  </div>
                </div>

                {/* Best time suggestions */}
                <div style={{ marginBottom: 16 }}>
                  <label style={L}>Best Times for {platform}</label>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {(platform === "Instagram"
                      ? [["Mon", "09:00"], ["Wed", "11:00"], ["Fri", "17:00"], ["Sun", "10:00"]]
                      : platform === "Facebook"
                      ? [["Tue", "09:00"], ["Thu", "13:00"], ["Fri", "11:00"]]
                      : [["Tue", "07:00"], ["Thu", "20:00"], ["Sat", "09:00"]]
                    ).map(([day, time], i) => {
                      const next = new Date();
                      const days = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
                      const target = days.indexOf(day);
                      const diff = (target - next.getDay() + 7) % 7 || 7;
                      next.setDate(next.getDate() + diff);
                      const d = next.toISOString().split("T")[0];
                      const label = new Date(`2000-01-01T${time}`).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
                      return (
                        <button key={i} onClick={() => { setSchedDate(d); setSchedTime(time); }}
                          style={{ ...Bgsm, borderRadius: 999, fontSize: 12, padding: "5px 12px", background: (schedDate === d && schedTime === time) ? BLUE_LIGHT : "rgba(255,255,255,0.55)", borderColor: (schedDate === d && schedTime === time) ? BLUE : "rgba(255,255,255,0.45)", color: (schedDate === d && schedTime === time) ? BLUE : TEXT2 }}>
                          {day} {label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={schedulePost} disabled={scheduleStatus === "scheduling"}
                    style={{ ...B, padding: "10px 24px", fontSize: 13, borderRadius: 999, opacity: scheduleStatus === "scheduling" ? 0.6 : 1 }}>
                    {scheduleStatus === "scheduling" ? "Scheduling..." : "Confirm Schedule"}
                  </button>
                  <button onClick={() => setShowScheduler(false)} style={{ ...Bg, padding: "10px 16px", fontSize: 13, borderRadius: 999 }}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}
        {postStatus === "posting" && (
          <div style={{ background: `${BLUE}0F`, border: `1px solid ${BLUE}33`, borderRadius: 14, padding: 16, marginTop: 8, display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 14, height: 14, border: `2px solid ${BLUE}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin .7s linear infinite" }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: BLUE }}>Publishing to {platform}...</span>
          </div>
        )}
        {postStatus === "approved" && (
          <div style={{ background: `${SUCCESS}0F`, border: `1px solid ${SUCCESS}33`, borderRadius: 14, padding: 16, marginTop: 8, display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: SUCCESS, animation: "pulse 2s infinite" }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: SUCCESS }}>Posted to {platform} successfully</span>
          </div>
        )}
        {scheduleStatus === "scheduled" && (
          <div style={{ background: `${BLUE}0F`, border: `1px solid ${BLUE}33`, borderRadius: 14, padding: 16, marginTop: 8, display: "flex", alignItems: "center", gap: 10 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            <span style={{ fontSize: 13, fontWeight: 600, color: BLUE }}>Scheduled for {fmtScheduled(`${schedDate}T${schedTime}`)}</span>
          </div>
        )}
        {postStatus === "declined" && (
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button style={{ ...Bg }} onClick={generate}>Regenerate</button>
            <button style={{ ...Bgsm, padding: "9px 14px" }} onClick={() => setSaved(p => [{ ...post, platform, postType, img: imgPrev, id: Date.now() }, ...p])}>Save for Later</button>
          </div>
        )}
        {postStatus !== "pending" && postStatus !== "approved" && postStatus !== "declined" && scheduleStatus !== "scheduled" && (
          <button style={{ ...Bg, marginTop: 8 }} onClick={generate}>Regenerate</button>
        )}
      </div>}
      {saved.length > 0 && <div style={card3d}><h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 14 }}>Drafts ({saved.length})</h3>
        {saved.map((p, i) => <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 0", borderBottom: i < saved.length - 1 ? `1px solid ${BORDER}` : "none" }}>
          {p.img && <img src={p.img} style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 10 }} />}
          <div style={{ flex: 1, minWidth: 0 }}><div style={{ display: "flex", gap: 6, marginBottom: 3 }}><PlatformBadge platform={p.platform} size={16} /><span style={{ fontSize: 12, fontWeight: 500, color: TEXT2 }}>{p.platform} . {p.postType}</span></div><p style={{ fontSize: 13, color: TEXT2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.caption}</p></div>
          <button style={Bsm}>Post</button></div>)}
      </div>}

      {/* Scheduled Queue */}
      {scheduledPosts.length > 0 && (
        <div style={card3d}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: TEXT }}>Scheduled</h3>
            <span style={{ fontSize: 11, fontWeight: 700, color: BLUE, background: BLUE_LIGHT, borderRadius: 6, padding: "2px 8px" }}>{scheduledPosts.length}</span>
          </div>
          {scheduledPosts.map((p, i) => (
            <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 0", borderBottom: i < scheduledPosts.length - 1 ? `1px solid ${BORDER}` : "none" }}>
              <div style={{ width: 42, height: 42, borderRadius: 10, background: BLUE_LIGHT, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                  <PlatformBadge platform={p.platform} size={14} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: BLUE }}>{fmtScheduled(p.scheduledAt)}</span>
                </div>
                <p style={{ fontSize: 13, color: TEXT2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.caption || p.content}</p>
              </div>
              <button onClick={() => cancelScheduled(p.id)} style={{ ...Bgsm, color: DANGER, borderColor: `${DANGER}33`, flexShrink: 0 }}>Cancel</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===========================================
// INBOX (preserved logic, dark theme)
// ===========================================
function InboxView({ sys }) {
  const SAMPLE = [
    { id: 1, type: "comment", platform: "Instagram", author: "sarah_styles22", text: "Love this product! Where can I buy it?", time: "10m", lead: "hot" },
    { id: 2, type: "dm", platform: "Facebook", author: "Mike Johnson", text: "Do you offer bulk discounts for 50+ units?", time: "25m", lead: "hot" },
    { id: 3, type: "comment", platform: "TikTok", author: "shopaholic_jen", text: "Looks amazing but price seems high vs competitors", time: "1h", lead: "warm" },
    { id: 4, type: "dm", platform: "Instagram", author: "local_biz_owner", text: "Interested in your ad services. Can we talk?", time: "2h", lead: "hot" },
    { id: 5, type: "comment", platform: "Facebook", author: "David R.", text: "Ordered last week -- still no shipping confirmation??", time: "3h", lead: "cold" },
  ];
  const [msgs, setMsgs] = useState(SAMPLE);
  const [syncing, setSyncing] = useState(false);
  const [liveData, setLiveData] = useState(false);
  const [replies, setReplies] = useState({}); const [loading, setLoading] = useState({}); const [sent, setSent] = useState({});
  const [leadScores, setLeadScores] = useState({});
  const [scoringLeads, setScoringLeads] = useState(false);
  const [segmentData, setSegmentData] = useState(null);
  const [segmenting, setSegmenting] = useState(false);

  // Load live inbox on mount
  useEffect(() => {
    const loadInbox = async () => {
      try {
        const data = await API.getInbox({ unreplied: false });
        if (data.items && data.items.length > 0) {
          setMsgs(data.items.map(item => ({
            id: item.id,
            type: item.type,
            platform: item.platform,
            author: item.authorName,
            text: item.message,
            time: timeAgo(item.createdAt),
            isLead: item.isLead,
            platformMessageId: item.platformMessageId,
          })));
          setLiveData(true);
        }
      } catch { /* use sample data */ }
    };
    loadInbox();
  }, []);

  const timeAgo = (date) => {
    if (!date) return "";
    const diff = (Date.now() - new Date(date)) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  const syncInbox = async () => {
    setSyncing(true);
    try {
      const result = await API.syncInbox();
      const data = await API.getInbox({});
      if (data.items && data.items.length > 0) {
        setMsgs(data.items.map(item => ({
          id: item.id, type: item.type, platform: item.platform,
          author: item.authorName, text: item.message, time: timeAgo(item.createdAt),
          isLead: item.isLead, platformMessageId: item.platformMessageId,
        })));
        setLiveData(true);
      }
      alert(result.message || "Inbox synced!");
    } catch { alert("Sync failed. Make sure your platforms are connected in Settings."); }
    setSyncing(false);
  };

  const reply = async (m) => { setLoading(p => ({ ...p, [m.id]: true })); const r = await askVella(sys.reply, `Platform: ${m.platform}\nType: ${m.type}\nFrom: ${m.author}\nMessage: "${m.text}"`); setReplies(p => ({ ...p, [m.id]: r })); setLoading(p => ({ ...p, [m.id]: false })); };
  const replyAll = async () => { for (const m of msgs) if (!replies[m.id]) await reply(m); };
  const sendReply = async (m) => {
    setSent(p => ({ ...p, [m.id]: "sending" }));
    try {
      await API.sendReply({ platform: m.platform, type: m.type, messageId: m.platformMessageId || m.id, reply: replies[m.id] });
      setSent(p => ({ ...p, [m.id]: "sent" }));
    } catch {
      setSent(p => ({ ...p, [m.id]: "error" }));
    }
  };

  // Score Leads -- analyze unreplied messages and assign lead scores
  const scoreLeads = async () => {
    setScoringLeads(true);
    const unreplied = msgs.filter(m => !replies[m.id]);
    const msgSummary = unreplied.map(m => `ID:${m.id} | @${m.author} | ${m.platform} ${m.type} | "${m.text}"`).join("\n");
    const raw = await askVella(sys.reply, `Analyze these inbox messages and score each as a lead. For each message, determine: lead score (hot/warm/cold), buying intent (1-10), urgency (1-10), and a brief reason. Format as JSON array: [{"id":1,"score":"hot","intent":8,"urgency":9,"reason":"Asking to buy - high purchase intent"}]\n\nMessages:\n${msgSummary}`);
    try {
      const parsed = JSON.parse(raw.replace(/```json\n?|```\n?/g, "").trim());
      const scoreMap = {};
      (Array.isArray(parsed) ? parsed : []).forEach(s => { scoreMap[s.id] = s; });
      setLeadScores(scoreMap);
      // Also update lead field on messages
      setMsgs(prev => prev.map(m => scoreMap[m.id] ? { ...m, lead: scoreMap[m.id].score } : m));
    } catch { /* scoring failed silently */ }
    setScoringLeads(false);
  };

  // Segment Audience -- group messages by audience type
  const segmentAudience = async () => {
    setSegmenting(true); setSegmentData(null);
    const msgSummary = msgs.map(m => `@${m.author} | ${m.platform} ${m.type} | "${m.text}"`).join("\n");
    const raw = await askVella(sys.reply, `Analyze these inbox messages and segment the audience. Group the senders into segments (e.g., "Ready to Buy", "Price Sensitive", "Support Needed", "Brand Advocates", "Potential Partners"). For each segment, list the authors and a recommended action. Format as JSON array: [{"segment":"Ready to Buy","icon":"fire","authors":["sarah_styles22","local_biz_owner"],"count":2,"action":"Prioritize with discount offer or direct link to purchase","priority":"high"}]\n\nMessages:\n${msgSummary}`);
    try {
      const parsed = JSON.parse(raw.replace(/```json\n?|```\n?/g, "").trim());
      setSegmentData(Array.isArray(parsed) ? parsed : [{ segment: "All", authors: msgs.map(m => m.author), count: msgs.length, action: raw, priority: "medium" }]);
    } catch { setSegmentData([{ segment: "All", authors: msgs.map(m => m.author), count: msgs.length, action: raw, priority: "medium" }]); }
    setSegmenting(false);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <h2 style={{ fontSize: 24, fontWeight: 700, color: TEXT, letterSpacing: "-0.022em" }}>Inbox</h2>
            {liveData && <span style={{ fontSize: 10, fontWeight: 700, color: SUCCESS, background: `${SUCCESS}15`, padding: "2px 8px", borderRadius: 6 }}>LIVE</span>}
          </div>
          <VellaNudge text="I've drafted replies for everything. Review and send." small />
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={{ ...Bg, borderRadius: 999, fontSize: 13 }} onClick={syncInbox} disabled={syncing}>{syncing ? "Syncing..." : "Sync Inbox"}</button>
          <button style={{ ...B, borderRadius: 999 }} onClick={replyAll}>Reply to All</button>
        </div>
      </div>
      {/* Lead Intelligence */}
      <div style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.68) 100%)", backdropFilter: "blur(34px) saturate(180%)", WebkitBackdropFilter: "blur(34px) saturate(180%)", border: `1px solid rgba(255,255,255,0.45)`, borderRadius: 20, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", boxShadow: "0 1px 0 rgba(255,255,255,0.70) inset, 0 8px 24px -10px rgba(14,165,233,0.18)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, flex: 1, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: TEXT }}>Lead Intelligence</span>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {[{ label: "Hot", color: "#EF4444" }, { label: "Warm", color: "#F59E0B" }, { label: "Cold", color: "#3B82F6" }].map(s => (
              <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: s.color }} />
                <span style={{ fontSize: 12, color: TEXT2 }}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={{ ...Bg, fontSize: 12, padding: "7px 14px", borderRadius: 999, borderColor: scoringLeads ? BLUE : "rgba(255,255,255,0.45)", color: scoringLeads ? BLUE : TEXT2 }} onClick={scoreLeads} disabled={scoringLeads}>
            {scoringLeads ? <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 12, height: 12, border: "2px solid " + BLUE, borderTopColor: "transparent", borderRadius: "50%", animation: "spin .7s linear infinite", display: "inline-block" }} />Scoring...</span> : <span style={{ display: "flex", alignItems: "center", gap: 6 }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20V10"/><path d="M18 20V4"/><path d="M6 20v-4"/></svg>Score Leads</span>}
          </button>
          <button style={{ ...Bg, fontSize: 12, padding: "7px 14px", borderRadius: 999, borderColor: segmenting ? BLUE : "rgba(255,255,255,0.45)", color: segmenting ? BLUE : TEXT2 }} onClick={segmentAudience} disabled={segmenting}>
            {segmenting ? <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 12, height: 12, border: "2px solid " + BLUE, borderTopColor: "transparent", borderRadius: "50%", animation: "spin .7s linear infinite", display: "inline-block" }} />Segmenting...</span> : <span style={{ display: "flex", alignItems: "center", gap: 6 }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="M8.59 13.51l6.83 3.98"/><path d="M15.41 6.51l-6.82 3.98"/></svg>Segment Audience</span>}
          </button>
        </div>
      </div>

      {/* Segment Results */}
      {segmentData && (
        <div style={{ ...card3d, marginBottom: 16, borderLeft: "3px solid " + BLUE }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="M8.59 13.51l6.83 3.98"/><path d="M15.41 6.51l-6.82 3.98"/></svg>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: TEXT }}>Audience Segments</h3>
            </div>
            <button onClick={() => setSegmentData(null)} style={{ background: "none", border: "none", cursor: "pointer", color: TEXT3, fontSize: 18, padding: 4 }}>x</button>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {segmentData.map((seg, i) => {
              const priorityColor = seg.priority === "high" ? "#EF4444" : seg.priority === "medium" ? "#F59E0B" : BLUE;
              return (
                <div key={i} style={{ flex: "1 1 220px", padding: 14, background: "rgba(255,255,255,0.35)", backdropFilter: "blur(18px) saturate(180%)", WebkitBackdropFilter: "blur(18px) saturate(180%)", borderRadius: 18, border: "1px solid rgba(255,255,255,0.45)", borderTop: "3px solid " + priorityColor }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: TEXT }}>{seg.segment}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: priorityColor, background: priorityColor + "15", padding: "2px 8px", borderRadius: 6 }}>{seg.count || seg.authors?.length || 0}</span>
                  </div>
                  {seg.authors && <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8 }}>{seg.authors.map((a, ai) => <span key={ai} style={{ fontSize: 11, fontWeight: 600, color: BLUE, background: BLUE_LIGHT, padding: "2px 8px", borderRadius: 10 }}>@{a}</span>)}</div>}
                  {seg.action && <p style={{ fontSize: 12, color: TEXT2, lineHeight: 1.4 }}>{seg.action}</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {msgs.map(m => {
        const leadColor = m.lead === "hot" ? "#EF4444" : m.lead === "warm" ? "#F59E0B" : m.lead === "cold" ? "#3B82F6" : null;
        const scoreInfo = leadScores[m.id];
        return (
        <div key={m.id} style={{ ...card3d, boxShadow: replies[m.id] ? SHADOW_LG : SHADOW, borderLeft: scoreInfo ? ("3px solid " + (leadColor || BORDER)) : undefined }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <PlatformBadge platform={m.platform} size={20} /><span style={{ fontSize: 13, fontWeight: 600, color: TEXT }}>{m.platform}</span>
            <span style={tg(m.type === "dm" ? BLUE_LIGHT : `${TEXT3}22`, m.type === "dm" ? BLUE : TEXT2)}>{m.type === "dm" ? "DM" : "Comment"}</span>
            {leadColor && (
              <div style={{ display: "flex", alignItems: "center", gap: 4, background: leadColor + "15", borderRadius: 20, padding: "2px 8px" }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: leadColor, animation: m.lead === "hot" ? "pulse 2s infinite" : "none" }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: leadColor, textTransform: "capitalize" }}>{m.lead}</span>
              </div>
            )}
            {scoreInfo && scoreInfo.intent && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 4 }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: TEXT3, background: BG, padding: "2px 6px", borderRadius: 6 }}>Intent {scoreInfo.intent}/10</span>
                <span style={{ fontSize: 10, fontWeight: 600, color: TEXT3, background: BG, padding: "2px 6px", borderRadius: 6 }}>Urgency {scoreInfo.urgency}/10</span>
              </div>
            )}
            <span style={{ fontSize: 12, color: TEXT3, marginLeft: "auto" }}>{m.time}</span>
          </div>
          <p style={{ fontSize: 13, fontWeight: 600, color: BLUE, marginBottom: 4 }}>@{m.author}</p>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: TEXT }}>"{m.text}"</p>
          {scoreInfo && scoreInfo.reason && (
            <div style={{ marginTop: 8, padding: "6px 12px", background: (leadColor || BLUE) + "08", borderRadius: 8, borderLeft: "2px solid " + (leadColor || BLUE) }}>
              <p style={{ fontSize: 12, color: TEXT2, fontStyle: "italic" }}>{scoreInfo.reason}</p>
            </div>
          )}
          {loading[m.id] && <Loader text="Crafting reply..." />}
          {replies[m.id] && <div style={{ background: BLUE_LIGHT, borderRadius: 14, padding: 16, marginTop: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}><VellaAvatar size={20} /><span style={{ fontSize: 11, fontWeight: 700, color: BLUE, letterSpacing: 0.5 }}>VELLA'S REPLY</span></div>
              <div style={{ display: "flex", gap: 6 }}><CopyBtn text={replies[m.id]} />
                {sent[m.id] === "sent" ? (
                  <span style={{ fontSize: 12, fontWeight: 700, color: SUCCESS, background: `${SUCCESS}15`, padding: "5px 12px", borderRadius: 8 }}>Sent!</span>
                ) : sent[m.id] === "sending" ? (
                  <span style={{ fontSize: 12, color: BLUE, padding: "5px 12px" }}>Sending...</span>
                ) : (
                  <button onClick={() => sendReply(m)} style={{ ...Bsm, background: "linear-gradient(135deg, #38BDF8, #0EA5E9 60%, #0284C7)", color: "#fff", border: "1px solid rgba(255,255,255,0.25)", padding: "8px 18px", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontWeight: 600 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>Send</button>
                )}
              </div>
            </div>
            <p style={{ fontSize: 14, lineHeight: 1.65, color: TEXT }}>{replies[m.id]}</p>
          </div>}
          {!replies[m.id] && !loading[m.id] && <button style={{ ...Bg, marginTop: 12 }} onClick={() => reply(m)}>Generate Reply</button>}
        </div>
        );
      })}
    </div>
  );
}

// ===========================================
// ADS — analytics-only dashboard. Shows performance + per-campaign data.
// Ad CREATION happens through Vella's chat, not here.
// ===========================================
function AdCampaignsView({ sys, biz, onSendToChat }) {
  const { autopilot: autopilotActive } = React.useContext(AutopilotContext);
  const [range, setRange] = useState("30D");

  // Whether a real ad platform is linked. Until one is, the figures below
  // are illustrative placeholders.
  const adAccountConnected = !!(biz?.googleAdsId || biz?.facebookUrl || biz?.metaAdAccount || biz?.adsConnected);

  const secLabel = { fontSize: 11, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: TEXT3, margin: "0 0 12px" };
  const fmtNum = (n) => n >= 1e6 ? `${(n / 1e6).toFixed(2)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(n >= 1e4 ? 0 : 1)}k` : `${n}`;

  // ── Mock performance figures (swap for live ad-account API later) ────────
  const metrics = [
    { label: "Total Ad Spend", value: "$4,820", change: "+8.4%", up: true, gradient: STAT_GRADIENTS.blue, chartColor: "#2563EB", spark: [120, 140, 132, 158, 150, 176, 172], icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg> },
    { label: "Revenue · 4.2x ROAS", value: "$20,244", change: "+14.2%", up: true, gradient: STAT_GRADIENTS.green, chartColor: "#10B981", spark: [400, 460, 500, 540, 600, 700, 760], icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg> },
    { label: "Impressions", value: "1.24M", change: "+5.1%", up: true, gradient: STAT_GRADIENTS.teal, chartColor: "#0D9488", spark: [180, 190, 200, 210, 205, 230, 240], icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg> },
    { label: "Clicks", value: "38,420", change: "+9.7%", up: true, gradient: STAT_GRADIENTS.purple, chartColor: "#9333EA", spark: [60, 70, 66, 80, 78, 92, 98], icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 9l5 12 1.8-5.2L21 14 9 9z"/><path d="M7.2 2.2L8 5M5 7.2L2.2 8M9.5 4.5L8 6M4.5 9.5L6 8"/></svg> },
    { label: "CTR", value: "3.1%", change: "+0.3pt", up: true, gradient: STAT_GRADIENTS.orange, chartColor: "#EA580C", spark: [2.6, 2.7, 2.9, 2.8, 3.0, 3.1, 3.1], icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg> },
    { label: "Conversions", value: "1,204", change: "+11.8%", up: true, gradient: STAT_GRADIENTS.pink, chartColor: "#DB2777", spark: [22, 26, 28, 30, 34, 40, 44], icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg> },
  ];

  const campaigns = [
    { name: "Summer Sale – Conversions", platform: "Facebook Ads", platformName: "Meta", status: "Active", budget: 50, spend: 1240, impr: 412000, clicks: 12800, ctr: 3.1, cpa: 12.4, roas: 4.6 },
    { name: "Search – Brand Terms", platform: "Google Ads", platformName: "Google", status: "Active", budget: 40, spend: 980, impr: 286000, clicks: 9100, ctr: 3.2, cpa: 9.8, roas: 5.1 },
    { name: "Retargeting – Cart Abandon", platform: "Facebook Ads", platformName: "Meta", status: "Active", budget: 30, spend: 720, impr: 198000, clicks: 7300, ctr: 3.7, cpa: 8.1, roas: 6.2 },
    { name: "TikTok – Awareness", platform: "TikTok Ads", platformName: "TikTok", status: "Paused", budget: 25, spend: 540, impr: 220000, clicks: 5400, ctr: 2.5, cpa: 18.2, roas: 2.1 },
    { name: "Google – Shopping", platform: "Google Ads", platformName: "Google", status: "Active", budget: 60, spend: 1340, impr: 124000, clicks: 3820, ctr: 3.1, cpa: 14.0, roas: 3.8 },
  ];

  // ── Spend vs revenue chart (deterministic mock series) ───────────────────
  const N = range === "7D" ? 7 : range === "90D" ? 90 : 30;
  const series = Array.from({ length: N }, (_, i) => {
    const spend = 110 + 55 * Math.sin(i * 0.6) + 35 * Math.sin(i * 0.27) + (i / N) * 70;
    const revenue = spend * (3.6 + 0.7 * Math.sin(i * 0.45 + 1));
    return { spend: Math.max(40, Math.round(spend)), revenue: Math.max(120, Math.round(revenue)) };
  });
  const rangeLabel = (i) => {
    if (N <= 7) return ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][i] || `D${i + 1}`;
    return `${N - i}d`;
  };
  const chartData = series.map((d, i) => ({ label: rangeLabel(i), spend: d.spend, revenue: d.revenue }));

  return (
    <div>
      {autopilotActive && (
        <div style={{ background: "linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)", border: "1px solid #6EE7B7", borderRadius: 12, padding: "14px 18px", marginBottom: 20, display: "flex", alignItems: "center", gap: 12 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/><path d="M9 12l2 2 4-4"/></svg>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#065F46" }}>Autopilot is ON</div>
            <div style={{ fontSize: 12, color: "#047857" }}>Vella is automatically managing your campaigns.</div>
          </div>
        </div>
      )}

      <h2 style={{ fontSize: 24, fontWeight: 700, color: TEXT, marginBottom: 4, letterSpacing: "-0.022em" }}>Ads</h2>
      <VellaNudge text="Here's how your ads are performing. Ask me in chat to launch or adjust a campaign." small />

      {/* Connect ad account CTA */}
      {!adAccountConnected && (
        <div style={{ ...card3d, marginTop: 14, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: BLUE_LIGHT, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: TEXT }}>Connect your ad account</div>
              <div style={{ fontSize: 13, color: TEXT2, lineHeight: 1.5 }}>Link Google, Meta, or TikTok Ads to see live spend, revenue, and ROAS.</div>
            </div>
          </div>
          <button onClick={() => window.dispatchEvent(new CustomEvent("vella:navigate", { detail: "settings" }))} style={{ ...B, borderRadius: 999, whiteSpace: "nowrap" }}>Connect ad account</button>
        </div>
      )}

      {/* Performance metrics */}
      <p style={{ ...secLabel, marginTop: 24 }}>
        Performance{!adAccountConnected && <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500, color: TEXT3 }}> &nbsp;· connect your ad account for real data</span>}
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: 24 }}>
        {metrics.map((m, i) => (
          <StatCard key={i} gradient={m.gradient} chartColor={m.chartColor} icon={m.icon} label={m.label} value={m.value} change={m.change} changePositive={m.up} chartData={m.spark} />
        ))}
      </div>

      {/* Spend vs revenue chart */}
      <div style={card3d}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: TEXT, letterSpacing: "-0.022em", margin: 0 }}>Spend vs Revenue</h3>
          </div>
          <div style={{ display: "flex", gap: 4, background: "rgba(255,255,255,0.55)", borderRadius: 999, padding: 3 }}>
            {["7D", "30D", "90D"].map((r) => (
              <button key={r} onClick={() => setRange(r)} style={{ padding: "5px 14px", borderRadius: 999, border: "none", fontSize: 12, fontWeight: range === r ? 700 : 500, background: range === r ? BLUE : "transparent", color: range === r ? "#fff" : TEXT2, cursor: "pointer", fontFamily: FONT }}>{r}</button>
            ))}
          </div>
        </div>
        <VellaAreaChart
          data={chartData}
          xKey="label"
          height={300}
          money
          series={[
            { key: "spend", color: "#275c73", name: "Spend" },
            { key: "revenue", color: "#3f8a63", name: "Revenue" },
          ]}
        />
      </div>

      {/* Per-campaign breakdown */}
      <p style={{ ...secLabel, marginTop: 24 }}>Campaigns</p>
      <div style={{ ...card3d, padding: 0, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT, minWidth: 880 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${BORDER}` }}>
                {["Campaign", "Platform", "Status", "Daily Budget", "Spend", "Impressions", "Clicks", "CTR", "CPA", "ROAS"].map((h, i) => (
                  <th key={h} style={{ textAlign: i === 0 ? "left" : "right", padding: "13px 16px", fontSize: 11, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: TEXT3, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c, i) => (
                <tr key={i} style={{ borderBottom: i < campaigns.length - 1 ? `1px solid ${BORDER}88` : "none" }}>
                  <td style={{ padding: "13px 16px", fontSize: 13, fontWeight: 600, color: TEXT, whiteSpace: "nowrap" }}>{c.name}</td>
                  <td style={{ padding: "13px 16px" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 7, justifyContent: "flex-end", width: "100%", fontSize: 13, color: TEXT2 }}>
                      <PlatformBadge platform={c.platform} size={16} />{c.platformName}
                    </span>
                  </td>
                  <td style={{ padding: "13px 16px", textAlign: "right" }}>
                    <span style={tg(c.status === "Active" ? `${SUCCESS}22` : `${WARNING}22`, c.status === "Active" ? SUCCESS : WARNING)}>{c.status}</span>
                  </td>
                  <td style={{ padding: "13px 16px", textAlign: "right", fontSize: 13, color: TEXT2 }}>${c.budget}/day</td>
                  <td style={{ padding: "13px 16px", textAlign: "right", fontSize: 13, fontWeight: 600, color: TEXT }}>${c.spend.toLocaleString()}</td>
                  <td style={{ padding: "13px 16px", textAlign: "right", fontSize: 13, color: TEXT2 }}>{fmtNum(c.impr)}</td>
                  <td style={{ padding: "13px 16px", textAlign: "right", fontSize: 13, color: TEXT2 }}>{c.clicks.toLocaleString()}</td>
                  <td style={{ padding: "13px 16px", textAlign: "right", fontSize: 13, color: TEXT2 }}>{c.ctr}%</td>
                  <td style={{ padding: "13px 16px", textAlign: "right", fontSize: 13, color: TEXT2 }}>${c.cpa}</td>
                  <td style={{ padding: "13px 16px", textAlign: "right", fontSize: 13, fontWeight: 700, color: c.roas >= 3 ? SUCCESS : c.roas >= 2 ? WARNING : DANGER }}>{c.roas}x</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer note — creation lives in chat */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 20, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, color: TEXT3 }}>To create new ads, ask Vella in chat.</span>
        {onSendToChat && (
          <button onClick={() => onSendToChat("I want to create a new ad campaign. Help me build one — headlines, copy, targeting, and budget.")} style={{ ...Bg, borderRadius: 999, padding: "8px 16px", fontSize: 13 }}>Ask Vella to create an ad</button>
        )}
      </div>
    </div>
  );
}

// ===========================================
// CHART HELPERS (pure SVG, no dependencies)
// ===========================================
export function MiniBarChart({ data, color = BLUE, height = 40 }) {
  const max = Math.max(...data);
  const w = 100 / data.length;
  return (
    <svg width="100%" height={height} viewBox={`0 0 100 ${height}`} preserveAspectRatio="none">
      {data.map((v, i) => {
        const barH = (v / max) * (height - 4);
        return <rect key={i} x={i * w + 1} y={height - barH} width={w - 2} height={barH} rx="1.5" fill={color} opacity={i === data.length - 1 ? 1 : 0.55} />;
      })}
    </svg>
  );
}

export function MiniLineChart({ data, color = BLUE, height = 40 }) {
  const max = Math.max(...data); const min = Math.min(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = height - 4 - ((v - min) / range) * (height - 8);
    return `${x},${y}`;
  }).join(" ");
  const area = `M0,${height} L${data.map((v, i) => `${(i / (data.length - 1)) * 100},${height - 4 - ((v - min) / range) * (height - 8)}`).join(" L")} L100,${height} Z`;
  return (
    <svg width="100%" height={height} viewBox={`0 0 100 ${height}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id={`lg-${color.replace("#","")}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.18"/>
          <stop offset="100%" stopColor={color} stopOpacity="0"/>
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#lg-${color.replace("#","")})`}/>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function AreaChart({ data, metric, labels, color = "#2563EB", height = 200 }) {
  const vals = data.map(d => d[metric]);
  const max = Math.max(...vals); const min = 0;
  const range = max - min || 1;
  const W = 700; const H = height; const PAD_L = 52; const PAD_B = 28; const PAD_T = 16;
  const cW = W - PAD_L - 16; const cH = H - PAD_B - PAD_T;
  const toX = i => PAD_L + (i / (vals.length - 1)) * cW;
  const toY = v => PAD_T + cH - ((v - min) / range) * cH;
  const linePts = vals.map((v, i) => `${toX(i)},${toY(v)}`).join(" L ");
  const area = `M${toX(0)},${toY(0)} L${linePts} L${toX(vals.length-1)},${H - PAD_B} L${toX(0)},${H - PAD_B} Z`;
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(p => Math.round(min + p * range));
  const showLabels = labels.filter((_, i) => i % Math.ceil(labels.length / 7) === 0);
  const showIdxs = labels.map((_, i) => i).filter(i => i % Math.ceil(labels.length / 7) === 0);

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" style={{ overflow: "visible" }}>
      <defs>
        <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.18"/>
          <stop offset="100%" stopColor={color} stopOpacity="0.01"/>
        </linearGradient>
      </defs>
      {yTicks.map((v, i) => {
        const y = toY(v);
        const label = v >= 1000 ? `$${(v/1000).toFixed(v >= 10000 ? 0 : 1)}k` : `$${v}`;
        return (
          <g key={i}>
            <line x1={PAD_L} y1={y} x2={W - 16} y2={y} stroke={BORDER} strokeWidth="1" strokeDasharray={i === 0 ? "none" : "3,3"}/>
            <text x={PAD_L - 6} y={y + 4} textAnchor="end" fontSize="10" fill={TEXT3} fontFamily="-apple-system, 'SF Pro Display', Inter, sans-serif">{label}</text>
          </g>
        );
      })}
      <path d={area} fill="url(#areaGrad)"/>
      <polyline points={vals.map((v, i) => `${toX(i)},${toY(v)}`).join(" ")} fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
      {showIdxs.map((idx, i) => (
        <text key={i} x={toX(idx)} y={H - PAD_B + 16} textAnchor="middle" fontSize="10" fill={TEXT3} fontFamily="-apple-system, 'SF Pro Display', Inter, sans-serif">{labels[idx]}</text>
      ))}
    </svg>
  );
}

// ===========================================
// PERFORMANCE (upgraded with charts)
// ===========================================
function PerformanceView() {
  const [activeMetric, setActiveMetric] = useState("spend");
  const [activeRange, setActiveRange] = useState("30d");
  const [campaigns, setCampaigns] = useState([]);
  const [liveOverview, setLiveOverview] = useState(null);

  useEffect(() => {
    API.getCampaignAnalytics().then(d => {
      if (d.campaigns && d.campaigns.length > 0) {
        setCampaigns(d.campaigns.map(c => ({
          name: c.name || "Campaign",
          platform: c.platform || "Facebook",
          status: c.status || "Active",
          budget: c.budget ? `$${c.budget}/day` : "--",
          spend: c.spend != null ? `$${Number(c.spend).toLocaleString()}` : "--",
          clicks: c.clicks != null ? Number(c.clicks).toLocaleString() : "--",
          ctr: c.ctr != null ? `${c.ctr}%` : "--",
          conv: c.conversions ?? 0,
          roas: c.roas != null ? `${c.roas}x` : "--",
        })));
      }
    }).catch(() => {});
    API.getAnalyticsOverview(activeRange).then(d => {
      const ov = d.overview || d;
      if (ov && typeof ov === "object") setLiveOverview(ov);
    }).catch(() => {});
  }, [activeRange]);

  const fmtVal = (n) => { if (!n && n !== 0) return "0"; if (n >= 1000000) return (n / 1000000).toFixed(2).replace(/\.?0+$/, "") + "M"; if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.?0+$/, "") + "K"; return n.toLocaleString(); };
  const lo = liveOverview;

  // Chart data from live overview or empty
  const days30 = Array.from({ length: 30 }, (_, i) => ({
    label: new Date(Date.now() - (29 - i) * 86400000).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    spend: 0, clicks: 0, conversions: 0, roas: 0,
  }));

  const metricConfigs = {
    spend: { label: "Total Spend", value: lo ? `$${fmtVal(lo.totalSpend ?? lo.spend ?? 0)}` : "$0", change: lo ? "Live" : "--", up: true, color: BLUE, chartKey: "spend" },
    clicks: { label: "Total Clicks", value: lo ? fmtVal(lo.totalClicks ?? lo.clicks ?? 0) : "0", change: lo ? "Live" : "--", up: true, color: "#8B5CF6", chartKey: "clicks" },
    conversions: { label: "Conversions", value: lo ? fmtVal(lo.totalConversions ?? lo.conversions ?? 0) : "0", change: lo ? "Live" : "--", up: true, color: SUCCESS, chartKey: "conversions" },
    roas: { label: "Avg ROAS", value: lo && lo.roas ? `${lo.roas}x` : "0x", change: lo ? "Live" : "--", up: true, color: "#F59E0B", chartKey: "roas" },
  };

  const active = metricConfigs[activeMetric];

  // Mini chart data for metric cards
  const miniSpend = [1420, 1680, 1590, 1720, 1850, 1780, 1910, 2050, 1960, 2200, 2180, 2420];
  const miniClicks = [1800, 2100, 1950, 2300, 2150, 2400, 2600, 2500, 2800, 3100, 2900, 3400];
  const miniRoas = [3.2, 3.8, 3.5, 4.1, 3.9, 4.3, 4.0, 4.5, 4.2, 4.8, 4.6, 5.1];
  const miniOrg = [310000, 340000, 328000, 355000, 370000, 382000, 395000, 408000, 415000, 424288];

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: TEXT, marginBottom: 2, letterSpacing: "-0.022em" }}>Analytics</h2>
          <p style={{ color: TEXT2, fontSize: 14 }}>Track performance across all campaigns and platforms.</p>
        </div>
        <div style={{ display: "flex", gap: 4, background: "rgba(255,255,255,0.35)", backdropFilter: "blur(18px) saturate(180%)", WebkitBackdropFilter: "blur(18px) saturate(180%)", border: "1px solid rgba(255,255,255,0.45)", borderRadius: 999, padding: 4 }}>
          {["7d", "30d", "3m", "6m", "1y"].map(r => (
            <button key={r} onClick={() => setActiveRange(r)} style={{ padding: "6px 14px", borderRadius: 999, border: "none", fontSize: 12, fontWeight: activeRange === r ? 700 : 400, background: activeRange === r ? "rgba(255,255,255,0.75)" : "transparent", color: activeRange === r ? TEXT : TEXT3, cursor: "pointer", boxShadow: activeRange === r ? "0 1px 0 rgba(255,255,255,0.85) inset, 0 4px 10px -4px rgba(11,107,211,0.20)" : "none", fontFamily: FONT }}>{r}</button>
          ))}
        </div>
      </div>

      {/* Metric cards with mini charts - like Image 2 */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14, marginBottom: 20 }}>
        {[
          { label: "Daily Revenue", value: lo ? `$${fmtVal(lo.totalRevenue ?? lo.revenue ?? lo.totalSpend ?? 0)}` : "$0", change: lo ? "Live" : "--", up: true, data: miniSpend, gradient: STAT_GRADIENTS.green, chartColor: "#10B981", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg> },
          { label: "Organic Traffic", value: lo ? fmtVal(lo.organicTraffic ?? lo.totalImpressions ?? 0) : "0", change: lo ? "Live" : "--", up: true, data: miniOrg, gradient: STAT_GRADIENTS.blue, chartColor: "#2563EB", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg> },
          { label: "ROAS", value: lo && lo.roas ? `${lo.roas}x` : "0x", change: lo ? "Live" : "--", up: true, data: miniRoas.map(v => Math.round(v * 100)), gradient: STAT_GRADIENTS.purple, chartColor: "#9333EA", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg> },
          { label: "Conversions", value: lo ? fmtVal(lo.totalConversions ?? lo.conversions ?? 0) : "0", change: lo ? "Live" : "--", up: true, data: [0,0,0,0,0,0,0,0,0,0,0,0], gradient: STAT_GRADIENTS.orange, chartColor: "#EA580C", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg> },
        ].map((card, i) => (
          <StatCard
            key={i}
            gradient={card.gradient}
            chartColor={card.chartColor}
            icon={card.icon}
            label={card.label}
            value={card.value}
            change={card.change}
            changePositive={card.up}
            chartData={card.data}
          />
        ))}
      </div>

      {/* Large Area Chart - like Image 1 */}
      <div style={{ ...card3d, marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
          <div>
            <p style={{ fontSize: 12, fontWeight: 600, color: TEXT3, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>{active.label}</p>
            <p style={{ fontSize: 32, fontWeight: 700, color: TEXT, fontFamily: "inherit", fontFeatureSettings: "'tnum','cv11'", letterSpacing: "-0.025em" }}>{active.value}</p>
          </div>
          {/* Metric selector tabs */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {Object.entries(metricConfigs).map(([key, cfg]) => (
              <button key={key} onClick={() => setActiveMetric(key)}
                style={{ padding: "5px 14px", borderRadius: 999, border: `1.5px solid ${activeMetric === key ? cfg.color : "rgba(255,255,255,0.45)"}`, fontSize: 12, fontWeight: activeMetric === key ? 700 : 400, background: activeMetric === key ? cfg.color + "15" : "rgba(255,255,255,0.35)", color: activeMetric === key ? cfg.color : TEXT3, cursor: "pointer", fontFamily: FONT, display: "flex", alignItems: "center", gap: 5 }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: cfg.color }} />
                {cfg.label}
              </button>
            ))}
          </div>
        </div>
        <VellaAreaChart
          data={days30.map(d => ({ label: d.label, value: d[active.chartKey] }))}
          xKey="label"
          height={260}
          series={[{ key: "value", color: "#275c73", name: active.label }]}
        />
      </div>

      {/* Campaign Table */}
      <div style={card3d}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 14, color: TEXT, letterSpacing: "-0.022em" }}>All Campaigns</h3>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${BORDER}` }}>
                {["Campaign", "Platform", "Status", "Budget", "Spend", "Clicks", "CTR", "Conv.", "ROAS"].map(h => (
                  <th key={h} style={{ padding: "10px", textAlign: "left", color: TEXT3, fontWeight: 600, fontSize: 10, textTransform: "uppercase", letterSpacing: 1, fontFamily: FONT }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${BORDER}`, transition: "background 0.15s", cursor: "default" }}
                  onMouseEnter={e => e.currentTarget.style.background = BG}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <td style={{ padding: "14px 10px", fontWeight: 600, color: TEXT }}>{c.name}</td>
                  <td style={{ padding: "14px 10px" }}><div style={{ display: "flex", alignItems: "center", gap: 6 }}><PlatformBadge platform={c.platform} size={16} /><span style={{ color: TEXT2 }}>{c.platform}</span></div></td>
                  <td style={{ padding: "14px 10px" }}><span style={tg(c.status === "Active" ? `${SUCCESS}22` : `${WARNING}22`, c.status === "Active" ? SUCCESS : WARNING)}>{c.status}</span></td>
                  <td style={{ padding: "14px 10px", color: TEXT2 }}>{c.budget}</td>
                  <td style={{ padding: "14px 10px", color: TEXT2 }}>{c.spend}</td>
                  <td style={{ padding: "14px 10px", color: TEXT2 }}>{c.clicks}</td>
                  <td style={{ padding: "14px 10px", color: BLUE, fontWeight: 600, fontFamily: "inherit", fontFeatureSettings: "'tnum','cv11'" }}>{c.ctr}</td>
                  <td style={{ padding: "14px 10px", color: TEXT2 }}>{c.conv}</td>
                  <td style={{ padding: "14px 10px", color: SUCCESS, fontWeight: 700, fontFamily: "inherit", fontFeatureSettings: "'tnum','cv11'" }}>{c.roas}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Competitive Intelligence */}
      <div style={{ marginTop: 6 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: TEXT, marginBottom: 12, letterSpacing: "-0.022em" }}>Competitive Intelligence</h3>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
          {/* Monitor Competitors */}
          <div style={{ ...card3d, flex: 1, minWidth: 220, marginBottom: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4ECDC4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <span style={{ fontSize: 14, fontWeight: 700, color: TEXT }}>Monitor Competitors</span>
            </div>
            {[
              { name: "Competitor A", status: "Active -- 3 new ads", dot: "#EF4444" },
              { name: "Competitor B", status: "Budget increased +20%", dot: "#F59E0B" },
              { name: "Competitor C", status: "Paused campaigns", dot: "#10B981" },
            ].map((c, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: i < 2 ? `1px solid ${BORDER}` : "none" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: c.dot, flexShrink: 0 }} />
                <div>
                  <p style={{ fontSize: 13, fontWeight: 600, color: TEXT, marginBottom: 1 }}>{c.name}</p>
                  <p style={{ fontSize: 11, color: TEXT2 }}>{c.status}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Industry Benchmark */}
          <div style={{ ...card3d, flex: 1, minWidth: 220, marginBottom: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
              <span style={{ fontSize: 14, fontWeight: 700, color: TEXT }}>Industry Benchmark</span>
            </div>
            <div style={{ textAlign: "center", marginBottom: 14 }}>
              <p style={{ fontSize: 36, fontWeight: 700, color: BLUE, fontFamily: "inherit", fontFeatureSettings: "'tnum','cv11'", letterSpacing: "-0.025em" }}>22nd</p>
              <p style={{ fontSize: 12, color: TEXT2 }}>percentile in your industry</p>
            </div>
            {[
              { label: "Spend Efficiency", pct: 78 },
              { label: "Creative Quality", pct: 65 },
              { label: "Audience Reach", pct: 82 },
            ].map((bar, i) => (
              <div key={i} style={{ marginBottom: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                  <span style={{ fontSize: 11, color: TEXT2 }}>{bar.label}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: BLUE }}>{bar.pct}%</span>
                </div>
                <div style={{ height: 5, background: BG, borderRadius: 3 }}>
                  <div style={{ height: "100%", width: bar.pct + "%", background: BLUE, borderRadius: 3 }} />
                </div>
              </div>
            ))}
          </div>

          {/* Audience Segments */}
          <div style={{ ...card3d, flex: 1, minWidth: 220, marginBottom: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
              <span style={{ fontSize: 14, fontWeight: 700, color: TEXT }}>Audience Segments</span>
            </div>
            {[
              { label: "25-34 Female", pct: 34, color: "#E1306C" },
              { label: "35-44 Female", pct: 28, color: "#8B5CF6" },
              { label: "25-34 Male", pct: 22, color: "#3B82F6" },
              { label: "Other", pct: 16, color: TEXT3 },
            ].map((seg, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 9 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: seg.color, flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: TEXT2, flex: 1 }}>{seg.label}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: TEXT }}>{seg.pct}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ===========================================
// ANALYSIS + RECS (preserved, dark theme)
// ===========================================

// Cheap classifier for the message the user just sent. Returns a taskType for
// the unified morph orb's generating card (website|image|campaign|ads|video),
// or null for plain chat (no card — the docked orb just spins). The card's
// verb set is keyed off this value.
function classifyRequest(message) {
  const t = String(message || "").toLowerCase();
  if (/(website|web site|landing page|build (me )?a site|design (a |my )?site)/.test(t)) return "website";
  if (/(video|reel|\bclip\b|storyboard|animation)/.test(t)) return "video";
  if (/(\bads?\b|advert|run ads|ad creative|google ads|meta ads)/.test(t)) return "ads";
  if (/(campaign|marketing plan|go[- ]to[- ]market|launch plan)/.test(t)) return "campaign";
  if (/(image|photo|picture|creative|generate.*image|post (image|graphic)|social (post|graphic)|logo|graphic)/.test(t)) return "image";
  return null;
}


// Quick-start prompts surfaced above the InlineChatBar input. Clicking
// one *pre-fills* the input so the user can review or edit before
// sending — we don't auto-send because some prompts (e.g. ads, websites)
// benefit from a one-word tweak before kicking off a long generation.

// Idle-home quick-start chip — chat-bar-matching pill with a muted lucide
// icon. Docked directly above the chat bar input (moved here from
// TalkToVella so the chips live in the same container as the composer).
function IdleChip({ Icon, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="vella-idle-chip"
      data-no-vella-ring="true"
      style={{
        flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        height: 36, padding: '0 14px', cursor: 'pointer', fontFamily: FONT, fontSize: 13, fontWeight: 400,
        borderRadius: 9999, color: 'var(--slate-600)',
        // Item 5 — the chat-bar glass recipe becomes the chip surface.
        background: 'rgba(255,255,255,0.55)',
        backdropFilter: 'blur(24px) saturate(1.8)',
        WebkitBackdropFilter: 'blur(24px) saturate(1.8)',
        border: '1px solid rgba(120,200,255,0.18)',
        boxShadow: 'var(--shadow-sm)',
        // Item 15 — chip physics (hover lift / press) on the spring easing.
        transition: 'transform 180ms var(--ds-ease-spring), box-shadow 180ms var(--ds-ease-spring), background 160ms ease',
      }}
    >
      {Icon && <Icon size={14} strokeWidth={1.5} color="#9aa7ae" />}
      {children}
    </button>
  );
}

const IDLE_CHIPS = [
  { Icon: Megaphone,       label: 'Run Ads',        cue: 'Help me launch a new ad campaign — walk me through audience, budget, and creative.' },
  { Icon: PenLine,         label: 'Create Post',    cue: 'Create a social media post I can publish today — caption, hook, hashtags.' },
  { Icon: Globe,           label: 'Design Website', cue: 'Design a custom website for my business — show me the homepage first.' },
  { Icon: LucideImageIcon, label: 'Generate Image', cue: 'Generate a marketing image for my brand.' },
  { Icon: FileText,        label: 'Write Copy',     cue: 'Write marketing copy for me.' },
  { Icon: Target,          label: 'Plan Campaign',  cue: 'Help me plan a marketing campaign.' },
];

function InlineChatBar({ sys, embedded = false, seed, onSeedConsumed }) {
  const [gVal, setGVal] = useState("");
  const [gTyping, setGTyping] = useState(false);
  const [gReply, setGReply] = useState(null);
  // Imperative handle on PromptInputBox so capability cards can seed the
  // input without sending — `setValue` writes to the textarea's internal
  // state directly (the component doesn't accept a `value` prop).
  const promptInputRef = useRef(null);
  // `genMode` is one of: null | "website" | "image". Set when a typing turn
  // begins so we can show the right loader above the chatbar; cleared when
  // the reply lands.
  const [genMode, setGenMode] = useState(null);
  const [voiceActive, setVoiceActive] = useState(false);
  const [voiceError, setVoiceError] = useState("");

  // Seed the composer from a routed suggestion (e.g. "Redeploy", a Dashboard
  // chip). This ONLY prefills the input — it never auto-sends and never
  // starts a voice session. Voice may begin exclusively from the mic gesture.
  useEffect(() => {
    if (!seed) return;
    setGVal(seed);
    try { promptInputRef.current?.setValue(seed); } catch (_) {}
    if (onSeedConsumed) onSeedConsumed();
  }, [seed, onSeedConsumed]);
  // Phase state is internal — kept for telemetry/debugging hooks. The
  // visible UI for voice is the End pill on the chatbar input itself
  // (PromptInputBox renders it when voiceActive=true).
  // eslint-disable-next-line no-unused-vars
  const [voicePhase, setVoicePhase] = useState("connecting");
  // eslint-disable-next-line no-unused-vars
  const [remoteAudioTrack, setRemoteAudioTrack] = useState(null);
  const histRef = useRef([]);
  // Abort handle for the in-flight generation so the user can stop it.
  const abortRef = useRef(null);

  // Voice path is LiveKit/WebRTC (same transport the phone bridge uses) —
  // we publish the user's mic into a `vella-call-{userId}-{ts}` room and
  // play whatever audio track the Vella voice agent (Claude + Deepgram +
  // ElevenLabs, deployed alongside the LiveKit Cloud dispatch) publishes
  // back. Continuous conversation + barge-in are owned by the agent; the
  // browser is just an A/V client.
  const lkClientRef         = useRef(null);
  const remoteAudioElRef    = useRef(null);
  const remoteAudioTrackRef = useRef(null);
  const voiceActiveRef      = useRef(false);
  const agentConnectedRef   = useRef(false);
  // Browser-side fallback handle, set when we drop to webkitSpeechRecognition
  // because LiveKit / the agent worker is unreachable. Kept here so stopVoice
  // can tear it down regardless of which path is active.
  const fallbackVoiceRef    = useRef(null);
  // Watchdog: if the agent worker doesn't join the room within N seconds,
  // tear the room down and surface a clear error rather than leaving the
  // user staring at "Connecting…" / "Listening…" forever (the most common
  // reason for the silent-Vella bug).
  const agentJoinTimerRef   = useRef(null);
  const AGENT_JOIN_TIMEOUT_MS = 12000;


  useEffect(() => { setVellaThinking(gTyping); return () => setVellaThinking(false); }, [gTyping]);

  // Quick-start chips visibility — shown on the empty home only, hidden as
  // soon as the active conversation has its first message (store-reactive so
  // voice turns and TalkToVella-side sends hide them too).
  const readHasMessages = () => ((getConversation(getActiveId() || "")?.messages || []).length > 0);
  const [hasMessages, setHasMessages] = useState(readHasMessages);
  useEffect(() => {
    const recheck = () => setHasMessages(readHasMessages());
    recheck();
    const unsub = subscribeConversations(recheck);
    return unsub;
  }, []);

  const clearAgentJoinTimer = () => {
    if (agentJoinTimerRef.current) {
      clearTimeout(agentJoinTimerRef.current);
      agentJoinTimerRef.current = null;
    }
  };

  const stopVoice = useCallback(async () => {
    voiceActiveRef.current = false;
    agentConnectedRef.current = false;
    clearAgentJoinTimer();
    setVoiceActive(false);
    setVoicePhase("connecting");
    setRemoteAudioTrack(null);
    const el = remoteAudioElRef.current;
    const track = remoteAudioTrackRef.current;
    if (el && track) { try { track.detach(el); } catch (_) {} }
    remoteAudioTrackRef.current = null;
    if (fallbackVoiceRef.current) {
      try { fallbackVoiceRef.current.stop(); } catch (_) {}
      fallbackVoiceRef.current = null;
    }
    try { await lkClientRef.current?.disconnect(); }
    catch (err) { console.warn("[InlineChatBar] livekit disconnect failed:", err); }
  }, []);

  const startVoice = useCallback(async () => {
    setVoiceError("");
    setVoicePhase("connecting");
    setRemoteAudioTrack(null);
    voiceActiveRef.current = true;
    agentConnectedRef.current = false;
    setVoiceActive(true);
    // Stop any ambient TTS playback (text-path replies) so we don't double-
    // speak when the LiveKit agent picks up.
    API.voiceStop();
    try {
      const meta = await fetchVoiceToken("voice_chatbar");
      if (!lkClientRef.current) {
        const client = createLiveKitClient();
        client.on("agentAudio", async (track) => {
          agentConnectedRef.current = true;
          clearAgentJoinTimer();
          remoteAudioTrackRef.current = track;
          setRemoteAudioTrack(track);
          const el = remoteAudioElRef.current;
          if (el) {
            try { track.attach(el); }
            catch (err) { console.warn("[InlineChatBar] track.attach failed:", err); }
            // Some browsers (Safari especially) don't auto-play remote audio
            // even after attach. Calling play() inside the user-gesture chain
            // started by the mic click usually unblocks it; if it still fails
            // we surface a helpful error instead of staying silent.
            try { await el.play(); }
            catch (err) {
              console.warn("[InlineChatBar] audio.play() failed:", err);
              if (voiceActiveRef.current) {
                setVoiceError("Tap the page to enable audio playback, then try again.");
                setVoicePhase("error");
              }
            }
          }
          if (voiceActiveRef.current) setVoicePhase("speaking");
        });
        client.on("agentConnected", () => {
          agentConnectedRef.current = true;
          clearAgentJoinTimer();
          if (voiceActiveRef.current) setVoicePhase("listening");
        });
        client.on("agentDisconnected", () => {
          if (voiceActiveRef.current) setVoicePhase("listening");
        });
        client.on("error", (err) => {
          console.warn("[InlineChatBar] livekit error:", err);
          if (voiceActiveRef.current) {
            setVoiceError(err?.message || "Voice connection error");
            setVoicePhase("error");
          }
        });
        client.on("connectionStateChanged", (state) => {
          if (state === "disconnected") {
            clearAgentJoinTimer();
            voiceActiveRef.current = false;
            setVoiceActive(false);
            setRemoteAudioTrack(null);
          }
        });
        lkClientRef.current = client;
      }
      await lkClientRef.current.connect({ token: meta.token, url: meta.url });
      await lkClientRef.current.publishMicrophone();
      // Arm the agent-arrival watchdog. agentConnected/agentAudio clears it.
      clearAgentJoinTimer();
      agentJoinTimerRef.current = setTimeout(() => {
        if (!voiceActiveRef.current) return;
        if (agentConnectedRef.current) return;
        setVoiceError("Vella's voice agent didn't pick up. Try again in a moment.");
        setVoicePhase("error");
        try { lkClientRef.current?.disconnect(); } catch (_) {}
      }, AGENT_JOIN_TIMEOUT_MS);
    } catch (err) {
      console.warn("[InlineChatBar] startVoice failed:", err);
      clearAgentJoinTimer();
      try { await lkClientRef.current?.disconnect(); } catch (_) {}

      // Best-effort browser fallback: when the LiveKit path is unreachable
      // (Railway down, missing LIVEKIT_* env vars, agent worker not joining)
      // we drop to Web Speech for a single listen→reply→speak round so the
      // mic button isn't completely dead. Vella's brain still answers via
      // askVella(), and TTS prefers /api/voice/speak with speechSynthesis
      // as last resort.
      if (isBrowserSpeechSupported()) {
        setVoiceError("Vella's cloud voice is offline — using your browser's voice for this turn.");
        setVoicePhase("listening");
        try {
          fallbackVoiceRef.current = startBrowserVoiceLoop({
            onAskVella: async (transcript) => {
              const oldHist = [...histRef.current];
              const h = [...oldHist, { role: "user", content: transcript }];
              const reply = await askVella(sys.main, transcript, h);
              histRef.current = [...h, { role: "assistant", content: reply }];
              setGReply(reply);
              return reply;
            },
            onSpeak: (text) => new Promise((resolve) => API.voiceSpeak(text, resolve)),
            onPhase: (phase) => {
              if (!voiceActiveRef.current) return;
              if (phase === "listening")   setVoicePhase("listening");
              else if (phase === "thinking") setVoicePhase("connecting");
              else if (phase === "speaking") setVoicePhase("speaking");
              else if (phase === "idle") {
                voiceActiveRef.current = false;
                setVoiceActive(false);
              } else if (phase === "error") {
                setVoiceError("Browser voice fallback failed. Try again or type your message.");
                setVoicePhase("error");
                voiceActiveRef.current = false;
                setVoiceActive(false);
              }
            },
          });
          return;
        } catch (fallbackErr) {
          console.warn("[InlineChatBar] browser fallback failed:", fallbackErr);
        }
      }

      setVoiceError(err?.message || "Could not start voice mode");
      setVoicePhase("error");
      voiceActiveRef.current = false;
      setVoiceActive(false);
    }
  }, [sys]);

  // Release mic + tear down the room on unmount so navigating away from a
  // chatbar surface doesn't leave the WebRTC track alive in the background.
  useEffect(() => () => {
    voiceActiveRef.current = false;
    agentConnectedRef.current = false;
    clearAgentJoinTimer();
    const el = remoteAudioElRef.current;
    const track = remoteAudioTrackRef.current;
    if (el && track) { try { track.detach(el); } catch (_) {} }
    remoteAudioTrackRef.current = null;
    if (fallbackVoiceRef.current) {
      try { fallbackVoiceRef.current.stop(); } catch (_) {}
      fallbackVoiceRef.current = null;
    }
    try { lkClientRef.current?.disconnect(); } catch (_) {}
    lkClientRef.current = null;
  }, []);

  const gSend = async (text, opts = {}) => {
    try { API.voiceStop(); } catch (_) {}
    if (voiceActiveRef.current) { try { await stopVoice(); } catch (_) {} }
    const m = text || gVal.trim();
    if (!m || gTyping) return;
    setGVal("");
    setGTyping(true);
    const taskType = classifyRequest(m);
    setGenMode(taskType);
    // Unified morph orb: artifact jobs (website/image/ads/campaign/video) morph
    // the orb INTO a generating card with the right verb set; plain chat just
    // spins the docked orb.
    if (taskType) {
      window.dispatchEvent(new CustomEvent("vella:orbCard", { detail: { taskType } }));
    } else {
      window.dispatchEvent(new Event("vella:orbThinking"));
    }

    let oldHist;
    let orbOk = true;
    if (embedded) {
      window.dispatchEvent(new CustomEvent("vella:chatbarMessage", { detail: { role: "user", content: m } }));
      const activeConvo = getConversation(getActiveId() || "");
      oldHist = (activeConvo?.messages || [])
        .slice(-8)
        .map((msg) => ({ role: msg.role === "ai" ? "assistant" : msg.role, content: msg.content || msg.text || "" }))
        .filter((msg) => msg.content);
    } else {
      oldHist = [...histRef.current];
    }
    const h = [...oldHist, { role: "user", content: m }];

    const controller = new AbortController();
    abortRef.current = controller;

    let r = "";
    let meta = null;
    try {
      r = await askVella(sys.main, m, h, {
        activeWorkflows: opts.activeWorkflows || [],
        signal: controller.signal,
        onMeta: (mm) => { meta = mm; },
      });
      if (!r) r = "Sorry — I got an empty response. Please try again.";
    } catch (err) {
      console.error("[InlineChatBar] gSend askVella threw:", err);
      r = "Sorry — something went wrong reaching Vella. Please try again.";
      orbOk = false;
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
    }

    // User hit Stop mid-flight — stopGeneration() already reset the UI and
    // posted a "Stopped" note, so don't append a duplicate / error bubble.
    if (controller.signal.aborted) return;
    // Living hero orb → success burst on a real reply (auto-returns to idle).
    if (orbOk) window.dispatchEvent(new Event("vella:orbSuccess"));

    histRef.current = [...h, { role: "assistant", content: r }];
    if (embedded) {
      // Forward the structured side-channel so TalkToVella can render result
      // previews (website / creative / analytics) — not just the text bubble.
      window.dispatchEvent(new CustomEvent("vella:chatbarMessage", {
        detail: {
          role: "assistant",
          content: r,
          widgets: meta?.widgets || null,
          structured_preview: meta?.structured_preview || null,
          // Cognitive-router path + responding model (after any fallback) so the
          // message metadata / cognitive monitor can surface which brain regions
          // fired and which model answered.
          routing: meta?.routing || null,
          model: meta?.model || null,
        },
      }));
    } else {
      setGReply(r);
    }
    setGTyping(false);
    setGenMode(null);
  };

  // Stop the current generation: abort the fetch, reset the typing UI, and
  // (in embedded mode) post a short note so the TalkToVella orb leaves its
  // generating state with whatever was produced so far.
  const stopGeneration = React.useCallback(() => {
    const controller = abortRef.current;
    if (controller) { try { controller.abort(); } catch (_) {} }
    abortRef.current = null;
    setGTyping(false);
    setGenMode(null);
    if (embedded) {
      window.dispatchEvent(new CustomEvent("vella:chatbarMessage", {
        detail: { role: "assistant", content: "Stopped. Tell me to pick up where I left off and I'll continue." },
      }));
    }
  }, [embedded]);

  // Let the generating-orb's "Stop generating" pill (in TalkToVella) abort too.
  React.useEffect(() => {
    const onStopEvt = () => stopGeneration();
    window.addEventListener("vella:stopGeneration", onStopEvt);
    return () => window.removeEventListener("vella:stopGeneration", onStopEvt);
  }, [stopGeneration]);

  return (
    <div
      style={{ display: "flex", justifyContent: "center", padding: embedded ? "0 24px 24px" : "0 24px 120px", background: "transparent" }}
      // Living hero orb → listening while the composer is focused, back to idle
      // on blur (focus/blur don't bubble, so capture at the composer container).
      onFocusCapture={() => window.dispatchEvent(new Event("vella:orbListening"))}
      onBlurCapture={() => window.dispatchEvent(new Event("vella:orbIdle"))}
    >
      <div style={{ width: "100%", maxWidth: 768 }}>
        <audio ref={remoteAudioElRef} autoPlay playsInline style={{ display: "none" }} />
        {/* The old in-bar generating boxes (AILoader website build + creative
            ImageGenerationReveal) are gone — the unified morph orb (LivingOrb)
            now IS the generating card, docked above this chat bar. It's driven
            by the vella:orbCard / vella:orbThinking events dispatched in gSend. */}
        {!embedded && gReply && <div style={{ marginBottom: 10, padding: "12px 16px", fontSize: 13, color: TEXT, maxHeight: 140, overflow: "auto", borderLeft: "3px solid #10B981", background: "rgba(255,255,255,0.7)", backdropFilter: "blur(18px) saturate(180%)", WebkitBackdropFilter: "blur(18px) saturate(180%)", border: "1px solid rgba(255,255,255,0.65)", borderRadius: 14, lineHeight: 1.5, boxShadow: "0 1px 0 rgba(255,255,255,0.85) inset, 0 4px 14px -6px rgba(16,185,129,0.15)" }}><span style={{ fontWeight: 700, color: "#0284C7" }}>Vella: </span>{gReply}<button onClick={() => setGReply(null)} style={{ float: "right", background: "none", border: "none", cursor: "pointer", color: TEXT3, fontSize: 14, lineHeight: 1 }}>&times;</button></div>}
        {voiceError && (
          <div style={{ marginBottom: 8, padding: "8px 12px", fontSize: 12, color: "#DC2626", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10 }}>{voiceError}</div>
        )}
        {/* Quick-start chips — docked to the chat bar, 8px above the input.
            Hidden after the first message of the active conversation. */}
        {embedded && !hasMessages && !gTyping && (
          <>
            <style>{`
              .vella-idle-chip:hover { transform: translateY(-2px); box-shadow: var(--shadow-md); }
              .vella-idle-chip:active { transform: scale(0.97); }
              @media (prefers-reduced-motion: reduce) {
                .vella-idle-chip:hover, .vella-idle-chip:active { transform: none; }
              }
              /* D8 — balanced 3×2 grid, centered on the chat bar, with bottom
                 clearance so the second row never tucks under the composer. */
              .vella-idle-chips {
                margin-bottom: 14px;
                display: grid;
                grid-template-columns: repeat(3, auto);
                justify-content: center;
                column-gap: 8px;
                row-gap: 8px;
              }
              /* D8 — short viewports can't fit two chip rows above the chat bar,
                 so collapse to a single horizontal-scroll row (no clipping). */
              @media (max-height: 700px) {
                .vella-idle-chips {
                  display: flex;
                  flex-wrap: nowrap;
                  justify-content: flex-start;
                  gap: 8px;
                  overflow-x: auto;
                  padding-bottom: 4px;
                  -webkit-overflow-scrolling: touch;
                  scrollbar-width: none;
                }
                .vella-idle-chips::-webkit-scrollbar { display: none; }
              }
            `}</style>
            {/* Run Ads · Create Post · Design Website / Generate Image · Write
                Copy · Plan Campaign. Layout + responsive collapse live in the
                .vella-idle-chips class above (D8). */}
            <div className="vella-idle-chips">
              {IDLE_CHIPS.map((c) => (
                <IdleChip key={c.label} Icon={c.Icon} onClick={() => gSend(c.cue)}>{c.label}</IdleChip>
              ))}
            </div>
          </>
        )}
        <PromptInputBox
          ref={promptInputRef}
          isLoading={gTyping}
          onStop={stopGeneration}
          placeholder="Ask Vella anything..."
          voiceActive={voiceActive}
          onStartVoice={startVoice}
          onStopVoice={stopVoice}
          taller={embedded}
          onSend={async (message, attached, meta) => {
            if (attached && attached.length > 0 && !embedded) {
              setGReply("File received: " + attached[0].name);
            }
            const m = (message || "").trim();
            if (!m || gTyping) return;
            await gSend(m, { activeWorkflows: meta?.activeWorkflows || [] });
          }}
        />
      </div>
    </div>
  );
}

function CreativeStudio() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    API.getCreativeHistory().then(r => { if (r && r.images) setHistory(r.images); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  const examples = [
    { prompt: "Boutique fashion store showcase", model: "DALL-E 3", type: "E-commerce", color: "#3B82F6" },
    { prompt: "Corporate team collaboration", model: "DALL-E 3", type: "B2B", color: "#3B82F6" },
    { prompt: "Abstract brand identity gradient", model: "Ideogram 2.0", type: "Branding", color: "#8B5CF6" },
    { prompt: "Bold sale typography poster", model: "Ideogram 2.0", type: "Retail", color: "#8B5CF6" },
    { prompt: "Modern home exterior twilight", model: "Higgsfield", type: "Real Estate", color: "#10B981" },
    { prompt: "Minimalist product photography", model: "Higgsfield", type: "Product", color: "#10B981" },
  ];
  const shown = filter === "all" ? examples : examples.filter(e => e.model === filter);
  return (
    <div>
      <h2 style={{ fontSize: 24, fontWeight: 700, color: TEXT, marginBottom: 4, letterSpacing: "-0.022em" }}>Creative Gallery</h2>
      <p style={{ color: TEXT2, fontSize: 14, marginBottom: 16 }}>AI-generated creatives and model examples</p>
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {["all", "DALL-E 3", "Ideogram 2.0", "Higgsfield"].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{ padding: "6px 16px", borderRadius: 999, border: filter === f ? "2px solid " + BLUE : "1px solid rgba(255,255,255,0.45)", background: filter === f ? BLUE_LIGHT : "rgba(255,255,255,0.55)", backdropFilter: "blur(18px) saturate(180%)", WebkitBackdropFilter: "blur(18px) saturate(180%)", color: filter === f ? BLUE : TEXT2, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{f === "all" ? "All Models" : f}</button>
        ))}
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: TEXT, marginBottom: 12 }}>Example Prompts by Model</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 16, marginBottom: 28 }}>
        {shown.map((img, i) => (
          <div key={i} style={{ ...card3d, padding: "18px 16px", borderLeft: "3px solid " + img.color }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: img.color, flexShrink: 0 }} />
              <span style={{ fontSize: 10, fontWeight: 700, color: img.color, textTransform: "uppercase", letterSpacing: "0.05em" }}>{img.model}</span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: TEXT, lineHeight: 1.4, marginBottom: 8 }}>{img.prompt}</div>
            <div style={{ fontSize: 11, color: TEXT3 }}>{img.type}</div>
          </div>
        ))}
      </div>
      {loading && <Loader text="Loading your creatives..." />}
      {!loading && history.length > 0 && <><div style={{ fontSize: 13, fontWeight: 700, color: TEXT, marginBottom: 12 }}>Your Creatives</div><div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 16 }}>
        {history.map((img, i) => (
          <div key={i} style={{ ...card3d, overflow: "hidden" }}>
            <img src={img.url || img.imageUrl} style={{ width: "100%", height: 160, objectFit: "cover" }} />
            <div style={{ padding: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{img.prompt || "Creative"}</div>
              <div style={{ fontSize: 11, color: TEXT3, marginTop: 2 }}>{img.model || "AI"}</div>
            </div>
          </div>
        ))}
      </div></>}
    </div>
  );
}

function AnalysisView({ sys }) {
  const [q, setQ] = useState(""); const [res, setRes] = useState(null); const [loading, setLoading] = useState(false);
  const ask = async (query) => { const qq = query || q; if (!qq.trim()) return; setLoading(true); setRes(null); const r = await askVella(sys.analysis, `Data: 4 campaigns (FB/IG/TikTok/Google), $3,750 spend, 4,930 clicks, 9.1% CTR, 139 conversions. Question: "${qq}"`); setRes(r); setLoading(false); };
  const presets = ["Which platform performs best?", "How to lower CPC?", "Should I scale budget?", "Best audience to target?"];
  return (
    <div>
      <h2 style={{ fontSize: 24, fontWeight: 700, color: TEXT, marginBottom: 4, letterSpacing: "-0.022em" }}>Analysis</h2><p style={{ color: TEXT2, fontSize: 14, marginBottom: 20 }}>Ask Vella to analyze performance.</p>
      <div style={card3d}><div style={{ display: "flex", gap: 10, marginBottom: 12 }}><input style={{ ...I, flex: 1 }} placeholder="Ask Vella about your ads..." value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && ask()} /><button style={{ ...B, borderRadius: 999 }} onClick={() => ask()}>Analyze</button></div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{presets.map((p, i) => <button key={i} onClick={() => { setQ(p); ask(p); }} style={{ ...Bg, fontSize: 12, padding: "7px 14px", borderRadius: 999, background: "rgba(255,255,255,0.55)", border: "1px solid rgba(255,255,255,0.45)" }}>{p}</button>)}</div></div>
      {loading && <Loader text="Analyzing..." />}
      {res && <div style={{ ...card3d, boxShadow: SHADOW_LG }}><span style={{ fontSize: 12, fontWeight: 700, color: BLUE, letterSpacing: 0.5 }}>VELLA'S ANALYSIS</span><div style={{ fontSize: 14, lineHeight: 1.75, color: TEXT, whiteSpace: "pre-wrap", marginTop: 10 }}>{res}</div></div>}
    </div>
  );
}

function RecsView({ sys }) {
  const [recs, setRecs] = useState(null); const [loading, setLoading] = useState(false);
  const get = async () => { setLoading(true); const r = await askVella(sys.analysis, `Data: 4 campaigns (FB, IG, TikTok, Google Ads), $3,750 spend, 4,930 clicks, 9.1% CTR, 139 conversions, ROAS 3.8x-5.1x. Give 5 specific, confident, actionable recommendations with expected impact.`); setRecs(r); setLoading(false); };
  return (
    <div>
      <h2 style={{ fontSize: 24, fontWeight: 700, color: TEXT, marginBottom: 4, letterSpacing: "-0.022em" }}>Agent Recommendations</h2>
      {!recs && !loading && <div style={{ ...card3d, textAlign: "center", padding: 48 }}><VellaLogo size={48} /><p style={{ color: TEXT2, fontSize: 15, margin: "16px 0 24px" }}>I'll audit everything and tell you exactly what to do next.</p>
              <div style={{ marginTop: 8, marginBottom: 4 }}>
                <span style={{ color: "#7C3AED", fontSize: 11, fontWeight: 600, letterSpacing: 0.5 }}>Powered by Claude AI</span>
                <div style={{ color: TEXT3, fontSize: 10, marginTop: 2 }}>Trusted by 70% of Fortune 100</div>
              </div><button style={{ ...B, borderRadius: 999, padding: "13px 36px", fontSize: 15 }} onClick={get}>Generate</button></div>}
      {loading && <Loader text="Reviewing campaigns..." />}
      {recs && <div style={{ ...card3d, boxShadow: SHADOW_LG }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}><span style={{ fontSize: 12, fontWeight: 700, color: BLUE, letterSpacing: 0.5 }}>RECOMMENDATIONS</span><button style={{ ...Bg, borderRadius: 999 }} onClick={get}>Refresh</button></div><div style={{ fontSize: 14, lineHeight: 1.8, color: TEXT, whiteSpace: "pre-wrap" }}>{recs}</div></div>}
    </div>
  );
}

// ===========================================
// WEBSITE / SEO (preserved with all sub-tools, dark theme)
// NOTE: All SEO sub-views preserved from 1.0 but with dark theme applied
// ===========================================
function ConnectedSites({ wixStatus, biz }) {
  const wixConnected = !!wixStatus?.connected;
  const goSettings = () => window.dispatchEvent(new CustomEvent('vella:navigate', { detail: 'settings' }));

  // Empty state: no integrations yet — surface the connect CTA inline
  // instead of hiding the card so the page is never blank.
  if (!wixConnected) {
    return (
      <div style={{ ...card3d, marginTop: 14 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: TEXT, letterSpacing: "-0.022em", margin: 0 }}>Connected Sites</h3>
          <span style={{ fontSize: 11, color: TEXT3, fontWeight: 600 }}>None yet</span>
        </div>
        <p style={{ fontSize: 13, color: TEXT2, margin: "0 0 12px" }}>
          Connect Wix so Vella can edit your site, optimize SEO, and publish updates.
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={goSettings} style={{ ...B, borderRadius: 999 }}>Connect Wix</button>
        </div>
      </div>
    );
  }

  const sites = [];
  if (wixConnected) {
    sites.push({
      kind: "wix",
      name: wixStatus?.siteName || biz?.name || "Wix Site",
      url: wixStatus?.siteUrl || biz?.website || "",
      meta: "Wix",
    });
  }

  const Logo = () => {
    return (
      <div style={{ width: 36, height: 36, borderRadius: 8, background: "#fff", border: `1px solid ${BORDER}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="#000"><path d="M5.6 6.5l1.5 8.5 1.7-6.7c.2-.7.5-1 .9-1.2.4-.2.9-.2 1.4 0 .4.2.7.5.9 1.2l1.7 6.7 1.5-8.5h2L14.6 17h-2L11 10.4 9.4 17h-2L5.4 6.5h2.2zm14 0v10.6h-2V6.5h2z"/></svg>
      </div>
    );
  };

  return (
    <div style={{ ...card3d, marginTop: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: TEXT, letterSpacing: "-0.022em", margin: 0 }}>Connected Sites</h3>
        <span style={{ fontSize: 11, color: TEXT3, fontWeight: 600 }}>{sites.length} {sites.length === 1 ? "site" : "sites"}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 }}>
        {sites.map((s, i) => {
          const href = s.url && /^https?:\/\//i.test(s.url)
            ? s.url
            : s.url ? `https://${s.url}` : null;
          return (
            <div key={`${s.kind}-${i}`} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 12, background: "rgba(255,255,255,0.55)", border: "1px solid rgba(255,255,255,0.55)" }}>
              <Logo />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: TEXT, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</div>
                <div style={{ fontSize: 11, color: TEXT3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {s.url || s.meta}
                </div>
              </div>
              <span style={tg(`${SUCCESS}18`, SUCCESS)}>Connected</span>
              {href && (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 11, fontWeight: 600, color: BLUE, textDecoration: "none", padding: "4px 10px", borderRadius: 999, border: `1px solid ${BLUE}33`, background: BLUE_LIGHT }}
                >
                  Open ↗
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WebsiteView({ sys, biz, setActive, setVellaPrompt }) {
  const [wixStatus, setWixStatus] = useState(null);
  useEffect(() => {
    API.getWixStatus().then(setWixStatus).catch(() => {});
  }, []);

  // A site is "live" when we have a URL from the connected platform or the
  // business profile. Deployment metadata falls back to sensible defaults
  // until the backend exposes real deploy timestamps.
  const rawUrl = wixStatus?.siteUrl || biz?.website || "";
  const hasSite = !!rawUrl;
  const href = rawUrl ? (/^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`) : null;
  const displayUrl = rawUrl.replace(/^https?:\/\//i, "");
  const siteName = wixStatus?.siteName || biz?.name || "My Website";
  const status = (wixStatus?.status || "ready").toString().toLowerCase();
  const isBuilding = status === "building" || status === "deploying";
  const lastDeployed = wixStatus?.lastDeployed
    ? new Date(wixStatus.lastDeployed).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : "Recently";

  // Route an instruction to Vella's chat surface.
  const toChat = (prompt) => {
    if (prompt && setVellaPrompt) setVellaPrompt(prompt);
    if (setActive) setActive("talkToVella");
    else window.dispatchEvent(new CustomEvent("vella:navigate", { detail: "talkToVella" }));
  };

  // D4: "Edit website" NAVIGATES to the dedicated editor route and fires
  // nothing else — no canned chat prompt, no auto voice-start. Voice may only
  // begin from an explicit mic gesture (see the global voice rule).
  const editWebsite = () => {
    if (setActive) setActive("website-edit");
    else window.dispatchEvent(new CustomEvent("vella:navigate", { detail: "website-edit" }));
  };
  const redeploy = () => toChat(`Please redeploy my website${displayUrl ? ` (${displayUrl})` : ""} with the latest changes and confirm once it's live.`);
  const designWebsite = () => toChat("Design a brand-new website for my business. Ask me anything you need about the brand, then build and deploy it.");
  const domainSettings = () => { if (setActive) setActive("settings"); else window.dispatchEvent(new CustomEvent("vella:navigate", { detail: "settings" })); };

  return (
    <div>
      <h2 style={{ fontSize: 24, fontWeight: 700, color: TEXT, marginBottom: 4, letterSpacing: "-0.022em" }}>My Website</h2>
      <VellaNudge text="Here's your live site and how it's performing. Ask me to edit or redeploy anytime." small />

      {/* ── Live website — Operation Showroom preview frame ───────────────── */}
      {hasSite ? (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: TEXT, letterSpacing: "-0.022em", margin: 0 }}>{siteName}</h3>
            <span style={tg(isBuilding ? `${WARNING}18` : `${SUCCESS}18`, isBuilding ? WARNING : SUCCESS)}>
              {isBuilding ? "Building" : "Ready"}
            </span>
            <span style={{ fontSize: 12, color: TEXT3 }}>Last deployed {lastDeployed}</span>
          </div>

          <ShowroomFrame
            url={href}
            domain={displayUrl}
            title={siteName}
            redeploying={isBuilding}
            onEdit={editWebsite}
            onVisit={href ? () => window.open(href, "_blank", "noopener") : undefined}
            onRedeploy={redeploy}
            onDomainSettings={domainSettings}
          />
        </div>
      ) : (
        <ShowroomEmptyState onDesign={designWebsite} />
      )}

      {/* ── Connected platforms ───────────────────────────────────────────── */}
      <ConnectedSites wixStatus={wixStatus} biz={biz} />

      {/* ── Analytics ─────────────────────────────────────────────────────── */}
      <div style={{ marginTop: 24 }}>
        <WebsiteAnalytics biz={biz} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// WebsiteEditView — the dedicated /website/edit route (D4). Reached ONLY by
// the "Edit website" button. Navigating here fires nothing else — no canned
// chat prompt, no auto voice-start. The site loads in an INTERACTIVE iframe
// (pointer-events auto) with a device toggle, so the user can click around.
// Content edits are requested via an explicit text submit → chat (no voice).
// ─────────────────────────────────────────────────────────────────────────
function WebsiteEditView({ biz, setActive, setVellaPrompt }) {
  const [wixStatus, setWixStatus] = useState(null);
  const [device, setDevice] = useState("desktop");
  const [ask, setAsk] = useState("");
  useEffect(() => { API.getWixStatus().then(setWixStatus).catch(() => {}); }, []);

  const rawUrl = wixStatus?.siteUrl || biz?.website || "";
  const href = rawUrl ? (/^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`) : null;
  const displayUrl = rawUrl.replace(/^https?:\/\//i, "");
  const siteName = wixStatus?.siteName || biz?.name || "My Website";

  const DEVS = { desktop: { label: "Desktop", w: 1280 }, tablet: { label: "Tablet", w: 834 }, phone: { label: "Phone", w: 390 } };
  const back = () => { if (setActive) setActive("website"); else window.dispatchEvent(new CustomEvent("vella:navigate", { detail: "website" })); };

  // Explicit user submit only — this is the ONE place an edit request leaves
  // this view, and it goes to the TEXT composer (never voice).
  const submitAsk = () => {
    const t = ask.trim();
    if (!t) return;
    if (setVellaPrompt) setVellaPrompt(`Edit my website${displayUrl ? ` (${displayUrl})` : ""}: ${t}`);
    if (setActive) setActive("talkToVella");
    else window.dispatchEvent(new CustomEvent("vella:navigate", { detail: "talkToVella" }));
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <SRActionButton variant="glass" onClick={back}>← Back</SRActionButton>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: TEXT, margin: 0, letterSpacing: "-0.022em" }}>Edit {siteName}</h2>
        <div style={{ marginLeft: "auto", display: "inline-flex", gap: 2, padding: 3, borderRadius: 10, background: "rgba(255,255,255,0.55)", border: `1px solid ${BORDER}` }}>
          {Object.entries(DEVS).map(([k, d]) => {
            const on = device === k;
            return (
              <button key={k} type="button" onClick={() => setDevice(k)} style={{ height: 28, padding: "0 12px", cursor: "pointer", borderRadius: 7, fontSize: 12, fontWeight: 700, border: "none", color: on ? "#fff" : TEXT2, background: on ? BLUE : "transparent" }}>{d.label}</button>
            );
          })}
        </div>
      </div>

      <div style={{ borderRadius: 20, overflow: "hidden", border: `1px solid ${BORDER}`, background: "#fff", boxShadow: "0 18px 48px -22px rgba(39,92,115,0.28)", display: "flex", justifyContent: "center", padding: device === "desktop" ? 0 : 20 }}>
        {href ? (
          <iframe
            title="Edit website"
            src={href}
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
            style={{ width: DEVS[device].w, maxWidth: "100%", height: 620, border: "none", display: "block", background: "#fff" }}
          />
        ) : (
          <div style={{ height: 620, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: TEXT3, fontSize: 13 }}>No site to edit yet</div>
        )}
      </div>

      <div style={{ marginTop: 16, display: "flex", gap: 10, alignItems: "center", maxWidth: 720 }}>
        <input
          value={ask}
          onChange={(e) => setAsk(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submitAsk(); }}
          placeholder="Describe a change — e.g. “make the hero headline bigger”"
          style={{ flex: 1, height: 44, padding: "0 16px", fontFamily: FONT, fontSize: 14, color: TEXT, borderRadius: 999, border: `1px solid ${BORDER}`, background: "rgba(255,255,255,0.7)", outline: "none" }}
        />
        <SRActionButton variant="primary" onClick={submitAsk}>Ask Vella</SRActionButton>
      </div>
    </div>
  );
}

// ===========================================
// SETTINGS (preserved, dark theme)
// ===========================================

// --- Autopilot View ---
function AutopilotView({ biz }) {
  const card = { background: "linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.68) 100%)", backdropFilter: "blur(34px) saturate(180%)", WebkitBackdropFilter: "blur(34px) saturate(180%)", borderRadius: 20, padding: 20, marginBottom: 14, boxShadow: "0 1px 0 rgba(255,255,255,0.70) inset, 0 18px 44px -18px rgba(11,107,211,0.28), 0 8px 24px -10px rgba(14,165,233,0.18)", border: `1px solid rgba(255,255,255,0.45)` };
  const label = { fontSize: 11, fontWeight: 600, color: TEXT3, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 6, display: "block" };
  const input = { background: BG, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "10px 14px", color: TEXT, fontSize: 14, outline: "none", width: "100%", fontFamily: FONT };
  const btn = { background: BLUE, color: "#FFF", border: "none", borderRadius: 999, padding: "11px 24px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: FONT, transition: "all 0.15s" };
  const [autoReply, setAutoReply] = useState(true);
  const [autoPost, setAutoPost] = useState(false);
  const [autoReport, setAutoReport] = useState(true);
  const [replySpeed, setReplySpeed] = useState("balanced");
  const [postFreq, setPostFreq] = useState("3x");
  const [reportDay, setReportDay] = useState("Monday");
  const [replyTone, setReplyTone] = useState("friendly");
  const [saved, setSaved] = useState(false);

  const handleSave = () => { setSaved(true); setTimeout(() => setSaved(false), 2000); };

  const toggle = (val, setVal, label, desc) => (
    <div style={{ ...card, display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
      <div><div style={{ fontSize: 14, fontWeight: 600, color: TEXT }}>{label}</div><div style={{ fontSize: 12, color: TEXT2, marginTop: 2 }}>{desc}</div></div>
      <div onClick={() => setVal(!val)} style={{ width: 44, height: 24, borderRadius: 12, background: val ? BLUE : BORDER, cursor: "pointer", position: "relative", transition: "background 0.2s" }}>
        <div style={{ width: 20, height: 20, borderRadius: 10, background: "#FFFFFF", position: "absolute", top: 2, left: val ? 22 : 2, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ ...card, background: BLUE + "0D", border: "1px solid " + BLUE + "33", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: BLUE, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ color: "#FFF", fontSize: 18 }}>&#9889;</span>
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: TEXT }}>Vella Autopilot</div>
            <div style={{ fontSize: 12, color: TEXT2 }}>Let Vella handle your marketing tasks automatically, 24/7</div>
          </div>
        </div>
      </div>

      <div style={{ fontSize: 13, fontWeight: 700, color: TEXT, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>Automation Controls</div>

      {toggle(autoReply, setAutoReply, "Auto-Reply to Comments & DMs", "Vella instantly replies to incoming messages on all connected platforms")}
      {toggle(autoPost, setAutoPost, "Auto-Post Scheduling", "Vella creates and publishes posts on a set schedule")}
      {toggle(autoReport, setAutoReport, "Weekly Performance Reports", "Receive AI-generated performance summaries every week")}

      {autoReply && (
        <div style={{ ...card, marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: TEXT, marginBottom: 12 }}>Reply Settings</div>
          <div style={{ marginBottom: 10 }}>
            <label style={label}>Response Speed</label>
            <select value={replySpeed} onChange={e => setReplySpeed(e.target.value)} style={input}>
              <option value="instant">Instant (under 1 min)</option>
              <option value="balanced">Balanced (1-5 min delay)</option>
              <option value="reviewed">Reviewed (queue for approval)</option>
            </select>
          </div>
          <div>
            <label style={label}>Reply Tone</label>
            <select value={replyTone} onChange={e => setReplyTone(e.target.value)} style={input}>
              <option value="friendly">Friendly & Warm</option>
              <option value="professional">Professional</option>
              <option value="casual">Casual & Fun</option>
              <option value="brand">Match Brand Voice</option>
            </select>
          </div>
        </div>
      )}

      {autoPost && (
        <div style={{ ...card, marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: TEXT, marginBottom: 12 }}>Posting Schedule</div>
          <label style={label}>Posts Per Week</label>
          <select value={postFreq} onChange={e => setPostFreq(e.target.value)} style={input}>
            <option value="1x">1x per week</option>
            <option value="3x">3x per week</option>
            <option value="5x">5x per week (weekdays)</option>
            <option value="7x">Daily</option>
          </select>
        </div>
      )}

      {autoReport && (
        <div style={{ ...card, marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: TEXT, marginBottom: 12 }}>Report Delivery</div>
          <label style={label}>Send Report On</label>
          <select value={reportDay} onChange={e => setReportDay(e.target.value)} style={input}>
            <option value="Monday">Monday</option>
            <option value="Wednesday">Wednesday</option>
            <option value="Friday">Friday</option>
          </select>
        </div>
      )}

      <div style={{ ...card, marginTop: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: TEXT, marginBottom: 10 }}>Autopilot Status</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <div style={{ padding: "8px 14px", borderRadius: 999, background: autoReply ? "#E8F5E9" : "rgba(255,255,255,0.45)", fontSize: 12, fontWeight: 600, color: autoReply ? "#2E7D32" : TEXT2 }}>{autoReply ? "Replies: Active" : "Replies: Off"}</div>
          <div style={{ padding: "8px 14px", borderRadius: 999, background: autoPost ? "#E8F5E9" : "rgba(255,255,255,0.45)", fontSize: 12, fontWeight: 600, color: autoPost ? "#2E7D32" : TEXT2 }}>{autoPost ? "Posting: Active" : "Posting: Off"}</div>
          <div style={{ padding: "8px 14px", borderRadius: 999, background: autoReport ? "#E8F5E9" : "rgba(255,255,255,0.45)", fontSize: 12, fontWeight: 600, color: autoReport ? "#2E7D32" : TEXT2 }}>{autoReport ? "Reports: Active" : "Reports: Off"}</div>
        </div>
      </div>

      <button onClick={handleSave} style={{ ...btn, width: "100%", marginTop: 16, padding: "12px 0", fontSize: 14, background: saved ? "#34C759" : BLUE }}>
        {saved ? "Saved!" : "Save Autopilot Settings"}
      </button>
    </div>
  );
}

// Subscription status row — fetches real billing state on mount and
// renders a status pill + plan label. Falls back to a neutral "Loading"
// state on first render and an "unavailable" message if the API errors,
// so we never lie to the user about what plan they're on.
function SubscriptionStatusInline() {
  const [billing, setBilling] = useState({ loading: true, error: null, status: null, plan: null });
  React.useEffect(() => {
    let cancelled = false;
    API.getBillingStatus()
      .then(d => { if (!cancelled) setBilling({ loading: false, error: null, status: d?.status || d?.subscription?.status || null, plan: d?.plan || d?.subscription?.plan || d?.planName || null }); })
      .catch(err => { if (!cancelled) setBilling({ loading: false, error: err?.message || "Could not load subscription", status: null, plan: null }); });
    return () => { cancelled = true; };
  }, []);

  if (billing.loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 13, color: TEXT3 }}>Loading subscription…</span>
      </div>
    );
  }
  if (billing.error || (!billing.status && !billing.plan)) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={tg(`${WARNING}18`, WARNING)}>Unknown</span>
        <span style={{ fontSize: 13, color: TEXT2 }}>We couldn't load your subscription. Try refreshing or contact support.</span>
      </div>
    );
  }
  const status = (billing.status || "active").toLowerCase();
  const isActive = status === "active" || status === "trialing";
  const isPastDue = status === "past_due" || status === "unpaid" || status === "incomplete";
  const pillBg = isActive ? `${SUCCESS}18` : isPastDue ? `${DANGER}18` : `${WARNING}18`;
  const pillFg = isActive ? SUCCESS : isPastDue ? DANGER : WARNING;
  const label = status === "trialing" ? "Trial" : status === "past_due" ? "Past Due" : status === "unpaid" ? "Unpaid" : status.charAt(0).toUpperCase() + status.slice(1);
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={tg(pillBg, pillFg)}>{label}</span>
        <span style={{ fontSize: 14, fontWeight: 600, color: TEXT }}>{billing.plan || "—"}</span>
      </div>
      <p style={{ fontSize: 13, color: TEXT2, marginTop: 6, lineHeight: 1.5 }}>
        {isActive ? "Social media, advertising, Wix SEO, AI content, auto-replies." : isPastDue ? "Your last payment didn't go through. Update your card via Manage Plan to keep Vella running." : "Your subscription needs attention — open Manage Plan to update it."}
      </p>
    </>
  );
}

function SettingsView({ biz, user, onUpdate, initialTab }) {
  const { t, language, setLanguage, languages } = useTranslation();
  const [activeTab, setActiveTab] = React.useState(initialTab || "account");
  React.useEffect(() => { if (initialTab) setActiveTab(initialTab); }, [initialTab]);
  const { autopilot: autopilotActive } = React.useContext(AutopilotContext);
  const [teamMembers, setTeamMembers] = React.useState([
    { email: "team@amplifx.com", role: "Super Admin", status: "Active", removable: false }
  ]);
  const [inviteEmail, setInviteEmail] = React.useState("");
  const maxSeats = 2;
  const handleInvite = () => {
    if (!inviteEmail || teamMembers.filter(m => m.removable).length >= maxSeats) return;
    setTeamMembers(prev => [...prev, { email: inviteEmail, role: "Member", status: "Pending", removable: true }]);
    setInviteEmail("");
  };
  const handleRemove = (email) => setTeamMembers(prev => prev.filter(m => m.email !== email));
  const [notifs, setNotifs] = useState({ weeklyReport: true, newComments: true, newDMs: true, campaignAlerts: true, budgetAlerts: true, seoUpdates: false, postReminders: true, leadAlerts: true });
  const [saved, setSaved] = useState({ email: false, acct: false, voice: false });
  const [website, setWebsite] = useState(biz?.website || "");
  const [fbUrl, setFbUrl] = useState(biz?.facebookUrl || "");
  const [igHandle, setIgHandle] = useState(biz?.instagramHandle || "");
  const [ttHandle, setTtHandle] = useState(biz?.tiktokHandle || "");
  const [gadsId, setGadsId] = useState(biz?.googleAdsId || "");
  const [bizPhone, setBizPhone] = useState(biz?.phone || "");
  const [voice, setVoice] = useState("Professional yet approachable. Data-driven. Bold CTAs.");
  // Brand setup — logo upload, brand colors (max 3), tagline. Mirrors
  // OnboardingWizard step 3 so users can edit later from Settings. Hydrated
  // from `biz` (which comes from /api/auth/business) so reopens reflect
  // saved state.
  const brandFileInputRef = React.useRef(null);
  const [brandLogoUrl, setBrandLogoUrl] = useState(biz?.logoUrl || null);
  const [brandLogoName, setBrandLogoName] = useState(null);
  const [brandColors, setBrandColors] = useState(
    Array.isArray(biz?.brandColors) && biz.brandColors.length > 0 ? biz.brandColors : [BLUE]
  );
  const [brandTagline, setBrandTagline] = useState(biz?.tagline || "");
  const [brandSaving, setBrandSaving] = useState(false);
  const [brandSaved, setBrandSaved] = useState(false);
  const [brandError, setBrandError] = useState("");
  // How Vella addresses the user — persisted on the User row via PATCH /me.
  const [preferredAddress, setPreferredAddress] = useState(user?.preferredAddress || "first_name");
  const [addrSaved, setAddrSaved] = useState(false);
  const saveAddress = async (value) => {
    setPreferredAddress(value);
    try {
      await API.updateMe({ preferredAddress: value });
      setAddrSaved(true);
      setTimeout(() => setAddrSaved(false), 2000);
    } catch (e) { console.warn("Save address preference failed:", e.message); }
  };
  React.useEffect(() => {
    if (biz?.logoUrl !== undefined) setBrandLogoUrl(biz.logoUrl || null);
    if (Array.isArray(biz?.brandColors) && biz.brandColors.length > 0) {
      setBrandColors(biz.brandColors);
    }
    if (typeof biz?.tagline === "string") setBrandTagline(biz.tagline);
  }, [biz?.logoUrl, biz?.brandColors, biz?.tagline]);
  const onBrandLogoSelected = (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) { setBrandError("Logo must be an image"); return; }
    if (file.size > 5 * 1024 * 1024) { setBrandError("Logo must be under 5 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setBrandLogoUrl(reader.result);
      setBrandLogoName(file.name);
      setBrandError("");
    };
    reader.readAsDataURL(file);
  };
  const addBrandColor = () => {
    if (brandColors.length >= 3) return;
    setBrandColors((c) => [...c, "#0A1B3A"]);
  };
  const setBrandColorAt = (i, v) => setBrandColors((c) => c.map((x, idx) => (idx === i ? v : x)));
  const removeBrandColorAt = (i) => setBrandColors((c) => c.filter((_, idx) => idx !== i));
  const saveBrand = async () => {
    if (brandSaving) return;
    setBrandSaving(true);
    setBrandError("");
    try {
      const updated = await API.saveBusiness({
        name: biz?.name || "Vella Client",
        industry: biz?.industry || "other",
        logoUrl: brandLogoUrl || undefined,
        brandColors,
        tagline: brandTagline.trim() || undefined,
      });
      // saveBusiness returns { business } in the existing API; tolerate either shape.
      const next = updated?.business || updated || {
        ...biz,
        logoUrl: brandLogoUrl,
        brandColors,
        tagline: brandTagline.trim() || null,
      };
      if (onUpdate) onUpdate({ ...biz, ...next });
      setBrandSaved(true);
      setTimeout(() => setBrandSaved(false), 2000);
    } catch (e) {
      setBrandError(e?.message || "Save failed");
    } finally {
      setBrandSaving(false);
    }
  };
  // ─── Account self-service: password / deletion / invoices ──────────
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwNew2, setPwNew2] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwSaved, setPwSaved] = useState(false);
  const [pwError, setPwError] = useState("");

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const [invoices, setInvoices] = useState(null); // null = not loaded, [] = empty
  const [invoicesError, setInvoicesError] = useState(null);
  React.useEffect(() => {
    let cancelled = false;
    API.getInvoices({ limit: 12 })
      .then((d) => { if (!cancelled) setInvoices(Array.isArray(d?.invoices) ? d.invoices : []); })
      .catch((e) => { if (!cancelled) { setInvoices([]); setInvoicesError(e?.message || "Could not load invoices"); } });
    return () => { cancelled = true; };
  }, []);

  const submitPasswordChange = async () => {
    setPwError("");
    if (!pwCurrent || !pwNew) { setPwError("Both fields are required."); return; }
    if (pwNew.length < 8) { setPwError("New password must be at least 8 characters."); return; }
    if (pwNew !== pwNew2) { setPwError("New passwords don't match."); return; }
    setPwSaving(true);
    try {
      await API.changePassword(pwCurrent, pwNew);
      setPwCurrent(""); setPwNew(""); setPwNew2("");
      setPwSaved(true);
      setTimeout(() => setPwSaved(false), 3000);
    } catch (e) {
      setPwError(e?.message || "Could not change password.");
    } finally {
      setPwSaving(false);
    }
  };

  const submitAccountDeletion = async () => {
    setDeleteError("");
    if (deleteConfirm !== "DELETE") { setDeleteError('Type "DELETE" to confirm.'); return; }
    if (!deletePassword) { setDeleteError("Enter your password."); return; }
    setDeleteSubmitting(true);
    try {
      await API.deleteAccount(deletePassword, deleteConfirm);
      // Hard reset — clear any cached state and bounce to the marketing site.
      try { localStorage.clear(); } catch {}
      try { sessionStorage.clear(); } catch {}
      window.location.href = "/";
    } catch (e) {
      setDeleteError(e?.message || "Could not delete account.");
      setDeleteSubmitting(false);
    }
  };

  const [wixStatus, setWixStatus] = useState(null);
  const [stripeAccepted, setStripeAccepted] = useState(false);
  const [briefingEnabled, setBriefingEnabled] = useState(false);
  const [briefingTime, setBriefingTime] = useState("08:00");
  const [briefingCallStatus, setBriefingCallStatus] = useState(null); // null | 'calling' | 'done' | 'error'
  const [briefingPhone, setBriefingPhone] = useState("");
  const [briefingTz, setBriefingTz] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York");
  const [briefingIncludeApprovals, setBriefingIncludeApprovals] = useState(true);
  const [briefingError, setBriefingError] = useState("");

  // Load existing schedule on mount
  useEffect(() => {
    API.getVoiceSchedule().then(({ schedule }) => {
      if (!schedule) return;
      setBriefingEnabled(!!schedule.enabled);
      if (schedule.call_time) setBriefingTime(schedule.call_time.slice(0, 5));
      if (schedule.phone_number) setBriefingPhone(schedule.phone_number);
      if (schedule.timezone) setBriefingTz(schedule.timezone);
      if (typeof schedule.include_approvals === "boolean") setBriefingIncludeApprovals(schedule.include_approvals);
    }).catch(() => {});
  }, []);

  // E.164 normalizer: "(555) 123-4567" -> "+15551234567"
  const _toE164 = (input) => {
    if (!input) return "";
    const digits = String(input).replace(/\D/g, "");
    if (!digits) return "";
    if (String(input).trim().startsWith("+")) return "+" + digits;
    if (digits.length === 10) return "+1" + digits;           // assume US
    if (digits.length === 11 && digits.startsWith("1")) return "+" + digits;
    return "+" + digits;
  };

  const _saveVoiceSchedule = async (patch) => {
    const phoneE164 = _toE164(briefingPhone);
    if (!phoneE164 || phoneE164.length < 8) {
      setBriefingError("Enter a valid phone number");
      return false;
    }
    setBriefingError("");
    try {
      await API.saveVoiceSchedule({
        phoneNumber: phoneE164,
        callTime: briefingTime,
        timezone: briefingTz,
        enabled: briefingEnabled,
        includeDigest: true,
        includeApprovals: briefingIncludeApprovals,
        ...patch,
      });
      return true;
    } catch (e) {
      setBriefingError(e.message || "Save failed");
      return false;
    }
  };
  const [oauthStatus, setOauthStatus] = useState({ facebook: null, instagram: null, tiktok: null, google_ads: null });
  const [connecting, setConnecting] = useState({});

  useEffect(() => {
    API.getWixStatus().then(setWixStatus).catch((err) => console.error("Wix status fetch failed:", err));
    API.getOAuthStatus().then(d => setOauthStatus(d.platforms)).catch((err) => console.error("OAuth status fetch failed:", err));
    // Check for redirect back from OAuth
    const params = new URLSearchParams(window.location.search);
    // Handle Wix claim code from callback redirect
    const wixClaim = params.get("wix_claim");
    if (wixClaim) {
      API.claimWixConnection(wixClaim)
        .then(() => {
          API.getWixStatus().then(setWixStatus);
        })
        .catch((err) => console.error("Wix claim failed:", err));
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    const connected = params.get("connected");
    const error = params.get("error");
    if (connected || error) {
      API.getOAuthStatus().then(d => setOauthStatus(d.platforms)).catch((err) => console.error("OAuth status check after redirect failed:", err));
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const [saveError, setSaveError] = useState(null);

  const connectPlatform = async (platform) => {
    setConnecting(p => ({ ...p, [platform]: true }));
    setSaveError(null);
    try {
      const d = await API.getOAuthConnectUrl(platform);
      if (d?.authUrl) {
        window.location.href = d.authUrl;
      } else {
        setSaveError(`Couldn't start the ${platform} connection. Try again in a moment.`);
        setConnecting(p => ({ ...p, [platform]: false }));
      }
    } catch (e) {
      setSaveError(`Couldn't start the ${platform} connection: ${e?.message || "request failed"}`);
      setConnecting(p => ({ ...p, [platform]: false }));
    }
  };

  const disconnectPlatform = async (platform) => {
    try {
      await API.disconnectPlatform(platform);
      setOauthStatus(p => ({ ...p, [platform]: null }));
    } catch (e) {
      setSaveError(`Couldn't disconnect ${platform}: ${e?.message || "request failed"}`);
    }
  };

  const toggleNotif = (k) => setNotifs(p => ({ ...p, [k]: !p[k] }));
  const saveNotifs = async () => {
    setSaveError(null);
    try {
      await API.saveNotificationPrefs(notifs);
      setSaved(p => ({ ...p, email: true }));
      setTimeout(() => setSaved(p => ({ ...p, email: false })), 2000);
    } catch (e) {
      setSaveError(`Couldn't save notifications: ${e?.message || "request failed"}`);
    }
  };
  const saveAccounts = async () => {
    setSaveError(null);
    const u = { ...biz, website, phone: bizPhone, facebookUrl: fbUrl, instagramHandle: igHandle, tiktokHandle: ttHandle, googleAdsId: gadsId };
    try {
      // saveBusiness is the source of truth — call it directly so we can
      // surface real failures instead of relying on the optimistic
      // onUpdate callback that swallowed errors.
      await API.saveBusiness(u);
      if (onUpdate) onUpdate(u);
      setSaved(p => ({ ...p, acct: true }));
      setTimeout(() => setSaved(p => ({ ...p, acct: false })), 2000);
    } catch (e) {
      setSaveError(`Couldn't save your account info: ${e?.message || "request failed"}`);
    }
  };

  const notifOpts = [
    { key: "weeklyReport", label: "Weekly Report", desc: "Performance summary every Monday" },
    { key: "newComments", label: "New Comments", desc: "When someone comments on your posts" },
    { key: "newDMs", label: "New DMs", desc: "Instant alert for new messages" },
    { key: "leadAlerts", label: "Lead Alerts", desc: "High-intent messages detected" },
    { key: "campaignAlerts", label: "Campaign Alerts", desc: "Campaign milestones or issues" },
    { key: "budgetAlerts", label: "Budget Alerts", desc: "Spend hits 80% or 100%" },
    { key: "postReminders", label: "Post Reminders", desc: "Haven't posted in 2+ days" },
    { key: "seoUpdates", label: "SEO Changes", desc: "Search ranking changes" },
  ];

  if (!biz) {
    return (
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: TEXT, marginBottom: 20, letterSpacing: "-0.022em" }}>{t("settings.title")}</h2>
        <SkeletonForm rows={5} />
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ fontSize: 24, fontWeight: 700, color: TEXT, marginBottom: 20, letterSpacing: "-0.022em" }}>{t("settings.title")}</h2>

      {/* Tab bar */}
      <div style={{ display: "flex", gap: 0, marginBottom: 24, borderBottom: `1px solid ${BORDER}`, overflowX: "auto" }}>
        {[
          { id: "account",       label: t("settings.tabs.account") },
          { id: "notifications", label: t("settings.tabs.notifications") },
          { id: "team",          label: t("settings.tabs.team") },
          { id: "privacy",       label: t("settings.tabs.privacy") },
          { id: "terms",         label: t("settings.tabs.terms") },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ padding: "10px 18px", border: "none", borderBottom: activeTab === tab.id ? `2px solid ${BLUE}` : "2px solid transparent", background: "transparent", fontFamily: FONT, fontSize: 13, fontWeight: activeTab === tab.id ? 700 : 500, color: activeTab === tab.id ? BLUE : TEXT2, cursor: "pointer", whiteSpace: "nowrap", marginBottom: -1, transition: "color 0.2s" }}>{tab.label}</button>
        ))}
      </div>

      {activeTab === "privacy" && <PrivacyPolicy embedded />}
      {activeTab === "terms" && <TermsOfService embedded />}

      {saveError && (
        <div role="alert" style={{ background: `${DANGER}10`, border: `1px solid ${DANGER}40`, color: DANGER, borderRadius: 12, padding: "10px 14px", marginBottom: 14, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <span>{saveError}</span>
          <button type="button" onClick={() => setSaveError(null)} aria-label="Dismiss" style={{ background: "transparent", border: "none", cursor: "pointer", color: DANGER, fontSize: 16, padding: 0, lineHeight: 1 }}>×</button>
        </div>
      )}

      {activeTab === "account" && <div style={card3d}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: TEXT, marginBottom: 6, letterSpacing: "-0.022em" }}>How should Vella address you?</h3>
        <p style={{ fontSize: 13, color: TEXT2, marginBottom: 12 }}>Used in chat and voice greetings.</p>
        <select
          style={{ ...I, width: "100%", appearance: "none" }}
          value={["first_name", "formal", "full_name"].includes(preferredAddress) ? preferredAddress : "custom"}
          onChange={e => {
            const v = e.target.value;
            if (v === "custom") { setPreferredAddress(""); return; }
            saveAddress(v);
          }}
        >
          <option value="first_name">First name{user?.name ? ` (${user.name.split(" ")[0]})` : ""}</option>
          <option value="formal">Mr./Ms. + Last name</option>
          <option value="full_name">Full name</option>
          <option value="custom">Custom…</option>
        </select>
        {!["first_name", "formal", "full_name"].includes(preferredAddress) && (
          <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
            <input
              style={{ ...I, flex: 1 }}
              value={preferredAddress}
              onChange={e => setPreferredAddress(e.target.value)}
              placeholder='e.g. "Chief" or "Vince"'
              maxLength={80}
            />
            <button style={{ ...B, borderRadius: 999, padding: "11px 20px" }} onClick={() => saveAddress(preferredAddress.trim())}>Save</button>
          </div>
        )}
        {addrSaved && <p style={{ fontSize: 12, color: SUCCESS, marginTop: 8 }}>Saved — Vella will use that from now on.</p>}
      </div>}
      {activeTab === "account" && <AutopilotSettings />}
      {activeTab === "account" && <div style={card3d}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: TEXT, marginBottom: 4, letterSpacing: "-0.022em" }}>Subscription</h3>
            <SubscriptionStatusInline />
          </div>
          <button style={{ ...B, borderRadius: 999, padding: "9px 20px", fontSize: 13 }} onClick={async () => {
            try {
              const d = await API.openBillingPortal();
              if (d?.url) window.open(d.url, "_blank");
              else alert("Billing portal isn't available right now. Please try again in a moment.");
            } catch (e) {
              alert(`Couldn't open the billing portal: ${e?.message || "request failed"}`);
            }
          }}>Manage Plan</button>
        </div>
        <div style={{ height: 1, background: BORDER, margin: "14px 0" }} />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <p style={{ fontSize: 14, fontWeight: 600, color: TEXT, marginBottom: 2 }}>AI Credits</p>
            <p style={{ fontSize: 13, color: TEXT2 }}>Top up for extra AI usage beyond your plan.</p>
          </div>
          <button
            style={{ background: `linear-gradient(135deg, ${BLUE} 0%, #1D4ED8 100%)`, color: "#FFF", border: "none", borderRadius: 999, padding: "10px 22px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: FONT, boxShadow: "0 4px 14px rgba(37,99,235,0.3)", display: "flex", alignItems: "center", gap: 8, opacity: stripeAccepted ? 1 : 0.4, pointerEvents: stripeAccepted ? "auto" : "none" }}            onClick={async () => {
              try {
                const data = await API.buyCredits("growth");
                if (data.url) window.open(data.url, "_blank");
              } catch (e) { console.warn("Buy credits failed:", e.message); }
            }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
            Buy Credits
          </button>
        </div>
        {/* Stripe Legal Disclaimer */}
        <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 10, padding: "12px 16px", marginTop: 12, display: "flex", alignItems: "flex-start", gap: 10 }}>
          <input type="checkbox" checked={stripeAccepted} onChange={e => setStripeAccepted(e.target.checked)} style={{ marginTop: 3, accentColor: BLUE, width: 18, height: 18, cursor: "pointer", flexShrink: 0 }} />
          <p style={{ fontSize: 12, color: "#92400E", lineHeight: 1.5, margin: 0 }}>By purchasing AI credits, you agree to Stripe's <a href="https://stripe.com/legal/consumer" target="_blank" rel="noreferrer" style={{ color: "#B45309", textDecoration: "underline" }}>Terms of Service</a> and authorize charges to your payment method. Credits are non-refundable. All transactions are securely processed by Stripe.</p>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap", opacity: stripeAccepted ? 1 : 0.4, pointerEvents: stripeAccepted ? "auto" : "none", transition: "opacity 0.3s" }}>          {[{ label: "Starter", price: "$15", desc: "500 credits" }, { label: "Growth", price: "$30", desc: "1,200 credits" }, { label: "Power", price: "$50", desc: "2,500 credits" }].map((pack, i) => (
            <div key={i} style={{ flex: 1, minWidth: 90, background: i === 1 ? BLUE_LIGHT : "rgba(255,255,255,0.35)", backdropFilter: "blur(18px) saturate(180%)", WebkitBackdropFilter: "blur(18px) saturate(180%)", border: `1px solid ${i === 1 ? BLUE : "rgba(255,255,255,0.45)"}`, borderRadius: 18, padding: "10px 14px", textAlign: "center" }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: i === 1 ? BLUE : TEXT }}>{pack.label}</p>
              <p style={{ fontSize: 18, fontWeight: 800, color: i === 1 ? BLUE : TEXT }}>{pack.price}</p>
              <p style={{ fontSize: 11, color: TEXT3 }}>{pack.desc}</p>
            </div>
          ))}
        </div>
      </div>}

      {activeTab === "account" && <div style={{ ...card3d, borderLeft: `3px solid ${wixStatus?.connected ? SUCCESS : WARNING}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div><h3 style={{ fontSize: 16, fontWeight: 700, color: TEXT, letterSpacing: "-0.022em" }}>Wix Website</h3><p style={{ fontSize: 12, color: TEXT2 }}>Connect for SEO, blog publishing, and auto-optimization.</p></div>
          <span style={tg(wixStatus?.connected ? `${SUCCESS}22` : `${WARNING}22`, wixStatus?.connected ? SUCCESS : WARNING)}>{wixStatus?.connected ? "Connected" : "Not Connected"}</span>
        </div>
        <button style={{ ...B, borderRadius: 999 }} onClick={async () => { try { const d = await API.getWixConnectUrl(); window.open(d.authUrl, "_blank"); } catch (e) { console.warn("Wix connect failed:", e.message); } }}>{wixStatus?.connected ? "Reconnect" : "Connect Wix"}</button>
      </div>}

      {/* Daily Briefing Call — Vella literally phones you */}
      {activeTab === "account" && <div style={card3d}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16 }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: TEXT, marginBottom: 4, letterSpacing: "-0.022em" }}>Daily Briefing Call</h3>
            <p style={{ fontSize: 13, color: TEXT2, lineHeight: 1.5, margin: 0 }}>Vella calls you every morning in her own voice with your marketing briefing — overnight performance, top leads, today's priorities, and pending approvals.</p>
          </div>
          <div
            onClick={async () => {
              const next = !briefingEnabled;
              setBriefingEnabled(next);
              const ok = await _saveVoiceSchedule({ enabled: next });
              if (!ok) setBriefingEnabled(!next); // revert on failure
            }}
            style={{ width: 48, height: 28, borderRadius: 14, background: briefingEnabled ? "#4ECDC4" : BORDER, cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0, marginLeft: 16, marginTop: 2 }}
          >
            <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: briefingEnabled ? 23 : 3, transition: "left 0.2s", boxShadow: "0 1px 4px rgba(0,0,0,0.18)" }} />
          </div>
        </div>

        {/* Phone + time + timezone always visible when enabled */}
        {briefingEnabled && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 16px", marginBottom: 12 }}>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: TEXT3, display: "block", marginBottom: 6 }}>Phone Number</label>
              <input
                type="tel"
                value={briefingPhone}
                onChange={(e) => setBriefingPhone(e.target.value)}
                onBlur={() => _saveVoiceSchedule({})}
                placeholder="+1 (555) 123-4567"
                style={{ ...S, width: "100%" }}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: TEXT3, display: "block", marginBottom: 6 }}>Call Time</label>
              <select
                value={briefingTime}
                onChange={(e) => {
                  setBriefingTime(e.target.value);
                  _saveVoiceSchedule({ callTime: e.target.value });
                }}
                style={{ ...S, width: "100%" }}
              >
                {(() => {
                  // Every 15 minutes, 5 AM → 10 PM
                  const opts = [];
                  for (let h = 5; h <= 22; h++) {
                    for (let mm of [0, 15, 30, 45]) {
                      const t = `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
                      const hr12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                      const ap = h >= 12 ? "PM" : "AM";
                      const label = `${hr12}:${String(mm).padStart(2, "0")} ${ap}`;
                      opts.push(<option key={t} value={t}>{label}</option>);
                    }
                  }
                  return opts;
                })()}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: TEXT3, display: "block", marginBottom: 6 }}>Timezone</label>
              <select
                value={briefingTz}
                onChange={(e) => {
                  setBriefingTz(e.target.value);
                  _saveVoiceSchedule({ timezone: e.target.value });
                }}
                style={{ ...S, width: "100%" }}
              >
                <option value="America/New_York">Eastern (New York)</option>
                <option value="America/Chicago">Central (Chicago)</option>
                <option value="America/Denver">Mountain (Denver)</option>
                <option value="America/Phoenix">Arizona (Phoenix)</option>
                <option value="America/Los_Angeles">Pacific (Los Angeles)</option>
                <option value="America/Anchorage">Alaska (Anchorage)</option>
                <option value="Pacific/Honolulu">Hawaii (Honolulu)</option>
                <option value="Europe/London">UK (London)</option>
                <option value="Europe/Paris">Europe (Paris)</option>
                <option value="Asia/Tokyo">Japan (Tokyo)</option>
                <option value="Australia/Sydney">Australia (Sydney)</option>
              </select>
            </div>

            {/* Approval toggle */}
            <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0 0" }}>
              <div>
                <p style={{ fontSize: 14, fontWeight: 600, color: TEXT, margin: 0 }}>Ask for approvals on the call</p>
                <p style={{ fontSize: 12, color: TEXT3, margin: "2px 0 0" }}>Vella will request spoken yes/no on pending posts, budget changes, and draft insights.</p>
              </div>
              <div
                onClick={() => {
                  const next = !briefingIncludeApprovals;
                  setBriefingIncludeApprovals(next);
                  _saveVoiceSchedule({ includeApprovals: next });
                }}
                style={{ width: 48, height: 28, borderRadius: 14, background: briefingIncludeApprovals ? BLUE : BORDER, cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0, marginLeft: 16 }}
              >
                <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: briefingIncludeApprovals ? 23 : 3, transition: "left 0.2s" }} />
              </div>
            </div>
          </div>
        )}

        {briefingEnabled && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 4 }}>
            <button
              style={{ ...B, borderRadius: 999, padding: "10px 20px", background: briefingCallStatus === "done" ? SUCCESS : briefingCallStatus === "error" ? "#E11D48" : BLUE, transition: "background 0.3s" }}
              onClick={async () => {
                if (briefingCallStatus === "calling") return;
                setBriefingCallStatus("calling");
                setBriefingError("");
                try {
                  const phoneE164 = _toE164(briefingPhone);
                  if (!phoneE164 || phoneE164.length < 8) throw new Error("Enter a valid phone number first");
                  // Ensure the schedule is persisted before triggering the call so the voice-call service can look it up.
                  await API.saveVoiceSchedule({
                    phoneNumber: phoneE164,
                    callTime: briefingTime,
                    timezone: briefingTz,
                    enabled: true,
                    includeDigest: true,
                    includeApprovals: briefingIncludeApprovals,
                  });
                  await API.triggerVoiceTestCall(phoneE164);
                  setBriefingCallStatus("done");
                } catch (e) {
                  setBriefingError(e.message || "Call failed");
                  setBriefingCallStatus("error");
                }
                setTimeout(() => setBriefingCallStatus(null), 3000);
              }}
              disabled={briefingCallStatus === "calling"}
            >
              {briefingCallStatus === "calling" ? "Calling..." : briefingCallStatus === "done" ? "Call started!" : briefingCallStatus === "error" ? "Call failed" : "Test Call Now"}
            </button>
          </div>
        )}

        {briefingError && (
          <div style={{ marginTop: 10, padding: "8px 12px", background: "#FEF2F2", border: "1px solid #FCA5A522", borderRadius: 8, fontSize: 12, color: "#B91C1C" }}>{briefingError}</div>
        )}

        {briefingEnabled && !briefingError && (
          <div style={{ marginTop: 14, padding: "10px 14px", background: "#F0FDFA", border: "1px solid #4ECDC422", borderRadius: 10, fontSize: 12, color: "#0D9488" }}>
            Vella will call {briefingPhone || "you"} at {(() => { const [h, m] = briefingTime.split(":"); const hour = parseInt(h); const hr12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour; return `${hr12}:${m} ${hour >= 12 ? "PM" : "AM"}`; })()} {briefingTz.replace(/_/g, " ")} each day.
          </div>
        )}
      </div>}

      {activeTab === "notifications" && <div style={card3d}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: TEXT, letterSpacing: "-0.022em" }}>Email Notifications</h3>
          <button style={{ ...B, borderRadius: 999, padding: "9px 20px", opacity: saved.email ? 0.6 : 1 }} onClick={saveNotifs}>{saved.email ? "Saved" : "Save"}</button>
        </div>
        {notifOpts.map((n, i) => (
          <div key={n.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0", borderBottom: i < notifOpts.length - 1 ? `1px solid ${BORDER}` : "none" }}>
            <div><p style={{ fontSize: 14, fontWeight: 600, color: TEXT }}>{n.label}</p><p style={{ fontSize: 12, color: TEXT3 }}>{n.desc}</p></div>
            <div onClick={() => toggleNotif(n.key)} style={{ width: 48, height: 28, borderRadius: 14, background: notifs[n.key] ? BLUE : BORDER, cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0, marginLeft: 16 }}>
              <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: notifs[n.key] ? 23 : 3, transition: "left 0.2s" }} />
            </div>
          </div>
        ))}
      </div>}

      {/* Business Profile */}
      {activeTab === "account" && <div style={card3d}><h3 style={{ fontSize: 16, fontWeight: 700, color: TEXT, marginBottom: 14, letterSpacing: "-0.022em" }}>Business Profile</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 20px", fontSize: 14 }}>
          <div><span style={{ color: TEXT3, fontSize: 12 }}>Business</span><p style={{ fontWeight: 600, color: TEXT }}>{biz?.name || "--"}</p></div>
          <div><span style={{ color: TEXT3, fontSize: 12 }}>Industry</span><p style={{ fontWeight: 600, color: TEXT }}>{biz?.industry || "--"}</p></div>
          <div><span style={{ color: TEXT3, fontSize: 12 }}>Location</span><p style={{ fontWeight: 600, color: TEXT }}>{biz?.location || "--"}</p></div>
          <div><span style={{ color: TEXT3, fontSize: 12 }}>Budget</span><p style={{ fontWeight: 600, color: TEXT }}>{biz?.budget || "--"}</p></div>
        </div>
      </div>}

      {/* Connected Accounts -- OAuth */}
      {activeTab === "account" && <div style={card3d}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: TEXT, marginBottom: 4, letterSpacing: "-0.022em" }}>Connected Accounts</h3>
        <p style={{ fontSize: 13, color: TEXT2, marginBottom: 20 }}>Connect your accounts so Vella can publish posts, run ads, and manage replies.</p>

        {[
          { platform: "meta", label: "Facebook", logo: <FacebookLogo size={28} />, status: oauthStatus.facebook, desc: "Pages, ads, DMs" },
          { platform: "meta", label: "Instagram", logo: <InstagramLogo size={28} />, status: oauthStatus.instagram, desc: "Feed, Stories, Reels, DMs" },
          { platform: "tiktok", label: "TikTok", logo: <TikTokLogo size={28} />, status: oauthStatus.tiktok, desc: "Videos, ads, comments" },
          { platform: "google", label: "Google Ads", logo: <GoogleAdsLogo size={28} />, status: oauthStatus.google_ads, desc: "Search & display campaigns" },
        ].map((acct, i) => {
          const isConnected = !!acct.status;
          const isLoading = connecting[acct.platform];
          const platformKey = acct.label === "Instagram" ? "instagram" : acct.label === "Facebook" ? "facebook" : acct.label === "TikTok" ? "tiktok" : "google_ads";
          return (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 16, padding: "18px 0", borderBottom: i < 3 ? `1px solid ${BORDER}` : "none" }}>
              <div style={{ width: 46, height: 46, borderRadius: 12, background: BG, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {acct.logo}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: TEXT }}>{acct.label}</span>
                  {isConnected
                    ? <span style={tg(`${SUCCESS}18`, SUCCESS)}>Connected</span>
                    : <span style={tg(`${BORDER}`, TEXT3)}>Not Connected</span>}
                </div>
                {isConnected
                  ? <p style={{ fontSize: 12, color: TEXT2 }}>{acct.status.name || acct.desc}</p>
                  : <p style={{ fontSize: 12, color: TEXT3 }}>{acct.desc}</p>}
              </div>
              <div style={{ flexShrink: 0 }}>
                {isConnected ? (
                  <button
                    style={{ ...Bg, fontSize: 13, padding: "8px 16px", borderRadius: 999, color: DANGER, borderColor: `${DANGER}44` }}
                    onClick={() => disconnectPlatform(platformKey)}>
                    Disconnect
                  </button>
                ) : (
                  <button
                    style={{ ...B, fontSize: 13, padding: "9px 20px", borderRadius: 999, opacity: isLoading ? 0.6 : 1 }}
                    onClick={() => connectPlatform(acct.platform)}
                    disabled={isLoading}>
                    {isLoading ? "Connecting..." : `Connect ${acct.label}`}
                  </button>
                )}
              </div>
            </div>
          );
        })}

        <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${BORDER}` }}>
          <label style={L}>Business phone number</label>
          <input style={{ ...I, width: "100%", marginBottom: 14 }} type="tel" value={bizPhone} onChange={e => setBizPhone(e.target.value)} placeholder="e.g. (336) 555-0142" />
          <label style={L}>Website URL</label>
          <div style={{ display: "flex", gap: 10 }}>
            <input style={{ ...I, flex: 1 }} value={website} onChange={e => setWebsite(e.target.value)} placeholder="e.g. mybusiness.com" />
            <button style={{ ...B, borderRadius: 999, padding: "11px 20px", opacity: saved.acct ? 0.6 : 1 }} onClick={saveAccounts}>{saved.acct ? "Saved" : "Save"}</button>
          </div>
        </div>
      </div>}

      {activeTab === "account" && <div style={card3d}><h3 style={{ fontSize: 16, fontWeight: 700, color: TEXT, marginBottom: 14, letterSpacing: "-0.022em" }}>Brand Voice</h3><textarea style={{ ...T, height: 90 }} value={voice} onChange={e => setVoice(e.target.value)} /><button onClick={async () => { try { await API.saveBusiness({ ...biz, brandVoice: voice }); if (onUpdate) onUpdate({ ...biz, brandVoice: voice }); setSaved(p => ({ ...p, voice: true })); setTimeout(() => setSaved(p => ({ ...p, voice: false })), 2000); } catch (e) { console.warn("Save voice failed:", e.message); } }} style={{ ...B, marginTop: 10, borderRadius: 999, background: saved.voice ? "#34C759" : BLUE }}>{saved.voice ? "Saved!" : "Save"}</button></div>}

      {/* Brand Setup — logo / colors / tagline (post-onboarding edits) */}
      {activeTab === "account" && <div style={card3d}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: TEXT, marginBottom: 4, letterSpacing: "-0.022em" }}>Brand Setup</h3>
        <p style={{ fontSize: 13, color: TEXT2, marginBottom: 18 }}>
          Vella uses these to keep creatives on-brand. Logo + tagline appear in generated previews; colors drive accent + CTA styling.
        </p>

        <label style={L}>Logo</label>
        <div
          onClick={() => brandFileInputRef.current?.click()}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            padding: "12px 14px",
            border: `1px solid ${BORDER}`,
            borderRadius: 12,
            background: BG,
            cursor: "pointer",
            marginBottom: 18,
          }}
        >
          <div style={{ width: 56, height: 56, borderRadius: 12, background: "#FFF", border: `1px solid ${BORDER}`, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
            {brandLogoUrl ? (
              <img src={brandLogoUrl} alt="logo preview" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
            ) : (
              <span style={{ color: TEXT3, fontSize: 20 }}>↑</span>
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: TEXT, marginBottom: 2 }}>
              {brandLogoName || (brandLogoUrl ? "Replace logo" : "Upload your logo")}
            </div>
            <div style={{ fontSize: 12, color: TEXT3 }}>PNG or JPG, up to 5 MB</div>
          </div>
          {brandLogoUrl && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setBrandLogoUrl(null); setBrandLogoName(null); }}
              style={{ background: "transparent", border: "none", color: TEXT3, fontSize: 18, cursor: "pointer", padding: "0 4px" }}
              aria-label="Remove logo"
            >×</button>
          )}
          <input
            ref={brandFileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => onBrandLogoSelected(e.target.files?.[0])}
          />
        </div>

        <label style={L}>Brand colors (up to 3)</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
          {brandColors.map((c, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 12, background: BG, border: `1px solid ${BORDER}` }}>
              <input type="color" value={c} onChange={(e) => setBrandColorAt(i, e.target.value)} style={{ width: 28, height: 28, border: "none", padding: 0, background: "transparent", cursor: "pointer" }} />
              <input type="text" value={c} onChange={(e) => setBrandColorAt(i, e.target.value)} spellCheck={false} style={{ ...I, padding: "6px 8px", fontSize: 12, fontFamily: '"SF Mono", ui-monospace, monospace', width: 96, border: "none", background: "transparent" }} />
              <button type="button" onClick={() => removeBrandColorAt(i)} style={{ background: "transparent", border: "none", color: TEXT3, cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "0 4px" }} aria-label="Remove color">×</button>
            </div>
          ))}
          {brandColors.length < 3 && (
            <button type="button" onClick={addBrandColor} style={{ padding: "8px 14px", borderRadius: 12, border: `1px dashed ${BORDER}`, background: "transparent", color: TEXT3, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>
              + Add color
            </button>
          )}
        </div>

        <label style={L}>Tagline</label>
        <input
          style={I}
          value={brandTagline}
          onChange={(e) => setBrandTagline(e.target.value)}
          placeholder="e.g. Locally roasted, slowly brewed."
          maxLength={120}
        />

        {brandError && (
          <div style={{ marginTop: 10, padding: "8px 12px", background: "#FEF2F2", border: `1px solid ${DANGER}33`, borderRadius: 8, fontSize: 12, color: "#B91C1C" }}>{brandError}</div>
        )}

        <button
          type="button"
          onClick={saveBrand}
          disabled={brandSaving}
          style={{ ...B, marginTop: 14, borderRadius: 999, background: brandSaved ? "#34C759" : BLUE, opacity: brandSaving ? 0.65 : 1 }}
        >
          {brandSaving ? t("buttons.saving") : brandSaved ? t("buttons.saved") : t("settings.saveBrand")}
        </button>
      </div>}

      {/* Language & Region — drives interface language + Vella's reply language */}
      {activeTab === "account" && <div style={card3d}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: TEXT, marginBottom: 4, letterSpacing: "-0.022em" }}>
          {t("settings.languageSection")}
        </h3>
        <p style={{ fontSize: 13, color: TEXT2, marginBottom: 16 }}>
          {t("language.description")}
        </p>
        <label style={L}>{t("language.label")}</label>
        <select
          style={{ ...S, maxWidth: 320 }}
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
        >
          {languages.map((lng) => (
            <option key={lng.code} value={lng.code}>
              {t(`language.${lng.code === "en" ? "english" : lng.code === "es" ? "spanish" : lng.code === "fr" ? "french" : "portuguese"}`)} · {lng.nativeLabel}
            </option>
          ))}
        </select>
        <p style={{ fontSize: 11, color: TEXT3, marginTop: 8 }}>
          {t("language.autoDetected")}
        </p>
      </div>}

      {/* Reports — printable monthly performance summary */}
      {activeTab === "account" && <div style={card3d}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, gap: 16 }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: TEXT, marginBottom: 4, letterSpacing: "-0.022em" }}>Monthly Report</h3>
            <p style={{ fontSize: 13, color: TEXT2, lineHeight: 1.5, margin: 0 }}>
              Download a printable PDF of the last 30 days — performance, ad spend, ROAS, posts, leads. Opens in a new tab and triggers your browser's "Save as PDF" dialog.
            </p>
          </div>
          <button
            type="button"
            onClick={() => API.openMonthlyReport({ days: 30, autoPrint: true })}
            style={{ ...B, borderRadius: 999, padding: "10px 20px", fontSize: 13, flexShrink: 0 }}
          >
            Download PDF
          </button>
        </div>
      </div>}

      {/* Billing history — last 12 invoices from Stripe */}
      {activeTab === "account" && <div style={card3d}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: TEXT, marginBottom: 4, letterSpacing: "-0.022em" }}>Billing History</h3>
        <p style={{ fontSize: 13, color: TEXT2, marginBottom: 14 }}>Your last 12 invoices. Click any row to view it on Stripe or grab the PDF.</p>
        {invoices === null ? (
          <p style={{ fontSize: 12, color: TEXT3 }}>Loading invoices…</p>
        ) : invoices.length === 0 ? (
          <p style={{ fontSize: 12, color: TEXT3 }}>{invoicesError ? invoicesError : "No invoices on file yet."}</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 0, border: `1px solid ${BORDER}`, borderRadius: 12, overflow: "hidden" }}>
            {invoices.map((inv, i) => (
              <div key={inv.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", borderBottom: i < invoices.length - 1 ? `1px solid ${BORDER}` : "none", background: i % 2 === 0 ? "rgba(255,255,255,0.7)" : "transparent", gap: 10 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: TEXT }}>
                    {inv.number || inv.id?.slice(0, 14)} · ${(inv.amountPaid > 0 ? inv.amountPaid : inv.amountDue) / 100}
                  </div>
                  <div style={{ fontSize: 11, color: TEXT3, marginTop: 2 }}>
                    {inv.created ? new Date(inv.created).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"}
                    {inv.description ? ` · ${inv.description}` : ""}
                  </div>
                </div>
                <span style={tg(inv.status === "paid" ? `${SUCCESS}22` : `${WARNING}22`, inv.status === "paid" ? SUCCESS : WARNING)}>
                  {inv.status}
                </span>
                {inv.invoicePdf && (
                  <a href={inv.invoicePdf} target="_blank" rel="noreferrer" style={{ ...Bgsm, textDecoration: "none", color: TEXT, fontSize: 11, padding: "6px 12px" }}>PDF</a>
                )}
                {inv.hostedInvoiceUrl && (
                  <a href={inv.hostedInvoiceUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: BLUE, fontWeight: 600, textDecoration: "none" }}>View →</a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>}

      {/* Change Password */}
      {activeTab === "account" && <div style={card3d}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: TEXT, marginBottom: 4, letterSpacing: "-0.022em" }}>Change Password</h3>
        <p style={{ fontSize: 13, color: TEXT2, marginBottom: 14 }}>Use a strong, unique password — at least 8 characters.</p>
        <div style={{ display: "grid", gap: 10, maxWidth: 420 }}>
          <input type="password" value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)} placeholder="Current password" autoComplete="current-password" style={I} />
          <input type="password" value={pwNew} onChange={(e) => setPwNew(e.target.value)} placeholder="New password" autoComplete="new-password" style={I} />
          <input type="password" value={pwNew2} onChange={(e) => setPwNew2(e.target.value)} placeholder="Confirm new password" autoComplete="new-password" style={I} />
        </div>
        {pwError && <div style={{ marginTop: 10, padding: "8px 12px", background: "#FEF2F2", border: `1px solid ${DANGER}33`, borderRadius: 8, fontSize: 12, color: "#B91C1C" }}>{pwError}</div>}
        <button
          type="button"
          onClick={submitPasswordChange}
          disabled={pwSaving}
          style={{ ...B, marginTop: 14, borderRadius: 999, background: pwSaved ? "#34C759" : BLUE, opacity: pwSaving ? 0.6 : 1 }}
        >
          {pwSaving ? "Saving…" : pwSaved ? "Password updated" : "Update Password"}
        </button>
      </div>}

      {/* Contact Support — mailto with prefilled subject + user context */}
      {activeTab === "account" && <div style={card3d}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: TEXT, marginBottom: 4, letterSpacing: "-0.022em" }}>Help &amp; Support</h3>
            <p style={{ fontSize: 13, color: TEXT2, lineHeight: 1.5, margin: 0 }}>
              Email the AMPLIFX team — we usually reply within a few hours during business days. Include screenshots when you can.
            </p>
          </div>
          <a
            href={`mailto:team@amplifx.com?subject=${encodeURIComponent("Vella support request — " + (biz?.name || "your business"))}&body=${encodeURIComponent(`Hi AMPLIFX team,\n\nI'd like help with:\n\n\n— ${user?.email || ""} (${biz?.name || ""})`)}`}
            style={{ ...B, borderRadius: 999, padding: "10px 20px", fontSize: 13, textDecoration: "none", flexShrink: 0 }}
          >
            Contact Support
          </a>
        </div>
      </div>}

      {/* DANGER ZONE — Delete Account (GDPR / CCPA) */}
      {activeTab === "account" && <div style={{ ...card3d, borderLeft: `3px solid ${DANGER}` }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: DANGER, marginBottom: 4, letterSpacing: "-0.022em" }}>Delete Account</h3>
        <p style={{ fontSize: 13, color: TEXT2, marginBottom: 14, lineHeight: 1.5 }}>
          Permanently deletes your account and everything in it: business profile, posts, campaigns, inbox messages, scheduled briefings, and connected platform tokens. Cancels your Stripe subscription. <strong>This cannot be undone.</strong>
        </p>
        <button
          type="button"
          onClick={() => { setDeleteError(""); setDeletePassword(""); setDeleteConfirm(""); setDeleteModalOpen(true); }}
          style={{ ...Bg, borderRadius: 999, color: DANGER, borderColor: `${DANGER}66` }}
        >
          Delete my account
        </button>
      </div>}

      {deleteModalOpen && (
        <div role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget && !deleteSubmitting) setDeleteModalOpen(false); }} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
          <div style={{ ...card3d, marginBottom: 0, padding: "26px 24px", maxWidth: 460, width: "100%" }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: DANGER, margin: "0 0 10px" }}>Delete this account?</h3>
            <p style={{ fontSize: 13, color: TEXT2, lineHeight: 1.55, margin: "0 0 14px" }}>
              This will permanently remove all your data and cancel your subscription. To confirm, enter your password and type <strong style={{ color: TEXT }}>DELETE</strong> below.
            </p>
            <label style={L}>Current password</label>
            <input type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} autoComplete="current-password" style={I} />
            <label style={{ ...L, marginTop: 12 }}>Type DELETE to confirm</label>
            <input type="text" value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} autoComplete="off" style={{ ...I, fontFamily: '"SF Mono", ui-monospace, monospace' }} />
            {deleteError && <div style={{ marginTop: 10, padding: "8px 12px", background: "#FEF2F2", border: `1px solid ${DANGER}33`, borderRadius: 8, fontSize: 12, color: "#B91C1C" }}>{deleteError}</div>}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
              <button type="button" onClick={() => setDeleteModalOpen(false)} disabled={deleteSubmitting} style={{ ...Bg, borderRadius: 999 }}>Cancel</button>
              <button
                type="button"
                onClick={submitAccountDeletion}
                disabled={deleteSubmitting || deleteConfirm !== "DELETE" || !deletePassword}
                style={{ ...B, borderRadius: 999, background: DANGER, opacity: (deleteSubmitting || deleteConfirm !== "DELETE" || !deletePassword) ? 0.5 : 1 }}
              >
                {deleteSubmitting ? "Deleting…" : "Permanently delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Team Members */}
      {activeTab === "team" && <div style={card3d}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: TEXT, margin: 0, letterSpacing: "-0.022em" }}>Team Members</h3>
          <span style={{ fontSize: 12, color: TEXT3, fontWeight: 600 }}>{teamMembers.filter(m => m.removable).length}/{maxSeats} seats used</span>
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="Enter email address" style={{ flex: 1, padding: "10px 14px", borderRadius: 10, border: "1px solid " + BORDER, fontSize: 13, outline: "none", color: TEXT }} onKeyDown={e => e.key === "Enter" && handleInvite()} />
          <button onClick={handleInvite} disabled={teamMembers.filter(m => m.removable).length >= maxSeats} style={{ padding: "10px 20px", borderRadius: 999, background: teamMembers.filter(m => m.removable).length >= maxSeats ? TEXT3 : BLUE_MED, color: "#fff", border: "none", fontSize: 13, fontWeight: 600, cursor: teamMembers.filter(m => m.removable).length >= maxSeats ? "not-allowed" : "pointer" }}>Invite</button>
        </div>
        {teamMembers.map((m, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderTop: i > 0 ? "1px solid " + BORDER : "none" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: BG2, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: TEXT2 }}>{m.email[0].toUpperCase()}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: TEXT }}>{m.email}</div>
                <div style={{ fontSize: 11, color: TEXT3 }}>{m.role}</div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, background: m.status === "Active" ? "#ECFDF5" : "#FEF3C7", color: m.status === "Active" ? "#059669" : "#D97706", fontWeight: 600 }}>{m.status}</span>
              {m.removable && <button onClick={() => handleRemove(m.email)} style={{ fontSize: 11, color: "#EF4444", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Remove</button>}
            </div>
          </div>
        ))}
      </div>}
    </div>
  );
}

// ===========================================
// DASHBOARD (upgraded for 2.0, dark theme)
// ===========================================
// ===========================================
// AUTOPILOT MODE -- Global AI control center
// ===========================================
export const AutopilotContext = React.createContext(null);
export const VellaPromptContext = React.createContext({ prompt: null, clearPrompt: () => {} });

function AutopilotProvider({ children }) {
  const [autopilot, setAutopilot] = useState(false);
  const [settings, setSettings] = useState({
    posts: false,       // true = no approval needed
    replies: false,
    budgets: true,      // always needs approval
    campaigns: true,
    seo: false,
  });

  // Screen Control — gives Vella control of the user's computer via Vella Helper (local menu-bar app).
  // Browsers sandbox web apps; the actual clicks/typing happen in a local Helper process that connects
  // to Vella's backend and executes actions on behalf of Opus 4.7.
  const [screenControl, setScreenControl] = useState(false);
  const [screenControlSettings, setScreenControlSettings] = useState({
    browse: false,      // auto-allow: browsing sites and reading pages
    forms: false,       // auto-allow: filling out forms
    email: true,        // require approval: sending emails
    apps: false,        // auto-allow: using connected desktop apps
    purchases: true,    // always-approval: anything involving payment
    files: true,        // require approval: reading or uploading local files
  });
  const [helperStatus, setHelperStatus] = useState("not-installed"); // not-installed | connected | disconnected

  // Poll the local Vella Helper (once it exists at 127.0.0.1:47861) to detect whether it's running.
  // Before the Helper ships this always returns not-installed — the UI is ready for plug-in.
  React.useEffect(() => {
    let cancelled = false;
    async function ping() {
      try {
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), 1000);
        const r = await fetch("http://127.0.0.1:47861/status", { method: "GET", signal: ctrl.signal });
        clearTimeout(t);
        if (!cancelled) setHelperStatus(r.ok ? "connected" : "disconnected");
      } catch {
        if (!cancelled) setHelperStatus("not-installed");
      }
    }
    ping();
    const iv = setInterval(ping, 10000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  const toggle = () => setAutopilot(p => !p);
  const toggleScreenControl = () => setScreenControl(p => !p);
  const updateSetting = (key, val) => setSettings(p => ({ ...p, [key]: val }));
  const updateScreenControlSetting = (key, val) => setScreenControlSettings(p => ({ ...p, [key]: val }));
  const needsApproval = (task) => {
    if (!autopilot) return true; // manual mode = always needs approval
    return settings[task] === true; // in autopilot, check per-task setting
  };
  const screenControlNeedsApproval = (task) => {
    if (!screenControl) return true;
    return screenControlSettings[task] === true;
  };

  return (
    <AutopilotContext.Provider value={{
      autopilot, toggle, settings, updateSetting, needsApproval,
      screenControl, toggleScreenControl, screenControlSettings, updateScreenControlSetting, screenControlNeedsApproval,
      helperStatus,
    }}>
      {children}
    </AutopilotContext.Provider>
  );
}

// Autopilot toggle pill -- shown in top bar
function AutopilotToggle() {
  const { autopilot, toggle } = React.useContext(AutopilotContext);
  return (
    <div onClick={toggle} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 14px", borderRadius: 20, border: `1.5px solid ${autopilot ? SUCCESS : BORDER}`, background: autopilot ? `${SUCCESS}12` : "rgba(255,255,255,0.6)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", cursor: "pointer", transition: "all 0.2s", userSelect: "none" }}>
      <div style={{ width: 28, height: 16, borderRadius: 8, background: autopilot ? SUCCESS : BORDER, position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
        <div style={{ position: "absolute", top: 2, left: autopilot ? 14 : 2, width: 12, height: 12, borderRadius: "50%", background: WHITE, boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left 0.2s" }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, color: autopilot ? SUCCESS : TEXT3, whiteSpace: "nowrap" }}>
        {autopilot ? "Autopilot ON" : "Autopilot OFF"}
      </span>
    </div>
  );
}

// Autopilot settings panel -- shown inside Settings page
function AutopilotSettings() {
  const { autopilot, toggle, settings, updateSetting } = React.useContext(AutopilotContext);

  const tasks = [
    { key: "posts", label: "Social Media Posts", desc: "Vella auto-posts to Instagram, Facebook, TikTok without asking" },
    { key: "replies", label: "Inbox Replies", desc: "Vella sends replies to comments and DMs automatically" },
    { key: "seo", label: "SEO Updates", desc: "Vella applies website SEO changes without review" },
    { key: "campaigns", label: "Ad Campaigns", desc: "Vella launches and adjusts ad campaigns (high risk -- approval recommended)" },
    { key: "budgets", label: "Budget Changes", desc: "Vella increases or decreases ad spend (always recommended to keep approval on)" },
  ];

  return (
    <div style={card3d}>
      {/* Header with master toggle */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: TEXT, marginBottom: 2 }}>Autopilot Mode</h3>
          <p style={{ fontSize: 13, color: TEXT2 }}>Let Vella act on your behalf without waiting for your approval.</p>
        </div>
        <div onClick={toggle} style={{ width: 44, height: 24, borderRadius: 12, background: autopilot ? SUCCESS : BORDER, cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
          <div style={{ position: "absolute", top: 3, left: autopilot ? 22 : 3, width: 18, height: 18, borderRadius: "50%", background: WHITE, boxShadow: "0 1px 4px rgba(0,0,0,0.2)", transition: "left 0.2s" }} />
        </div>
      </div>

      <div style={{ background: autopilot ? `${SUCCESS}0A` : `${WARNING}0A`, border: `1px solid ${autopilot ? SUCCESS : WARNING}22`, borderRadius: 10, padding: "10px 14px", marginBottom: 18, fontSize: 12, color: autopilot ? SUCCESS : WARNING, fontWeight: 600 }}>
        {autopilot ? "Autopilot is ON -- Vella is acting independently on enabled tasks" : "Autopilot is OFF -- Vella will ask for your approval on everything"}
      </div>

      <div style={{ height: 1, background: BORDER, marginBottom: 18 }} />

      <p style={{ fontSize: 12, fontWeight: 700, color: TEXT3, letterSpacing: 1, textTransform: "uppercase", marginBottom: 14 }}>Task Approval Settings</p>

      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {tasks.map(task => {
          const noApproval = settings[task.key] === false;
          return (
            <div key={task.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0", borderBottom: `1px solid ${BORDER}` }}>
              <div style={{ flex: 1, marginRight: 20 }}>
                <p style={{ fontSize: 14, fontWeight: 600, color: autopilot ? TEXT : TEXT2, marginBottom: 2 }}>{task.label}</p>
                <p style={{ fontSize: 12, color: TEXT3, lineHeight: 1.4 }}>{task.desc}</p>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                <span style={{ fontSize: 11, color: noApproval && autopilot ? SUCCESS : TEXT3, fontWeight: 600 }}>
                  {noApproval && autopilot ? "Auto" : "Approval"}
                </span>
                <div onClick={() => autopilot && updateSetting(task.key, !noApproval)}
                  style={{ width: 36, height: 20, borderRadius: 10, background: noApproval && autopilot ? SUCCESS : BORDER, cursor: autopilot ? "pointer" : "not-allowed", position: "relative", transition: "background 0.2s", opacity: autopilot ? 1 : 0.4 }}>
                  <div style={{ position: "absolute", top: 2, left: noApproval && autopilot ? 18 : 2, width: 16, height: 16, borderRadius: "50%", background: WHITE, boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left 0.2s" }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <p style={{ fontSize: 11, color: TEXT3, marginTop: 14, lineHeight: 1.5 }}>
        Tip: Start with Posts and Replies on Auto, keep Budgets and Campaigns on Approval until you trust the AI's judgment.
      </p>

      {/* ───────────── SCREEN CONTROL MODE ───────────── */}
      <div style={{ height: 1, background: BORDER, margin: "22px 0 18px" }} />

      <ScreenControlSection />
    </div>
  );
}

// ─── Screen Control Section (inside Autopilot Mode) ──────────
// Gives Vella control of the user's computer via Vella Helper (local menu-bar app).
// UI is live now; actually taking control requires Helper to be installed + connected.
function ScreenControlSection() {
  const {
    screenControl,
    toggleScreenControl,
    screenControlSettings,
    updateScreenControlSetting,
    helperStatus,
  } = React.useContext(AutopilotContext);

  const tasks = [
    { key: "browse",    label: "Browse Websites",     desc: "Vella opens URLs, reads pages, clicks links, scrolls" },
    { key: "forms",     label: "Fill Out Forms",      desc: "Contact forms, business filings, account registrations" },
    { key: "apps",      label: "Use Desktop Apps",    desc: "Slack, Zoom, design tools, connected workflow apps" },
    { key: "email",     label: "Send Emails",         desc: "Compose + send from Gmail/Outlook (approval recommended)" },
    { key: "files",     label: "Access Files",        desc: "Read or upload local documents (approval recommended)" },
    { key: "purchases", label: "Make Purchases",      desc: "Anything involving payment — always requires approval" },
  ];

  const statusConfig = {
    "connected":     { color: SUCCESS, bg: `${SUCCESS}14`, border: `${SUCCESS}55`, dot: SUCCESS, label: "Vella Helper Connected",    sub: "Screen control is ready — flip it on below." },
    "disconnected":  { color: WARNING, bg: `${WARNING}14`, border: `${WARNING}55`, dot: WARNING, label: "Vella Helper Disconnected", sub: "Helper is installed but not running. Open it from the menu bar." },
    "not-installed": { color: TEXT2,   bg: `${BORDER}55`,  border: BORDER,          dot: TEXT3,  label: "Vella Helper Required",     sub: "Install the Helper app to let Vella control your Mac." },
  }[helperStatus];

  const canEnable = helperStatus === "connected";

  return (
    <div>
      {/* Header with master toggle */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ flex: 1, marginRight: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={screenControl && canEnable ? SUCCESS : TEXT2} strokeWidth="2">
              <rect x="2" y="3" width="20" height="14" rx="2" />
              <line x1="8" y1="21" x2="16" y2="21" />
              <line x1="12" y1="17" x2="12" y2="21" />
            </svg>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: TEXT, margin: 0 }}>Screen Control</h3>
            <span style={{ fontSize: 10, fontWeight: 700, color: "#fff", background: "linear-gradient(135deg,#6366F1,#8B5CF6)", padding: "2px 8px", borderRadius: 10, letterSpacing: 0.4 }}>BETA</span>
          </div>
          <p style={{ fontSize: 13, color: TEXT2, margin: 0 }}>
            Give Vella complete control of your computer. She can browse, click, type, and work across any app.
          </p>
        </div>
        <div
          onClick={() => canEnable && toggleScreenControl()}
          title={canEnable ? "" : "Install Vella Helper to enable Screen Control"}
          style={{
            width: 44, height: 24, borderRadius: 12,
            background: screenControl && canEnable ? SUCCESS : BORDER,
            cursor: canEnable ? "pointer" : "not-allowed",
            position: "relative", transition: "background 0.2s", flexShrink: 0,
            opacity: canEnable ? 1 : 0.5,
          }}>
          <div style={{ position: "absolute", top: 3, left: screenControl && canEnable ? 22 : 3, width: 18, height: 18, borderRadius: "50%", background: WHITE, boxShadow: "0 1px 4px rgba(0,0,0,0.2)", transition: "left 0.2s" }} />
        </div>
      </div>

      {/* Helper Status Card */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        background: statusConfig.bg,
        border: `1px solid ${statusConfig.border}`,
        borderRadius: 12, padding: "12px 14px", marginBottom: 14,
      }}>
        <div style={{
          width: 10, height: 10, borderRadius: "50%",
          background: statusConfig.dot,
          boxShadow: helperStatus === "connected" ? `0 0 8px ${statusConfig.dot}` : "none",
          animation: helperStatus === "connected" ? "pulse 2s infinite" : "none",
          flexShrink: 0,
        }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: statusConfig.color, marginBottom: 2 }}>{statusConfig.label}</div>
          <div style={{ fontSize: 12, color: TEXT3 }}>{statusConfig.sub}</div>
        </div>
        {helperStatus === "not-installed" && (
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('vella:navigate', { detail: 'relay-auth' }))}
            style={{
              fontSize: 12, fontWeight: 700, color: "#fff",
              background: "linear-gradient(135deg,#6366F1,#8B5CF6)",
              padding: "8px 14px", borderRadius: 10, border: "none",
              whiteSpace: "nowrap", flexShrink: 0, cursor: "pointer",
            }}>
            Extension Setup
          </button>
        )}
      </div>

      {/* Safety notice when enabled */}
      {screenControl && canEnable && (
        <div style={{ background: `${WARNING}0F`, border: `1px solid ${WARNING}33`, borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: WARNING, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={WARNING} strokeWidth="2.5">
            <path d="M12 9v4M12 17h.01"/>
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
          </svg>
          Press ESC at any time to immediately stop Vella. Never leave Screen Control ON unattended.
        </div>
      )}

      <p style={{ fontSize: 12, fontWeight: 700, color: TEXT3, letterSpacing: 1, textTransform: "uppercase", marginBottom: 10 }}>
        Computer-Use Approval Settings
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {tasks.map(task => {
          const noApproval = screenControlSettings[task.key] === false;
          const active = screenControl && canEnable;
          return (
            <div key={task.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0", borderBottom: `1px solid ${BORDER}` }}>
              <div style={{ flex: 1, marginRight: 20 }}>
                <p style={{ fontSize: 14, fontWeight: 600, color: active ? TEXT : TEXT2, marginBottom: 2 }}>{task.label}</p>
                <p style={{ fontSize: 12, color: TEXT3, lineHeight: 1.4 }}>{task.desc}</p>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                <span style={{ fontSize: 11, color: noApproval && active ? SUCCESS : TEXT3, fontWeight: 600 }}>
                  {noApproval && active ? "Auto" : "Approval"}
                </span>
                <div
                  onClick={() => active && updateScreenControlSetting(task.key, !noApproval)}
                  style={{
                    width: 36, height: 20, borderRadius: 10,
                    background: noApproval && active ? SUCCESS : BORDER,
                    cursor: active ? "pointer" : "not-allowed",
                    position: "relative", transition: "background 0.2s",
                    opacity: active ? 1 : 0.4,
                  }}>
                  <div style={{ position: "absolute", top: 2, left: noApproval && active ? 18 : 2, width: 16, height: 16, borderRadius: "50%", background: WHITE, boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left 0.2s" }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <p style={{ fontSize: 11, color: TEXT3, marginTop: 14, lineHeight: 1.5 }}>
        How it works: Vella Helper runs quietly in your menu bar with macOS Screen Recording + Accessibility permissions.
        When Screen Control is ON, Opus 4.7 sees your screen and controls mouse + keyboard to complete tasks you delegate in chat.
        All sessions are recorded locally for your review.
      </p>
    </div>
  );
}

// ===========================================
// DASHBOARD VIEW
// ===========================================

function VellaProgressView() {
  const [filter, setFilter] = React.useState("today");
  const filterBtn = (label, val) => ({
    padding: "8px 18px",
    borderRadius: 999,
    border: filter === val ? "1px solid rgba(255,255,255,0.25)" : "1px solid rgba(255,255,255,0.5)",
    cursor: "pointer",
    fontWeight: filter === val ? 700 : 500,
    fontSize: 14,
    background: filter === val ? "linear-gradient(135deg, #38BDF8, #0EA5E9 60%, #0284C7)" : "rgba(255,255,255,0.40)",
    backdropFilter: "blur(14px) saturate(180%)",
    WebkitBackdropFilter: "blur(14px) saturate(180%)",
    color: filter === val ? "#fff" : TEXT2,
    boxShadow: filter === val ? "0 8px 20px -6px rgba(14,165,233,0.45), inset 0 1px 0 rgba(255,255,255,0.3)" : "0 1px 0 rgba(255,255,255,0.8) inset",
    transition: "all 0.22s ease",
    fontFamily: FONT,
  });

  return (
    <div style={{ padding: 32, maxWidth: 900, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <h2 style={{ fontSize: 26, fontWeight: 800, color: TEXT, margin: 0 }}>Vella's Progress</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={filterBtn("Today", "today")} onClick={() => setFilter("today")}>Today</button>
          <button style={filterBtn("This Week", "week")} onClick={() => setFilter("week")}>This Week</button>
          <button style={filterBtn("This Month", "month")} onClick={() => setFilter("month")}>This Month</button>
        </div>
      </div>
      {/* Vella's Progress - brand logos instead of emojis */}
      <div style={{ ...card3d, transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)" }}
        onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 1px 0 rgba(255,255,255,0.9) inset, 0 28px 70px -20px rgba(11,107,211,0.30), 0 12px 32px -10px rgba(14,165,233,0.18)"; }}
        onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = card3d.boxShadow; }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: TEXT, marginBottom: 14 }}>What Vella Did Today</h3>
        {[
          { a: "Drafted 3 Instagram carousels for your approval", t: "2m", logo: <InstagramLogo size={18} /> },
          { a: "Replied to 8 customer DMs on Facebook (5 saved as leads)", t: "15m", logo: <FacebookLogo size={18} /> },
          { a: "Spring Promo ROAS hit 4.2x -- increased bid by 12%", t: "1h", logo: <GoogleAdsLogo size={18} /> },
          { a: "Ran SEO audit -- found 6 issues, generated fixes", t: "3h", logo: <GoogleAdsLogo size={18} /> },
          { a: "Posted TikTok reel -- 1,200 views in first hour", t: "4h", logo: <TikTokLogo size={18} /> },
        ].map((r, i) => (
          <div key={i}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 10px", borderBottom: i < 4 ? "1px solid rgba(255,255,255,0.35)" : "none", borderRadius: 10, transition: "background 0.2s", cursor: "default" }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.35)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ flexShrink: 0 }}>{r.logo}</div>
              <span style={{ fontSize: 14, color: TEXT }}>{r.a}</span>
            </div>
            <span style={{ fontSize: 12, color: TEXT3, flexShrink: 0, marginLeft: 12 }}>{r.t} ago</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function NotFoundView({ onGoHome }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", textAlign: "center", padding: "40px 24px" }}>
      <div style={{ fontSize: 72, fontWeight: 800, color: BLUE, opacity: 0.15, lineHeight: 1, marginBottom: 8 }}>404</div>
      <h2 style={{ fontSize: 24, fontWeight: 700, color: TEXT, marginBottom: 10, letterSpacing: "-0.022em" }}>Page not found</h2>
      <p style={{ fontSize: 14, color: TEXT2, maxWidth: 340, lineHeight: 1.6, marginBottom: 28 }}>
        This section doesn't exist. Head back to your dashboard to keep growing.
      </p>
      <button
        onClick={onGoHome}
        style={{ padding: "11px 28px", fontSize: 14, fontWeight: 600, background: BLUE, color: "#fff", border: "none", borderRadius: 10, cursor: "pointer", boxShadow: "0 2px 8px rgba(14,165,233,0.35)" }}
      >
        Go to Dashboard
      </button>
    </div>
  );
}

export default function VellaApp() {
  useEffect(() => {
    if (!document.getElementById('vella-pulse-css')) {
      const style = document.createElement('style');
      style.id = 'vella-pulse-css';
      style.textContent = '@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }';
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    const handler = (e) => setActive(e.detail);
    window.addEventListener('vella:navigate', handler);
    return () => window.removeEventListener('vella:navigate', handler);
  }, []);

  // Session-expiry toast — fired by api.js when a 401 comes back. We
  // show a 1.5s notice before api.js triggers the hard reload so the
  // user knows what happened (vs. the page silently reloading).
  const [sessionExpiredToast, setSessionExpiredToast] = useState(false);
  useEffect(() => {
    function onExpired() { setSessionExpiredToast(true); }
    window.addEventListener("vella:session-expired", onExpired);
    return () => window.removeEventListener("vella:session-expired", onExpired);
  }, []);

  const [screen, setScreen] = useState("loading");
  const [active, setActive] = useState("talkToVella");
  const [biz, setBiz] = useState(null);
  const [user, setUser] = useState(null);
  // Split preview panel coordination — TalkToVella's result drawer fires
  // `vella:previewPanel` so we can pad the bottom composer dock to match.
  const [previewPanel, setPreviewPanel] = useState({ open: false, width: "0px" });
  useEffect(() => {
    const onPanel = (e) => setPreviewPanel({ open: !!e.detail?.open, width: e.detail?.width || "0px" });
    window.addEventListener("vella:previewPanel", onPanel);
    return () => window.removeEventListener("vella:previewPanel", onPanel);
  }, []);
  const [vellaPrompt, setVellaPrompt] = useState(null);
  const clearVellaPrompt = React.useCallback(() => setVellaPrompt(null), []);
  // ChatMenu (left conversation panel) collapse — only meaningful on the chat
  // page. Persisted in localStorage by ChatMenu; seeded here so the content
  // layout starts in the right state.
  const [chatMenuCollapsed, setChatMenuCollapsed] = useState(() => getChatMenuCollapsed());
  // Seed for the text ChatView — wrap in an object so resubmitting the same
  // text still changes the reference and re-triggers the injection effect.
  const [chatSeed, setChatSeed] = useState(null);
  const sendToChat = React.useCallback((text) => {
    if (!text || !text.trim()) return;
    setChatSeed({ text: text.trim(), at: Date.now() });
    setActive("chat");
  }, []);
  const [settingsTab, setSettingsTab] = useState(null);
  const isMobile = useIsMobile();
  const [legalScreen, setLegalScreen] = useState(() => {
    const h = window.location.hash;
    if (h === "#privacy") return "privacy";
    if (h === "#terms") return "terms";
    return null;
  });

  // Deep-linkable /website/edit route → the live-site editor (Showroom Part 2).
  // Matched from the URL (pathname or hash) and kept in sync via popstate plus
  // a `vella:open-website-edit` event any surface can fire to enter the editor.
  const readEditRoute = () => {
    try {
      return (
        window.location.pathname.replace(/\/+$/, "") === "/website/edit" ||
        window.location.hash === "#/website/edit"
      );
    } catch {
      return false;
    }
  };
  const [websiteEdit, setWebsiteEdit] = useState(readEditRoute);
  useEffect(() => {
    const sync = () => setWebsiteEdit(readEditRoute());
    const openRoute = () => {
      try { window.history.pushState({}, "", "/website/edit"); } catch {}
      setWebsiteEdit(true);
    };
    window.addEventListener("popstate", sync);
    window.addEventListener("vella:open-website-edit", openRoute);
    return () => {
      window.removeEventListener("popstate", sync);
      window.removeEventListener("vella:open-website-edit", openRoute);
    };
  }, []);
  const exitWebsiteEdit = React.useCallback(() => {
    try {
      if (window.location.pathname.replace(/\/+$/, "") === "/website/edit") {
        window.history.pushState({}, "", "/");
      } else if (window.location.hash === "#/website/edit") {
        window.history.replaceState("", document.title, window.location.pathname);
      }
    } catch {}
    setWebsiteEdit(false);
  }, []);

  const sys = buildSys(biz, user);
  sys._biz = biz;

  // --- Load fonts & dark theme styles ---
  useEffect(() => {
    const link = document.createElement("link");
    link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    const s = document.createElement("style");
  s.textContent = `
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:.35}}
  @keyframes spin{to{transform:rotate(360deg)}}
  @keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
  @keyframes slideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
  @keyframes shimmer{0%{background-position:-1000px 0}100%{background-position:1000px 0}}
  @keyframes scaleIn{from{opacity:0;transform:scale(0.95)}to{opacity:1;transform:scale(1)}}
  *{margin:0;padding:0;box-sizing:border-box;font-family:-apple-system,BlinkMacSystemFont,"SF Pro Display","SF Pro Text","Inter","Helvetica Neue",system-ui,sans-serif;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;text-rendering:optimizeLegibility;font-feature-settings:"cv11","ss01","ss03"}
  h1,h2,h3,h4,h5,h6{letter-spacing:-0.022em;font-feature-settings:"cv11","ss01"}
  h1{letter-spacing:-0.03em}
  h2{letter-spacing:-0.025em}
  ::-webkit-scrollbar{width:6px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:var(--slate-300);border-radius:3px}::-webkit-scrollbar-thumb:hover{background:var(--slate-400)}
  input::placeholder,textarea::placeholder{color:var(--slate-400)}
  ::selection{background:rgba(39,92,115,0.18);color:var(--slate-900)}
  body{background:linear-gradient(180deg,#eef4f7 0%,#dbe9ee 55%,#e6eff2 100%);background-attachment:fixed;color:var(--slate-900)}
  select option{background:#FFFFFF;color:var(--slate-900)}
  input:focus,textarea:focus,select:focus{border-color:var(--blue-700);box-shadow:0 0 0 3px rgba(39,92,115,0.16);outline:none;transition:border-color 0.2s ease,box-shadow 0.2s ease}
  button,a,[role="button"]{transition:all 0.2s cubic-bezier(0.4,0,0.2,1)}
  .vella-brand-btn:hover{background:rgba(39,92,115,0.06);box-shadow:0 0 28px -2px rgba(37,99,196,0.55),0 0 12px -1px rgba(37,99,196,0.45)}
  `;
    document.head.appendChild(s);
  }, []);

  // --- Auto-login (preserved from 1.0) ---
  useEffect(() => {
    const checkAuth = async () => {
      if (!API.isLoggedIn()) { setScreen("login"); return; }
      try {
        const data = await API.getMe();
        if (data.user) setUser(data.user);
        if (data.business) { setBiz(data.business); setScreen("app"); }
        else setScreen("onboarding");
      } catch { API.logout(); setScreen("login"); }
    };
    checkAuth();
  }, []);

  // --- Onboarding completion retry ---
  // If the wizard's final updateMe call failed (network hiccup, transient
  // 5xx), it persists a `vella_onboarding_pending` flag. Re-attempt the
  // backend save here so the wizard doesn't reappear next session.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    try {
      const pending = localStorage.getItem("vella_onboarding_pending");
      if (!pending) return;
    } catch { return; }
    (async () => {
      try {
        await API.updateMe({ onboardingComplete: true });
        if (cancelled) return;
        try { localStorage.removeItem("vella_onboarding_pending"); } catch {}
        setUser((u) => (u ? { ...u, onboardingComplete: true } : u));
      } catch (err) {
        console.warn("[Onboarding] retry updateMe failed:", err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]); // re-run when the authed user identity changes

  const handleLogin = (data) => {
    if (data.user) setUser(data.user);
    if (data.needsOnboarding || !data.business) {
      // New signup -> show pricing first
      if (data.isNewUser) setScreen("pricing");
      else setScreen("onboarding");
    } else {
      setBiz(data.business);
      setScreen("app");
    }
  };

  const handleOnboardingComplete = async (data) => {
    try { const result = await API.saveBusiness(data); setBiz(result.business); setScreen("app"); }
    catch { setBiz(data); setScreen("app"); }
  };

  const handleBizUpdate = async (newBiz) => {
    setBiz(newBiz);
    try { await API.saveBusiness(newBiz); } catch (e) { console.warn("Save business failed:", e.message); }
  };

  const handleLogout = () => { API.logout(); setBiz(null); setUser(null); setScreen("login"); };

  // NOTE: Backend CORS must be restricted to https://vella.amplifx.com in server.js
  // on the Railway backend. See Agent 1/2 task — do not open CORS to wildcard (*).

  const legalFooterStyle = {
    position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 200,
    background: "rgba(255,255,255,0.88)", backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)", borderTop: "1px solid rgba(200,216,232,0.7)",
    display: "flex", alignItems: "center", justifyContent: "center", gap: 20,
    padding: "8px 24px", fontSize: 11, color: "#5a7a9a",
  };
  const legalLinkStyle = { color: "#0EA5E9", fontWeight: 600, cursor: "pointer", textDecoration: "none", fontSize: 11 };

  const LegalFooterBar = () => (
    <div className="vella-legal-footer" style={legalFooterStyle}>
      <span>© 2026 Mancini Business Consulting LLC</span>
      <span style={legalLinkStyle} onClick={() => { if (screen === "app") { setActive("settings"); setSettingsTab("privacy"); } else setLegalScreen("privacy"); }}>Privacy Policy</span>
      <span style={{ color: "#c8d8e8" }}>·</span>
      <span style={legalLinkStyle} onClick={() => { if (screen === "app") { setActive("settings"); setSettingsTab("terms"); } else setLegalScreen("terms"); }}>Terms of Service</span>
    </div>
  );

  const closeLegal = () => {
    setLegalScreen(null);
    window.history.replaceState("", document.title, window.location.pathname);
  };

  if (legalScreen === "privacy") return <PrivacyPolicy onBack={closeLegal} />;
  if (legalScreen === "terms") return <TermsOfService onBack={closeLegal} />;

  if (screen === "loading") return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: "transparent", position: "relative", isolation: "isolate", overflow: "hidden" }}>
      <style>{(LIQUID_GLOBAL_CSS_PRO || LIQUID_GLOBAL_CSS) + SILVER_SPATIAL_BG_CSS + VELLA_THINKING_RING_CSS}</style>
      <SilverSpatialBackground />
      <div style={{ textAlign: "center", position: "relative", zIndex: 1, ...liquidSurfaceElevated, padding: "36px 44px" }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 8, marginBottom: 16 }}>
          <span style={{ fontSize: 32, fontWeight: 800, color: TEXT, letterSpacing: 1 }}>Vella</span>
          <span style={{ fontSize: 16, fontWeight: 700, color: BLUE, background: "var(--blue-50)", border: "1px solid var(--blue-200)", borderRadius: 999, padding: "2px 12px" }}>2.0</span>
        </div>
        <p style={{ fontSize: 14, color: TEXT2 }}>Loading your workspace...</p>
        <div style={{ width: 18, height: 18, border: `2px solid ${BORDER}`, borderTopColor: BLUE, borderRadius: "50%", animation: "spin .7s linear infinite", margin: "16px auto 0" }} />
      </div>
    </div>
  );

  if (screen === "login") return <><LoginScreen onLogin={handleLogin} /><LegalFooterBar /></>;
  if (screen === "pricing") return <><PricingScreen onComplete={() => setScreen("onboarding")} /><LegalFooterBar /></>;
  if (screen === "onboarding") return <><OnboardingScreen onComplete={handleOnboardingComplete} /><LegalFooterBar /></>;

  // After business profile is saved, surface the new 4-step OnboardingWizard
  // for any user who hasn't yet had it dismissed (`onboardingComplete`
  // flipped on the User row via PATCH /api/users/me). localStorage guard:
  // if the backend flag can't persist (e.g. prod DB pre-migration), the
  // wizard still records completion client-side so it never re-appears.
  let localOnboarded = false;
  try { localOnboarded = localStorage.getItem(`vella_onboarded_${user?.id}`) === "true"; } catch {}
  if (user && user.onboardingComplete !== true && !localOnboarded) {
    return (
      <OnboardingWizard
        user={user}
        onComplete={() => setUser((u) => (u ? { ...u, onboardingComplete: true } : u))}
        onJumpToRelay={() => setActive("talkToVella")}
      />
    );
  }

  // /website/edit — the live-site editor renders fullscreen, bypassing the
  // normal app shell (like the admin dashboard). Only reachable once authed.
  if (websiteEdit) {
    return <EditorLayout biz={biz} user={user} onExit={exitWebsiteEdit} />;
  }

  const views = {
    dashboard: <DashboardView setActive={setActive} biz={biz} setVellaPrompt={setVellaPrompt} />,
    progress: <VellaProgressView />,
    chat: <ChatView sys={sys} seed={chatSeed} onSeedConsumed={() => setChatSeed(null)} />,
    inbox: <InboxView sys={sys} />,
    performance: <PerformanceView />,
    analysis: <AnalysisView sys={sys} />,
    recommendations: <RecsView sys={sys} />,
    website: <WebsiteView sys={sys} biz={biz} setActive={setActive} setVellaPrompt={setVellaPrompt} />,
    "website-edit": <WebsiteEditView biz={biz} setActive={setActive} setVellaPrompt={setVellaPrompt} />,
    "creative-studio": <CreativeStudioView setActive={setActive} setVellaPrompt={setVellaPrompt} />,
    schedule: <ScheduleView />,
    settings: <SettingsView biz={biz} user={user} onUpdate={handleBizUpdate} initialTab={settingsTab} />,
    autopilot: <AutopilotView biz={biz} />,
    smsSettings: <SMSSettings />,
    objective: <ObjectiveSelector />,
    talkToVella: <TalkToVella setActive={setActive} vellaPrompt={vellaPrompt} clearVellaPrompt={clearVellaPrompt} />,
    "relay-auth": <RelayAuth />,
    brain: <BrainMonitor />,
    privacy: <PrivacyPolicy onBack={() => setActive("dashboard")} />,
    terms: <TermsOfService onBack={() => setActive("dashboard")} />,
  };

  // Admin dashboard renders fullscreen, bypassing the normal app shell
  if (active === "admin" && user?.email === "team@amplifx.com") {
    return (
      <AutopilotProvider>
        <AdminDashboard onExit={() => setActive("dashboard")} />
      </AutopilotProvider>
    );
  }

  return (
    <AutopilotProvider>
    <div style={{ fontFamily: FONT, background: "transparent", color: TEXT, minHeight: "100vh", position: "relative", isolation: "isolate", overflowX: "hidden" }}>
      <style>{(LIQUID_GLOBAL_CSS_PRO || LIQUID_GLOBAL_CSS) + SILVER_SPATIAL_BG_CSS + VELLA_THINKING_RING_CSS}</style>
      <OfflineBanner />
      {sessionExpiredToast && (
        <div role="status" aria-live="polite" style={{ position: "fixed", top: 16, left: "50%", transform: "translateX(-50%)", zIndex: 9999, background: "rgba(255,255,255,0.55)", color: TEXT, borderRadius: 14, padding: "12px 18px", fontSize: 13, fontWeight: 500, border: "1px solid rgba(120,200,255,0.18)", backdropFilter: "blur(24px) saturate(1.8)", WebkitBackdropFilter: "blur(24px) saturate(1.8)", boxShadow: "var(--shadow-lg)", display: "flex", alignItems: "center", gap: 10, fontFamily: FONT }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
          Your session expired — signing you back in…
        </div>
      )}
      <KeyboardShortcuts getActive={() => active} setActive={setActive} />
      {/* Frosted-glass spatial backdrop — gives surfaces something to blur over */}
      <SilverSpatialBackground />
      {/* Item 8 — background depth. The chat page's field is owned by the
          Breath Grid (rendered inside TalkToVella); this barely-there radial
          tint + ~2% top→bottom gradient is the FALLBACK for every other view. */}
      {active !== "talkToVella" && (
        <div aria-hidden="true" style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none", background: "radial-gradient(120% 80% at 50% 0%, rgba(77,113,255,0.05), transparent 60%), linear-gradient(180deg, rgba(15,23,42,0.00) 0%, rgba(15,23,42,0.02) 100%)" }} />
      )}
      <div style={{ display: "flex", flexDirection: "column", height: "100vh", position: "relative", zIndex: 1, overflowX: "hidden" }}>
        {/* Top Bar — frosted glass, three-section: brand · autopilot+status · actions */}
          <header style={{ padding: isMobile ? "12px 16px" : "0 24px", height: isMobile ? "auto" : 60, minHeight: isMobile ? 56 : 60, borderBottom: "1px solid rgba(255,255,255,0.5)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, background: "rgba(255,255,255,0.6)", backdropFilter: "blur(40px) saturate(180%)", WebkitBackdropFilter: "blur(40px) saturate(180%)", boxShadow: "0 1px 0 rgba(255,255,255,0.8) inset, 0 8px 24px -14px rgba(39,92,115,0.18)", position: "sticky", top: 0, zIndex: 50 }}>
          {/* Left: brand + inline page menu (Menu A) — single header row */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: 1 }}>
            <button
              type="button"
              onClick={() => setActive("talkToVella")}
              aria-label="Go to Vella 2.0 chat"
              className="vella-brand-btn"
              data-no-vella-ring="true"
              style={{
                // Item 10 — smaller logo pill, quieter glow.
                fontSize: 15, fontWeight: 700, color: TEXT, letterSpacing: "-0.02em",
                whiteSpace: "nowrap", flexShrink: 0, border: "none", cursor: "pointer",
                padding: "5px 11px", borderRadius: 8, background: "transparent",
                boxShadow: "0 0 12px -6px rgba(37, 99, 196, 0.30)",
                transition: "all 180ms var(--ds-ease-spring)", fontFamily: FONT,
              }}
            >
              Vella <span style={{ color: ORB_BABY_BLUE }}>2.0</span>
            </button>
            {!isMobile && <span style={{ width: 1, height: 18, background: "var(--slate-200)", flexShrink: 0 }} />}
            <TopPagesMenu
              active={active}
              setActive={setActive}
              goToSettingsTab={(tab) => { setSettingsTab(tab); setActive("settings"); }}
              isMobile={isMobile}
            />
          </div>
          {/* Right: autopilot · status · actions (autopilot + active hidden on mobile) */}
          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 6 : 8, justifyContent: "flex-end", flexShrink: 0 }}>
            {!isMobile && (
              <>
                <div style={{ ...liquidSurface, display: "inline-flex", alignItems: "center", gap: 6, borderRadius: 999, padding: "4px 11px", fontSize: 12, fontWeight: 600 }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: SUCCESS, animation: "liquidPulse 2s ease-in-out infinite" }} />
                  <span style={{ color: SUCCESS }}>Active</span>
                </div>
                <button title="Search" data-no-vella-ring="true" className="liquid-hover liquid-press" style={{ width: 36, height: 36, borderRadius: 14, background: "rgba(255,255,255,0.55)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)", border: `1px solid ${GLASS_BORDER_COLOR}`, color: TEXT2, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: `transform 220ms ${EASE_OUT_QUART}, background 220ms ease-out` }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                </button>
              </>
            )}
            <NotificationBell onNavigate={setActive} isMobile={isMobile} />
            {/* Autopilot toggle — far right of the header controls */}
            {!isMobile && <AutopilotToggle />}
          </div>
        </header>
        {/* Body row — Menu B (ChatMenu) docks left of the content column on the
            chat page only; the two menus are fully independent. */}
        <div style={{ flex: 1, minHeight: 0, display: "flex", overflow: "hidden", position: "relative", zIndex: 1 }}>
          {active === "talkToVella" && !isMobile && (
            <ChatMenu collapsed={chatMenuCollapsed} setCollapsed={setChatMenuCollapsed} user={user} />
          )}
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", minHeight: 0, overflowX: "hidden" }}>
            {/* Edit toolbar now docks top-center of the PREVIEW frame inside
                ResultPreviewPanel (right panel), not over the chat rail — see
                D9. It's rendered from within ResultPreviewPanel.jsx. */}
            {/* Page Content */}
            <div style={active === "chat" ? { flex: 1, minHeight: 0, overflow: "hidden", position: "relative", zIndex: 1 } : active === "talkToVella" ? { padding: isMobile ? "20px 16px" : "32px 40px", paddingBottom: isMobile ? 80 : undefined, flex: 1, minHeight: 0, overflow: "hidden", position: "relative", zIndex: 1 } : { padding: isMobile ? "20px 16px" : "32px 40px", paddingBottom: isMobile ? 80 : undefined, flex: 1, minHeight: 0, overflowY: "auto", position: "relative", zIndex: 1 }}>
              {/* Keep ad-campaigns mounted to preserve state */}
              <div style={{ display: active === "ad-campaigns" ? "block" : "none" }}><AdCampaignsView sys={sys} biz={biz} onSendToChat={sendToChat} /></div>
              {active !== "ad-campaigns" && (
                <div style={{ animation: "fadeIn 0.25s ease" }}>{views[active] || <NotFoundView onGoHome={() => setActive("dashboard")} />}</div>
              )}
            </div>
            {/* Chat bar lives on Home only — voice-only relay surface owns its
                own input on every other page. When the split preview panel is
                open we pad the dock so the composer stays inside the left pane. */}
            {active === "talkToVella" && !isMobile && (
              <div data-vella-chrome="chatbar" style={{ position: "relative", zIndex: 30, paddingRight: previewPanel.open ? previewPanel.width : 0, transition: "padding-right 280ms cubic-bezier(0.22,1,0.36,1)" }}>
                <InlineChatBar key={active} sys={sys} embedded={true} seed={vellaPrompt} onSeedConsumed={clearVellaPrompt} />
              </div>
            )}
          </div>
        </div>

        {/* Mobile Bottom Nav */}
        {isMobile && (
          <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "rgba(255,255,255,0.62)", backdropFilter: "blur(30px) saturate(180%)", WebkitBackdropFilter: "blur(30px) saturate(180%)", borderTop: "1px solid rgba(255,255,255,0.55)", boxShadow: "0 -1px 0 rgba(255,255,255,0.8) inset, 0 -8px 24px -12px rgba(39,92,115,0.18)", display: "flex", zIndex: 6, paddingBottom: "env(safe-area-inset-bottom)" }}>
            {[
              { id: "dashboard", label: "Dashboard", d: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" },
              { id: "creative-studio", label: "Creative", d: "M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z" },
              { id: "schedule", label: "Schedule", d: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" },
              { id: "website", label: "Website", d: "M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" },
              { id: "chat", label: "Vella 2.0", d: "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" },
            ].map(tab => {
              const on = active === tab.id;
              return (
                <button key={tab.id} onClick={() => setActive(tab.id)} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, padding: "10px 4px", border: "none", background: "transparent", cursor: "pointer" }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={on ? BLUE : TEXT3} strokeWidth={on ? 2.2 : 1.7} strokeLinecap="round" strokeLinejoin="round">
                    <path d={tab.d} />
                  </svg>
                  <span style={{ fontSize: 10, fontWeight: on ? 700 : 400, color: on ? BLUE : TEXT3 }}>{tab.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
      <TaskProgress />
      {/* LegalFooterBar removed from global layout — Privacy/Terms live in Settings tabs */}
    </AutopilotProvider>
  );
}

